import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import de from './i18n/de';
import nl from './i18n/nl';
import fr from './i18n/fr';
import ar from './i18n/ar';
import localSection, { DASH as LOCAL_DASH } from './i18n/localSection';
import additions from './i18n/additions';
import additions2 from './i18n/additions2.json';

const resources = {
  en: {
    translation: {
      "membership": {
        "chooseTitle": "Choose your membership",
        "chooseSubtitle": "Start free, or unlock your full profile. Prices include IVA.",
        "freeForever": "Free forever · no card needed",
        "chooseFree": "Choose Free Directory",
        "month": "month",
        "year": "year",
        "trial60": "First 60 days free",
        "ivaIncluded": "IVA included",
        "chooseBasic": "Choose Basic",
        "plusName": "Business Plus",
        "orAnnual": "or",
        "choosePlusMonthly": "Monthly",
        "choosePlusAnnual": "Annual",
        "consentNote": "Paid plans start with a free 60-day trial. You will not be charged until the trial ends, and only if you provided consent. Cancel anytime.",
        "freeBadge": "Free Directory",
        "freeTitle": "Free listing — €0 forever",
        "freeBody": "Your basic listing (business name, category and town) goes live once approved. No card required. You can upgrade to Basic or Business Plus anytime to show your full details.",
        "paidBody": "Your listing goes live once approved. You will NOT be charged for the first 60 days. After the free trial ends, your card is charged automatically only if you consented — and you can cancel anytime before then.",
        "firstChargeOn": "First charge (if you continue):",
        "checkoutNotReady": "Secure card checkout for paid plans will be switched on in the next step (Stripe Test mode). Your registration details are complete and ready — no charge has been made.",
        "freeCta": "Create my free listing",
        "paidCta": "Continue — start my 60-day free trial",
        "freeName": "Free Directory",
        "basicName": "Basic",
        "feat": {
          "free": ["Business name", "Business category", "Town", "Basic directory visibility"],
          "basic": ["First 60 days free", "Full business description", "Telephone & contact details", "Website & social media links", "Business photos", "Address, map & directions", "Standard business search visibility"],
          "plus": ["First 60 days free", "Everything in Basic", "Business Plus badge", "Promote events & special offers"]
        }
      },
      "listShare": {
        "today": "Today's Shopping",
        "todayHint": "Your current shopping trip · compare prices",
        "myLists": "My Lists",
        "noLists": "Create lists for anything — groceries, DIY, logs, pets, gardening…",
        "newList": "New List",
        "namePh": "List name (e.g. Weekly Groceries, DIY, Logs)",
        "nameRequired": "Please name the list",
        "kind_personal": "Personal",
        "kind_calendar": "Household",
        "kind_direct": "Shared",
        "hintPersonal": "Private to you.",
        "hintCalendar": "Shared with everyone on that calendar.",
        "hintDirect": "Share with specific people by email.",
        "pickCalendar": "Choose a calendar…",
        "create": "Create",
        "created": "List created",
        "rename": "Rename",
        "delListConfirm": "Delete this list? Items on it will be removed.",
        "share": "Share with someone",
        "shared": "List shared",
        "invite": "Invite",
        "emailPh": "Find-2-Day user's email",
        "editingShared": "You are working on a shared list",
        "shareHelp": "People you invite can view and edit this list, and can see the names and email addresses of the others sharing it.",
        "remove": "Remove",
        "addToWhich": "Add to…",
        "addedToList": "Added {{n}} to {{list}}",
        "addToListN": "Add to list ({{n}})",
        "copySelectedToList": "Copy to list ({{n}})",
        "listEmpty": "No products yet. Add anything you buy.",
        "select": "Select",
        "compareThis": "Compare This List",
        "addAll": "Add whole list",
        "addSelected": "Add selected ({{n}})",
        "addSelectedShort": "Add selected",
        "addWholeShop": "Add whole shop",
        "pickItems": "Tick the items you need",
        "copiedToday": "Added {{n}} to Today's Shopping",
        "itemsN": "{{n}} items",
        "repeatShop": "Repeat Last Shop",
        "repeatHint": "Add items from your most recent receipt",
        "repeatEmpty": "No previous shop found. Scan a receipt first.",
        "favourite": "Favourite",
        "favAdded": "Added to Favourites",
        "favRemoved": "Removed from Favourites"
      },
      "priceEdit": {
        "title": "Price details", "edit": "Edit price", "delete": "Delete", "save": "Save changes", "cancel": "Cancel",
        "confirmDelete": "Delete this price permanently? This cannot be undone.", "confirmYes": "Yes, delete",
        "productName": "Product name", "price": "Price", "brand": "Brand", "packSize": "Pack / size", "quantity": "Quantity",
        "shop": "Supplier / shop", "town": "Town / location", "date": "Date recorded", "notes": "Notes",
        "notesPh": "e.g. on special offer, loyalty price, bulk pack…", "optional": "optional",
        "saved": "Price updated", "deleted": "Price deleted",
        "errName": "Please enter a product name", "errPrice": "Please enter a valid price",
        "errSave": "Could not update price", "errDelete": "Could not delete price"
      },
      "priceHistory": {
        "title": "Price history", "none": "No price history yet",
        "noneHint": "More recorded prices for this product at this shop will build a trend here.",
        "latest": "LATEST", "rose": "Price rose", "fell": "Price fell", "flat": "About the same"
      },
      "pricePromo": {
        "title": "Price-2-Day Promotions",
        "subtitle": "Promote a product offer to nearby shoppers",
        "slots": "Slots",
        "suspendedNote": "Your business is currently suspended from promotions. Please contact support.",
        "new": "New promotion",
        "embedTitle": "My Promotions",
        "promoteItem": "Promote an Item",
        "boostAgain": "Boost Again",
        "noSlots": "No promotion slots remaining. Archive an active one to free a slot.",
        "editTitle": "Edit promotion",
        "createTitle": "Create promotion",
        "product": "Product",
        "productPh": "e.g. Olive Oil 2L",
        "brand": "Brand",
        "pack": "Pack / size",
        "normalPrice": "Normal price",
        "promoPrice": "Promotional price",
        "description": "Description",
        "town": "Town / area",
        "branch": "Shop / branch",
        "optional": "optional",
        "start": "Start date",
        "end": "End date",
        "availability": "Availability wording",
        "availabilityPh": "e.g. While stocks last",
        "photo": "Photo",
        "photoError": "Could not upload photo",
        "save": "Save",
        "publish": "Publish",
        "updated": "Promotion updated",
        "published": "Promotion is now live",
        "submitted": "Promotion submitted for review",
        "archived": "Promotion archived",
        "viewActive": "Active",
        "viewExpired": "Expired",
        "empty": "No promotions here yet.",
        "ends": "ends",
        "sponsored": "Sponsored",
        "sponsoredHint": "Paid offer — not part of the price comparison",
        "needProduct": "Please enter a product name",
        "needPromoPrice": "Please enter a promotional price",
        "needEnd": "Please choose an end date",
        "saveError": "Could not save promotion",
        "needDuration": "Please choose a promotion duration",
        "publishedFree": "Your FREE promotion is now live",
        "ended": "Promotion ended",
        "startHint": "leave blank for today",
        "duration": "Promotion duration",
        "days": "days",
        "free": "FREE",
        "willEnd": "Ends",
        "testMode": "Preview test mode — price shown, no real charge is made",
        "introAvailable": "Your first 7-day promotion is FREE (one per business).",
        "introUsed": "Free introductory promotion used.",
        "viewLive": "Live",
        "viewScheduled": "Scheduled",
        "edit": "Edit",
        "endEarly": "End early",
        "archive": "Archive",
        "status": { "live": "Live", "scheduled": "Scheduled", "pending": "Pending review", "suspended": "Suspended", "expired": "Expired", "archived": "Archived" }
      },
      "price": {
        "tagline": "Scan a receipt to read its prices",
        "homeHeadline": "Save money together",
        "homeTagline": "Real prices. Real people. A stronger community.",
        "homeIntro": "Scan your receipts, share prices, compare shops and help everyone find the best deals.",
        "actListTitle": "My Shopping List",
        "actListSub": "What do I need to buy?",
        "actSearchTitle": "Find Cheapest Price",
        "actSearchSub": "Compare prices near you",
        "actScanTitle": "Scan & Share Prices",
        "actScanSub": "Add the prices from my shopping",
        "helpTitle": "Your receipts help everyone!",
        "helpBody": "When you scan your shopping receipt, we only use the product information and prices to help you and others find the best deals.",
        "helpPrivacy": "We do NOT collect or save your personal details, payment/card information or the receipt image.",
        "buyOftenTitle": "Things I Buy Often",
        "buyOftenSub": "Quick add to my list",
        "dealsBtnTitle": "Today's Deals",
        "dealsBtnSub": "Special offers near you",
        "backToMenu": "Back",
        "scanWhyTitle": "Help everyone save money",
        "scanWhyBody": "Scan your shopping receipt and we'll read the prices. Your prices help you and other people in the community see where products are cheapest.",
        "savedCommunityTitle": "Thank you! \u2764\ufe0f",
        "savedCommunityBody": "Your prices have been added. You've just helped other shoppers find better prices.",
        "slAddHeading": "What do you need?",
        "quickAddTitle": "Quick add",
        "regularTitle": "Things I buy regularly",
        "addToListBtn": "Add",
        "buyOftenEmpty": "Products you buy often will appear here, ready to add with one tap.",
        "manageProductsLink": "Manage my saved products",
        "addedToListToast": "Added to your shopping list",
        "tabScan": "Scan",
        "tabSearch": "Search",
        "tabFav": "Favourites",
        "tabDeals": "Deals",
        "dist": { "pick": "Select your town to see distances", "list": "List", "map": "Map", "soon": "Coming soon", "directions": "Directions", "unavailable": "Location not yet available", "approxTravel": "Approx. travel", "extraTravel": "Approx. extra travel", "productSaving": "Product saving", "vsOne": "vs One Shop", "partial": "Some shop locations not yet available", "nearestNone": "No reliably-located shop for these items yet", "knownFor": "Known recent prices for {{covered}}/{{total}} items" },
        "reviewNote": "Review what the AI read, correct anything that is wrong, then save. The receipt photo is never stored — only the prices.",
        "scanTitle": "Scan Receipt",
        "scanBlurb": "Photograph a receipt or choose one from your phone. Works with any shop and any language.",
        "receiptPrivacy": "Receipt Privacy: Your receipt is used only to read product and price information. The receipt image is not displayed publicly and is not permanently stored by Price-2-Day. Personal and payment information is not added to the Price-2-Day price database.",
        "signInToScan": "Please sign in to scan and submit a receipt.",
        "signIn": "Sign in",
        "takePhoto": "Take Photo",
        "choosePhoto": "Choose Existing Photo",
        "readingReceipt": "Reading receipt…",
        "receiptPreviewAlt": "receipt preview",
        "reviewTitle": "Review & correct",
        "reviewSubtitle": "Edit any field that the AI read incorrectly.",
        "fShop": "Shop",
        "fLocation": "Location / branch",
        "fDate": "Date",
        "fCurrency": "Currency",
        "fTotal": "Receipt total",
        "itemsLabel": "ITEMS ({{count}})",
        "noItems": "No line items were recognised.",
        "itemN": "Item {{n}}",
        "removeItem": "Remove item",
        "fDesc": "Description (as printed)",
        "fBrand": "Brand",
        "fPackSize": "Pack size",
        "fQty": "Quantity",
        "fUnitPrice": "Unit price",
        "fLineTotal": "Line total",
        "fDiscount": "Discount / offer",
        "confirmSave": "Confirm & Save Prices",
        "saving": "Saving…",
        "cancel": "Cancel",
        "signInHint": "You'll need to sign in to save.",
        "savedTitle": "Receipt added successfully",
        "savedBody": "Thanks for contributing. You can add as many receipts as you like.",
        "scanAnother": "Scan Another Receipt",
        "searchPlaceholder": "Search a product, e.g. Olive Oil or ACEITE",
        "searchBtn": "Search",
        "sortLabel": "Sort:",
        "sortLowest": "Lowest price",
        "sortRecent": "Most recent",
        "sortNearest": "Nearest",
        "noResults": "No matching products found. Try different wording.",
        "asPrinted": "as printed",
        "lowestPrice": "Lowest price",
        "lowestUnit": "Lowest unit price",
        "unknownShop": "Unknown shop",
        "shopsCount": "{{count}} shop(s)",
        "receiptDated": "Receipt dated",
        "offer": "Offer",
        "freshRecent": "Recent",
        "freshOlder": "Older",
        "freshHistorical": "Historical",
        "freshUnknown": "Unknown",
        "disclaimer": "Prices shown on Price-2-Day are based on community-provided information. They may be out of date or vary by shop and time. Always check in-store before purchase.",
        "errMin2": "Type at least 2 characters to search.",
        "errHeic": "Could not read that photo format. Please try a JPG or PNG.",
        "errChooseImage": "Please choose a receipt image.",
        "errNoData": "No data could be extracted. Try a clearer photo.",
        "errScan": "Could not read the receipt. Please try again.",
        "errNoItems": "Add at least one item with a description before saving.",
        "errSignIn": "Please sign in to save receipt prices.",
        "errDuplicate": "You have already submitted this receipt.",
        "errSaveGeneric": "Could not save. Please try again.",
        "errSearch": "Search failed. Please try again.",
        "tabProducts": "My Products",
        "tabList": "Shopping List",
        "signInCta": "Sign in",
        "errGeneric": "Something went wrong. Please try again.",
        "edit": "Edit",
        "delete": "Delete",
        "back": "Back",
        "mpSignIn": "Sign in to keep your private list of products you buy.",
        "mpAddTitle": "Add a product",
        "mpNamePh": "Product name (e.g. Milk)",
        "mpBrandPh": "Brand (optional)",
        "mpPackPh": "Pack size (optional)",
        "mpAddBtn": "Add",
        "mpFromReceipt": "From receipt",
        "mpNameRequired": "Please enter a product name.",
        "mpDuplicate": "That product is already in My Products.",
        "mpAdded": "Added to My Products.",
        "mpEmpty": "No products yet. Add the things you buy regularly.",
        "mpToList": "Need to buy",
        "mpPickReceipt": "Choose one of your saved receipts",
        "mpNoReceipts": "You have no saved receipts yet.",
        "mpTickItems": "Tick the items to add to My Products",
        "mpAddSelected": "Add selected",
        "mpPickOne": "Select at least one item.",
        "mpFromReceiptDone": "Added {{added}} · {{skipped}} already there",
        "itemsCount": "{{count}} item(s)",
        "status_have": "Have plenty",
        "status_low": "Running low",
        "status_need": "Need to buy",
        "slSignIn": "Sign in to keep your private shopping list.",
        "slAddTitle": "Add to shopping list",
        "slNamePh": "Type an item, e.g. Bread",
        "slNameRequired": "Please enter an item.",
        "slIncreased": "Increased the quantity on your list.",
        "slAdded": "Added to your shopping list.",
        "slFromProductsHint": "Tip: tap \"Need to buy\" on a product in My Products to add it here.",
        "slEmpty": "Your shopping list is empty.",
        "slTick": "Mark as bought",
        "slUntick": "Move back to list",
        "slBought": "Bought ({{n}})",
        "slClearCompleted": "Clear completed",
        "slCleared": "Cleared {{n}} item(s).",
        "buyAgain": "Buy Again",
        "buyAgainDone": "Added to your shopping list.",
        "addToMyProductsTitle": "Add these to My Products?",
        "addToMyProductsBtn": "Add selected to My Products",
        "addedToMyProducts": "Added to My Products.",
        "cmpButton": "Compare My Shopping",
        "cmpTitle": "Compare My Shopping",
        "cmpOneShop": "One shop",
        "cmpTwoShops": "Two shops",
        "cmpCheapest": "Cheapest",
        "cmpNearest": "Nearest",
        "cmpEstTotal": "Estimated total",
        "cmpSaving": "Estimated saving",
        "cmpSubtotal": "Subtotal",
        "cmpCoverage": "Prices found for {{found}} of {{total}} items.",
        "cmpNoRecent": "No recent comparison available",
        "cmpNoData": "No supported prices for this option yet.",
        "cmpDisclaimer": "Estimated from community receipt prices. Prices may have changed."
      },
      "notes": {
        "back": "Notes", "overviewHint": "Private and shared notes for this calendar.",
        "createNote": "Create note", "editNote": "Edit note", "title": "Title", "text": "Text",
        "textPlaceholder": "Write your note here…", "colour": "Colour", "privacy": "Privacy",
        "private": "Private", "shared": "Shared", "privateHint": "Only you can see this note.",
        "sharedHint": "Pick calendar members to share it with after saving.",
        "formHint": "Give your note a title and text. Notes are private unless you share them.",
        "save": "Save", "cancel": "Cancel", "open": "Open", "edit": "Edit", "delete": "Delete",
        "copy": "Copy text", "share": "Share", "manageSharing": "Manage sharing", "memberEmail": "Member email",
        "shareDialogHint": "Add the exact email of a calendar member. They must already belong to this calendar.",
        "noneShared": "Not shared with anyone yet.", "sharedWith": "Shared with", "sharedTag": "shared with you",
        "readOnly": "You have read-only access to this note.", "empty": "This note has no text yet.",
        "emptyTitle": "No notes yet", "emptyHint": "Create your first private or shared note.",
        "deleteNote": "Delete note", "deleteConfirm": "This permanently deletes the note. This cannot be undone.",
        "created": "Note created", "updated": "Note updated", "deleted": "Note deleted",
        "copied": "Note copied to clipboard", "shareCopied": "Sharing not supported here — note copied instead",
        "shareSaved": "Sharing updated", "loadError": "Could not load notes", "openError": "This note is not available",
        "saveError": "Could not save note", "deleteError": "Could not delete note", "copyError": "Could not copy note",
        "shareError": "Could not share note", "shareUpdateError": "Could not update sharing",
        "dragHandle": "Drag to reorder", "reorderHint": "Drag a note by its handle to reorder, or use the ▲ ▼ arrows",
        "moveUp": "Move up", "moveDown": "Move down", "reorderError": "Could not reorder"
      },
      "lists": {
        "back": "Lists", "overviewHint": "Private and shared checklists for this calendar.",
        "createList": "Create list", "editList": "Edit list", "name": "Name", "colour": "Colour",
        "privacy": "Privacy", "private": "Private", "shared": "Shared",
        "privateHint": "Only you can see and manage this list.",
        "sharedHint": "You choose exactly which calendar members can access it.",
        "formHint": "Name your list, pick a colour and choose who can see it.",
        "save": "Save", "cancel": "Cancel", "delete": "Delete", "duplicate": "Duplicate", "editItem": "Edit item",
        "deleteList": "Delete list", "deleteConfirm": "This permanently deletes the list and all its items. This cannot be undone.",
        "manageSharing": "Manage sharing", "memberEmail": "Member email",
        "shareHint": "Add the exact email of a calendar member. They must already belong to this calendar.",
        "noneShared": "Not shared with anyone yet.", "sharedWith": "Shared with", "sharedTag": "shared with you",
        "readOnly": "You have read-only access to this list.", "noItems": "No items yet.", "newItem": "New item",
        "itemText": "Item", "itemPlaceholder": "e.g. Milk", "quantity": "Quantity", "qty": "Qty", "done": "done",
        "emptyTitle": "No lists yet", "emptyHint": "Create your first private or shared list.",
        "dragHandle": "Drag to reorder", "reorderHint": "Drag the handle to reorder, or use the ▲ ▼ arrows", "reorderListsHint": "Drag a list by its handle to reorder",
        "itemTextRequired": "Item text is required", "created": "List created", "updated": "List updated",
        "deleted": "List deleted", "duplicated": "A private copy was created", "itemUpdated": "Item updated",
        "loadError": "Could not load lists", "openError": "This list is not available", "saveError": "Could not save list",
        "deleteError": "Could not delete list", "dupError": "Could not duplicate list", "itemError": "Could not add item",
        "itemError2": "Could not update item", "itemDelError": "Could not delete item", "reorderError": "Could not reorder",
        "shareSaved": "Sharing updated", "shareError": "Could not update sharing"
      },
      "bookingCal": {
        "repeatEditHint": "change how this booking repeats", "repeatUpdated": "Repeat updated — {n} bookings",
        "repeatRemoved": "This booking no longer repeats", "skipDate": "Skip this date", "skipDateQ": "Skip this date?",
        "skipDateDesc": "Only {date} will be removed. The rest of the repeating series stays unchanged. You can restore it later from the Recycle Bin.",
        "dateSkipped": "This date was skipped — the rest of the repeat is unchanged",
        "repeatsPrefix": "Repeats", "recEveryDay": "every day", "recEveryNDays": "every {n} days",
        "recEveryWeek": "every week", "recEvery2Weeks": "every 2 weeks", "recEveryNWeeks": "every {n} weeks",
        "recOn": "on", "recMonthly": "monthly", "recEveryNMonths": "every {n} months", "recUntil": "until {d}",
        "recForN": "for {n} occurrences", "dupDelFailed": "Could not delete the duplicated bookings",
        "listsNav": "Lists", "notesNav": "Notes",
        "agenda": "Agenda",
        "recycleBin": "Recycle Bin",
        "yourRole": "Your role",
        "backupNow": "Backup Now",
        "lastBackup": "Last automatic backup",
        "verified": "VERIFIED",
        "backupFailed2": "CALENDAR BACKUP FAILED",
        "noBackupYet": "No automatic backup yet",
        "myCalendars": "My calendars",
        "sharedWithMe": "Shared with me",
        "createCalendar": "Create calendar",
        "renameCalendar": "Rename calendar",
        "deleteCalendar": "Delete calendar",
        "restore": "Restore",
        "recentlyDeleted": "Recently deleted",
        "createFirstBody": "Create your own private calendar, or wait to be invited to one.",
        "createCalHint": "Your new calendar is private. You become its owner.",
        "calNamePlaceholder": "e.g. Personal, Work, Family",
        "calNameRequired": "Calendar name is required",
        "calCreated": "Calendar created",
        "calRenamed": "Calendar renamed",
        "calDeleted": "Calendar deleted — recoverable for 30 days",
        "calRestored": "Calendar restored",
        "deleteCalendarQ": "Delete this calendar?",
        "deleteCalendarDesc": "The calendar and its bookings will be hidden and can be restored within 30 days.",
        "members": "Members",
        "membersHint": "People who can access this calendar and their roles.",
        "inviteByEmail": "Invite by email",
        "sendInvite": "Invite",
        "invitePending": "Invitation saved — they will get access when they join Find-2-Day",
        "inviteAdded": "Member added",
        "emailRequired": "Email is required",
        "pendingInvites": "Pending invites",
        "makeOwner": "Make owner",
        "remove": "Remove",
        "ownershipTransferred": "Ownership transferred",
        "memberRemoved": "Member removed",
        "removeMemberTitle": "Remove this person?",
        "removeMemberBody": "This will immediately revoke {name}'s access to this calendar. Any bookings they created are kept. You can invite them again later.",
        "removeAccess": "Remove access",
        "thisPerson": "this person",
        "reminders": "Reminders",
        "remindersHint": "Notify calendar members before this booking. Optional — choose any that apply.",
        "remind15": "15 minutes before",
        "remind60": "1 hour before",
        "remind1d": "1 day before",
        "remindNone": "No reminder",
        "reminderSet": "Reminder set",
        "birthday": "Birthday",
        "birthdays": "Birthdays",
        "addBirthday": "Add birthday",
        "editBirthday": "Edit birthday",
        "birthdayHint": "Birthdays repeat every year until you delete them. They are separate from bookings.",
        "personName": "Name",
        "personNamePlaceholder": "e.g. Adele",
        "day": "Day",
        "month": "Month",
        "bdayOnDay": "On the day",
        "bday1Day": "1 day before",
        "bday1Week": "1 week before",
        "birthdayReminderHint": "Reminders notify calendar members at 09:00 (Spain time).",
        "feb29Note": "In non-leap years this birthday is shown and reminded on 28 February.",
        "bdayNameRequired": "Please enter a name",
        "bdayAdded": "Birthday added",
        "bdaySaved": "Birthday updated",
        "bdayDeleted": "Birthday deleted",
        "noBirthdays": "No birthdays yet.",
        "repeatsYearly": "Repeats every year",
        "deleteBirthdayTitle": "Delete this birthday?",
        "deleteBirthdayBody": "This will remove {name} and stop all future yearly reminders.",
        "roleOwner": "Owner",
        "roleAdmin": "Admin",
        "roleMember": "Member",
        "roleView": "View only",
        "repeat": "Repeat",
        "repeatNone": "Does not repeat",
        "repeatDaily": "Every day",
        "repeatWeekly": "Every week",
        "repeatBiweekly": "Every 2 weeks",
        "repeatMonthly": "Every month",
        "repeatCustom": "Custom",
        "every": "Every",
        "days": "days",
        "weeks": "weeks",
        "months": "months",
        "ends": "Ends",
        "endAfter": "After N times",
        "endOn": "On date",
        "endDateRequired": "Please choose an end date for the repeat",
        "applyTo": "Apply changes to",
        "scope_this": "This booking only",
        "scope_future": "This and future bookings",
        "scope_series": "Whole series",
        "repeats": "Repeating booking",
        "repeatsShort": "Repeats",
        "savedSeries": "Added {n} bookings",
        "payment": "Payment",
        "unpaid": "Unpaid",
        "paidStatus": "Paid",
        "free": "Free",
        "price": "Price",
        "amountPaid": "Paid",
        "balance": "Balance",
        "notSpecified": "Not specified",
        "cash": "Cash",
        "card": "Card",
        "bankTransfer": "Bank Transfer",
        "other": "Other",
        "paymentNotePh": "Payment note (e.g. €10 deposit)",
        "paidBadge": "PAID",
        "unpaidBadge": "UNPAID",
        "dueBadge": "DUE",
        "paidExceeds": "Amount paid cannot exceed the price",
        "payAll": "All payments",
        "payDue": "Money due",
        "payPaid": "Paid",
        "payFree": "Free",
        "today": "Today",
        "addBooking": "Add booking",
        "add": "Add",
        "edit": "Edit",
        "reschedule": "Reschedule",
        "pickDate": "Pick a date",
        "cancel": "Cancel",
        "delete": "Delete",
        "history": "History",
        "noBookings": "No bookings from this date onward.",
        "newBooking": "New booking",
        "editBooking": "Edit booking",
        "formHint": "Enter the client and appointment details.",
        "date": "Date",
        "start": "Start",
        "end": "End",
        "clientName": "Client name",
        "service": "Service / appointment",
        "phone": "Telephone",
        "email": "Email",
        "status": "Status",
        "notes": "Notes",
        "savedNew": "Booking added",
        "savedEdit": "Booking updated",
        "savedDupGroup": "All duplicated bookings updated",
        "dupEditTitle": "Apply changes to",
        "dupEditDesc": "This booking is part of a duplicated group of {n} bookings. How would you like to save your changes?",
        "dupEditThis": "This booking only",
        "dupEditAll": "All duplicated bookings",
        "actions": "Actions",
        "saveFailed": "Could not save booking",
        "clientRequired": "Client name is required",
        "notesTooLong": "Notes are too long. Maximum {n} characters.",
        "duplicate": "Duplicate",
        "duplicateBooking": "Duplicate booking",
        "dupHint": "Pick one or more dates. Each becomes a separate, fully independent booking (not a repeating series).",
        "dupModePick": "Pick individual dates",
        "dupModeEveryDay": "Every day",
        "dupFrom": "From",
        "dupTo": "To",
        "dupEveryDayHint": "A booking will be created for every day in this range (inclusive).",
        "dupPickRange": "Please choose a valid From and To date.",
        "dupTooMany": "That range is too large (max {n} days). Please choose a shorter range.",
        "dupOriginal": "Original",
        "dupCopies": "Copies: client, service, phone/email, status, category, notes & payment.",
        "dupSelectDates": "Select new date(s)",
        "dupCopyReminders": "Also copy reminders ({n})",
        "dupNoDates": "No dates selected yet.",
        "dupWillCreate": "This will create {n} independent booking(s).",
        "dupPickDate": "Please select at least one date.",
        "dupNeedTimes": "Please choose a start and end time.",
        "dupEndAfterStart": "End time must be after the start time.",
        "endAfterStart": "End time must be later than start time.",
        "dupCreate": "Duplicate",
        "dupCreating": "Creating...",
        "dupCreated": "Created {n} independent booking(s)",
        "dupPartial": "Created {ok}; {fail} could not be created: {list}",
        "dupAllFailed": "Could not create any bookings",
        "dupFailedRetry": "Could not create the bookings. Please try again in a moment.",
        "dupDelTitle": "Delete duplicated booking",
        "dupDelIntro": "This booking is one of {n} created together by Duplicate.",
        "dupDelThis": "Delete this booking only",
        "dupDelSelect": "Select bookings to delete",
        "dupDelAll": "Delete all duplicated bookings",
        "dupThis": "this one",
        "dupDelSelectedGo": "Delete selected",
        "dupDelAllConfirm": "Delete all {n} duplicated bookings? They will move to the Recycle Bin.",
        "dupDelAllGo": "Delete all",
        "dupDelPickOne": "Please select at least one booking.",
        "dupDelDone": "Deleted {n} booking(s)",
        "dupDelPartial": "Deleted {ok}; {fail} failed",
        "removeMemberTitleName": "Remove {name} from this calendar?",
        "removeMemberBody2": "They will immediately lose access to this calendar. Their existing bookings will NOT be deleted.",
        "rescheduled": "Booking rescheduled",
        "cancelled": "Booking cancelled",
        "deleted": "Booking moved to Recycle Bin",
        "restored": "Booking restored",
        "backupDone": "Backup CSV downloaded",
        "backupFailed": "Backup failed",
        "signInNeeded": "Please sign in to view your calendar.",
        "noAccessTitle": "No calendars yet",
        "noAccessBody": "You don't have access to any calendars. You'll see one here when you're invited.",
        "recycleHint": "Deleted bookings are kept for 30 days and can be restored.",
        "recycleEmpty": "Recycle Bin is empty.",
        "deletedBy": "Deleted by",
        "restore": "Restore",
        "cancelBookingQ": "Cancel this booking?",
        "cancelBookingDesc": "It will be marked as cancelled but remain in the calendar.",
        "keepIt": "Keep it",
        "cancelBooking": "Cancel booking",
        "deleteBookingQ": "Delete this booking?",
        "deleteBookingDesc": "It will move to the Recycle Bin and can be restored within 30 days.",
        "noHistory": "No history yet.",
        "loadError": "Could not load your calendar. Please try again.",
        "calendarNav": "Calendar",
        "agendaNav": "Agenda",
        "searchNav": "Search",
        "upcoming": "Upcoming",
        "past": "Past",
        "cancelledFilter": "Cancelled",
        "searchPlaceholder": "Search client name, service or birthday…",
        "searchPrompt": "Type a client name, service or birthday to search past and future bookings.",
        "noResults": "No bookings or birthdays match your search.",
        "noBookingsDay": "No bookings on this day.",
        "bookingsOn": "Bookings on",
        "category": "Category / colour",
        "noCategory": "No category",
        "allCategories": "All categories",
        "manageCategories": "Manage categories",
        "manageCatHint": "Shared colour categories for this calendar.",
        "newCatName": "New category name",
        "addCategory": "Add category",
        "noCategories": "No categories yet.",
        "catAdded": "Category added",
        "catDeleted": "Category deleted — bookings kept",
        "catNameRequired": "Category name is required",
        "deleteCatQ": "Delete this category?",
        "deleteCatDesc": "Bookings using it will NOT be deleted — they will simply lose this category.",
        "close": "Close",
        "colourInUse": "Already used by another category",
        "by": "by"
      },
      "home": {
        "heroTagline": "Everything happening in your town.",
        "startCardTitle": "Start",
        "startCardDesc": "Discover everything happening in your town.",
        "businessCardTitle": "Business",
        "businessCardDesc": "Promote your business, organisation or community group.",
        "discoverTitle": "Discover businesses & events",
        "localAlertsTitle": "Community Safety Hub",
        "classifiedsTitle": "Sell · Lost · Found",
        "bankHolidaysTitle": "Bank Holidays",
        "emergencyTitle": "Emergency Numbers",
        "businessSignInTitle": "Already have a Business Account? Sign In",
        "businessCalendarTitle": "Business Calendar"
      },
      "frontpageEarnCommission": "Become a Community Partner",
      "appBadgeTitle": "Alerts on your phone",
      "appBadgeDesc": "Show a badge on the app icon and a notification when a new Safety Alert arrives — even when the app is closed.",
      "appBadgeUnsupported": "Not supported on this device",
      "appBadgeEnabled": "Phone alerts on. Install Find-2-Day to your home screen for the icon badge.",
      "appBadgeFallback": "Your device shows notifications but not an icon badge — you'll still get the in-app bell indicator.",
      "safetyOptInTitle": "Never miss a Safety Alert",
      "safetyOptInBody": "Get Official Safety Alerts directly on your phone so you never miss important information from your community.",
      "safetyOptInCta": "Turn on phone alerts",
      "safetyOptInEnabling": "Enabling…",
      "safetyOptInError": "Could not enable alerts. Please try again.",
      // Front Page
      "appTitle": "Find-2-day",
      "appTagline": "Find local trades, experiences, events, and activities near you",
      "getStarted": "Get Started",
      "listYourBusiness": "List Your Business",
      "signIn": "Sign In",
      "joinBusiness": "Join as a Business",
      "categories": {
        "food": "Food & Dining",
        "music": "Music & Nightlife",
        "family": "Family Activities",
        "offers": "Special Offers",
        "wellbeing": "Wellbeing & more"
      },
      
      // Navigation
      "nav": {
        "discover": "Discover",
        "favorites": "Following",
        "events": "Events",
        "offers": "Offers",
        "profile": "Profile",
        "forSale": "Sell / Lost / Found",
        "communityHub": "Community"
      },
      
      // Sign In
      "welcomeBack": "Welcome Back!",
      "signInSubtitle": "Sign in to continue discovering",
      "emailOrUsername": "Email",
      "password": "Password",
      "rememberMe": "Remember me",
      "forgotPassword": "Forgot Password?",
      "forgotPasswordTitle": "Forgot your password?",
      "forgotPasswordDesc": "Enter your email address and we'll send you a link to reset your password.",
      "forgotPasswordError": "Something went wrong. Please try again.",
      "sendResetLink": "Send Reset Link",
      "sending": "Sending...",
      "checkYourEmail": "Check your email",
      "resetEmailSent": "If an account exists for that email, we've sent a password reset link. Please check your inbox and spam folder.",
      "backToSignIn": "Back to Sign In",
      "setNewPassword": "Set a new password",
      "setNewPasswordDesc": "Choose a strong password with at least 6 characters.",
      "newPassword": "New password",
      "confirmPassword": "Confirm password",
      "resetPassword": "Reset Password",
      "resetting": "Resetting...",
      "passwordReset": "Password Reset",
      "passwordResetSuccess": "Your password has been reset successfully. You can now sign in with your new password.",
      "invalidResetLink": "Invalid Reset Link",
      "invalidResetLinkDesc": "This password reset link is invalid or has expired.",
      "requestNewLink": "Request a new reset link",
      "passwordMinLength": "Password must be at least 6 characters",
      "passwordsMustMatch": "Passwords do not match",
      "emailAddress": "Email address",
      "dontHaveAccount": "Don't have an account?",
      "signUp": "Sign Up",
      
      // Business Sign Up
      "businessSignUp": "Business Sign Up",
      "joinAsBusinessTitle": "Join as a Business",
      "freeTrialOffer": "Start listing your business instantly",
      "startInstantlyTagline": "Start listing your business instantly",
      "accountInformation": "Account Information",
      "fullName": "Full Name",
      "email": "Email",
      "businessInformation": "Business Information",
      "businessName": "Business Name",
      "description": "Description",
      "tellUsAbout": "Tell us about your business...",
      "password": "Password",
      "continue": "Continue",
      "signIn": "Sign In",
      
      // Business Sign Up - Placeholders
      "fullNamePlaceholder": "Juan García",
      "passwordPlaceholder": "Create a strong password",
      "businessNamePlaceholder": "Your Business Name",
      
      // Business Sign Up - Step indicator
      "stepOf": "Step {{step}} of {{total}}",
      
      // Business Sign Up - Validation errors
      "allFieldsRequired": "All fields are required",
      "passwordMinLength": "Password must be at least 6 characters",
      "businessNameDescRequired": "Business name and description are required",
      "fillAllRequired": "Please fill in all required fields",
      "specifyCategoryOther": "Please specify your category when selecting \"Other\"",
      "selectPlanRequired": "Please select a subscription plan",
      "registrationFailed": "Registration failed. Please try again.",
      "emailAlreadyRegistered": "This email is already registered. Please use a different email or sign in.",
      
      // Business Sign Up - Step 3: Contact & Location
      "contactLocation": "Contact & Location",
      "categoryLabel": "Category",
      "selectCategory": "Select category",
      "specifyCategory": "Specify Category",
      "specifyCategoryPlaceholder": "e.g., Pet Grooming",
      "cityLabel": "City",
      "businessAddress": "Business Address",
      "addressPlaceholder": "123 Main Street, Madrid, 28001, Spain",
      "addressTip": "Tip: Search your business on Google Maps and copy the full address for best results",
      "phoneNumber": "Phone Number",
      "businessEmailLabel": "Business Email",
      "websiteLabel": "Website",
      "openingHoursLabel": "Opening Hours",
      
      // Business Sign Up - Step 4: Features
      "whatDoesOffer": "What Does Your Business Offer?",
      "selectAllApply": "Select all that apply to help customers find you",
      "groupSizeLabel": "Group Size Accommodation",
      "groupSizeHelp": "Select group sizes your venue can accommodate",
      "eventTypesLabel": "Event Types",
      "eventTypesHelp": "Select types of events you host (if applicable)",
      "specifyEventType": "Specify your event type (e.g., Comedy Shows, Trivia Nights)",
      "venueTypeLabel": "Venue Type",
      "venueTypeHelp": "Select if your venue is indoor, outdoor, or both",
      "dietaryOptionsLabel": "Dietary Options",
      "dietaryOptionsHelp": "Select dietary options you cater to (if applicable)",
      
      // Business Sign Up - Step 5: Additional Features
      "additionalFeaturesTitle": "Additional Features",
      "specialFeatures": "Special Features",
      "accessibilityFacilities": "Accessibility & Facilities",
      
      // Business Sign Up - Step 6: Plan Selection
      "chooseYourPlan": "Choose Your Plan",
      "planSubtitle": "Select a subscription plan. Your listing goes live as soon as payment is confirmed.",
      "b2bExplainerTitle": "Invoicing for a company?",
      "b2bExplainerBody": "On the next (Stripe) screen you'll be able to add your CIF/NIF and company name to receive a fully compliant B2B factura with IVA breakdown. Skip the field if you're subscribing as an individual.",
      "perMonth": "month",
      "plusIVA": "IVA incluido",
      "bestValue": "Best Value",
      "selected": "Selected",
      "freeTrialTitle": "Start listing instantly",
      "freeTrialDesc": "Pay once — your listing goes live as soon as payment is confirmed. All prices include 21% IVA. Cancel anytime.",
      "freeTrialBullet1": "Get started today for €1.25/month (billed annually)",
      "freeTrialBullet2": "Immediate listing once payment is confirmed",
      "freeTrialBullet3": "Cancel anytime from your billing portal",
      "instantListingTitle": "Start listing your business instantly",
      "instantListingDesc": "From just €1.25/month (billed annually, IVA incluido). No trial, no surprises — your listing goes live as soon as your payment is confirmed.",
      "instantBullet1": "Immediate listing once payment is confirmed",
      "instantBullet2": "Cancel anytime from your billing portal",
      "instantBullet3": "All prices include 21% IVA — no surprise charges",
      "settingUp": "Setting up...",
      "proceedToPayment": "Proceed to Payment",
      
      // Business Sign Up - Footer
      "alreadyHaveAccount": "Already have an account?",
      
      // Opening Hours
      "selectDaysHours": "Select days and set opening hours",
      "timeTo": "to",
      "closed": "Closed",
      "dayMonday": "Monday",
      "dayTuesday": "Tuesday",
      "dayWednesday": "Wednesday",
      "dayThursday": "Thursday",
      "dayFriday": "Friday",
      "daySaturday": "Saturday",
      "daySunday": "Sunday",
      
      // Plan duration
      "planBasic": "Basic",
      "plan6months": "6 months",
      "plan12months": "Best Setup / Full Access",
      "perYear": "/year",
      "per6Months": "/6mo",
      "per12Months": "/12mo",
      "ivaIncluded": "IVA incluido",
      "basicMonthlyHint": "Founding Business Offer · FREE for 12 months — no card required",
      "basicRestrictions": "Listing + photos + contact only. No events, offers or boosted promotions.",
      "upgradeFromBasicTitle": "Unlock events, offers & features",
      "upgradeFromBasicDesc": "All businesses are currently free for 12 months under the Founding Business Offer — events, offers, boosted promotions and all listing features are included at no cost during the founding period.",
      "planFeatureLocked": "Not available on the Basic plan",
      "upgradeToUse": "Upgrade to use",

      // Affiliate / referral — Iter 186 unified 50% model
      "commissionHeadlineNew": "Earn 50% of everything your referred businesses pay",
      "commissionHeadlineSub": "subscriptions, renewals, boosts, and every future paid product.",
      // Legacy keys retained as fallbacks so older email content / cached pages
      // still render something coherent. Default values point to the new model.
      "earnUpToPerSignup": "Earn 50% of everything your referrals pay",
      "commissionBasic": "50% of every Basic payment",
      "commission6mo": "50% of every 6-Month payment (legacy plan)",
      "commission12mo": "50% of every 12-Month payment",
      "payoutProgress": "Payout progress",
      "balanceLabel": "Balance",
      "eligibleForPayout": "Eligible for payout",
      "notYetEligible": "Not yet eligible — keep going!",
      "pendingMaturing": "Pending (maturing)",
      "pendingMaturingDesc": "Commissions waiting for the {{days}}-day clearance window.",
      "availableForPayout": "Available for payout",
      "availableForPayoutDesc": "Cleared the {{days}}-day window and ready to be paid out.",
      "clearanceTooltip": "Commissions become available {{days}} days after payment clears, in line with our payment processor's chargeback window. You'll still earn the full amount — it just takes {{days}} days to mature.",
      "maturesInDays": "Matures in {{count}} day",
      "maturesInDays_plural": "Matures in {{count}} days",
      "availableNow": "Available",
      "min": "min",
      "payoutCycleNote": "Payouts are reviewed after a {{days}}-day cooldown (refund window) and processed monthly by the admin team.",
      "minPayoutHint": "Minimum €{{amount}} balance needed for payout.",

      // Renewal commission — plain language for non-technical helpers
      "earnEachRenewal": "+ 50% every time they renew, for as long as they stay",
      "renewalEarnings": "{{count}} renewal · €{{total}}",
      "lifetimeRenewalCommission": "lifetime",
      "renewalCommissionTooltip": "You earned this because the business you referred renewed their plan.",
      "initialCommissionTooltip": "You earned this when the business you referred first signed up.",
      "renewalLabel": "Renewal",
      "initialLabel": "Initial",
      "lifetimeRenewalEarnings": "Lifetime renewal earnings",
      "lifetimeRenewalEarningsHint": "You'll start earning these the moment your referrals renew — same 50% rule. Nothing extra to do.",

      // How you earn — helper signup landing copy
      "howYouEarnTitle": "How you earn — and how you get paid",
      "howYouEarnPoint1": "You earn 50% of every payment a business you referred makes — subscription, renewal, boost, or any future paid product.",
      "howYouEarnPoint2": "Same 50% rule, forever. As long as they keep paying Find-2-Day, you keep earning.",
      "howYouEarnPoint3": "Nothing extra to do — we calculate and track every commission automatically.",
      "howYouEarnPoint4": "Each new earning is held as pending for {{days}} days, in case the business cancels or gets a refund. If they do, that one earning is removed. Anything already paid out to you is yours to keep.",

      // How payouts work — collapsible panel on dashboard
      "howPayoutsWorkTitle": "How payouts work",
      "howPayoutsWorkOpen": "Show how payouts work",
      "howPayoutsWorkClose": "Hide how payouts work",
      "howPayoutsRule1": "You earn 50% of everything a business you referred pays — subscriptions, renewals, boosts, and any future paid product.",
      "howPayoutsRule2": "One rule, no exceptions. As long as they stay with Find-2-Day, you keep earning 50% every time they pay.",
      "howPayoutsRule3": "New earnings are pending for {{days}} days while we wait for the payment to clear with the bank.",
      "howPayoutsRule4": "If the payment fails, is refunded, cancelled, or charged back during those {{days}} days, that earning is removed.",
      "howPayoutsRule5": "Once {{days}} days have passed cleanly, the earning becomes available for your next payout.",
      "howPayoutsRule6": "Earnings already paid out to you are never reversed.",

      // Updated affiliate example under unified 50% model
      "affiliateExampleWithRenewal": "Refer a business: they join the Founding Business Offer — free for 12 months. When paid plans return, you earn 50% commission on every payment they make — signup, renewal, boost — automatically, for as long as they stay.",

      // Notification preferences (single master toggle)
      "notificationPreferences": "Notification preferences",
      "emailNotifications": "Email notifications",
      "emailNotificationsOnDesc": "We'll email you about your favourites, helper updates and weekly digests.",
      "emailNotificationsOffDesc": "Off — we'll stop sending updates and digests. You'll still receive critical emails (password reset, account/security, billing receipts) and important paid-service updates.",
      "emailNotificationsSavedOn": "Email notifications turned on.",
      "emailNotificationsSavedOff": "Email notifications turned off. You'll still receive critical and billing emails.",

      // Active / Archived tabs
      "tabActive": "Active",
      "tabArchived": "Archived",
      "noActiveItems": "Nothing active right now.",
      "noArchivedItems": "Nothing archived yet.",
      "restore": "Restore",
      "restored": "Restored",
      "cantRestoreExpired": "This item has already expired. Please duplicate it instead.",
      "restoreFailed": "Could not restore. Please try again.",

      "payoutProgress": "Payout progress",
      "balanceLabel": "Balance",
      "eligibleForPayout": "Eligible for payout",
      "notYetEligible": "Not yet eligible — keep going!",
      
      // Discover
      "sponsored": "Sponsored",
      "featured": "Featured",
      "findAmazing": "Find amazing experiences near you",
      "searchPlaceholder": "Search businesses...",
      "logout": "Logout",
      "shareApp": "Share Find-2-Day",
      "dash": {
        "tagline": "Your local guide to businesses, events and community.",
        "searchPlaceholder": "Search for businesses, events, offers and more…",
        "discover": "DISCOVER", "discoverSub": "Find what you need in your area",
        "search": "Search", "searchDesc": "Find anything",
        "businesses": "Businesses", "businessesDesc": "Local services",
        "events": "Events", "eventsDesc": "What's on",
        "offers": "Offers", "offersDesc": "Deals near you",
        "myF2d": "MY FIND-2-DAY", "myF2dSub": "Keep track and stay organised",
        "calendar": "Calendar", "calendarDesc": "Plan your time",
        "lists": "Lists", "listsDesc": "Stay organised",
        "inbox": "Inbox", "inboxDesc": "Your updates",
        "favourites": "Favourites", "favouritesDesc": "Save what you love",
        "community": "COMMUNITY", "communitySub": "A safer, stronger community",
        "alerts": "Alerts", "alertsDesc": "Stay informed",
        "localInfo": "Local Information", "localInfoDesc": "Your area guide",
        "business": "BUSINESS", "businessSub": "For local businesses",
        "businessDashboard": "Business Dashboard", "businessDashboardDesc": "Manage your business",
        "account": "ACCOUNT", "accountSub": "Manage your app experience",
        "settings": "Settings", "settingsDesc": "Your preferences",
        "shareDesc": "Tell your friends",
        "help": "Help & Support", "helpDesc": "Get assistance"
      },
      "shareAppText": "Find local businesses, events and offers near you on Find-2-Day.",
      "linkCopied": "Link copied!",
      "all": "All",
      "pendingTab": "Pending",
      
      // Business Detail extra
      "callNow": "Call Now",
      "emailButton": "Email",
      "openingHours": "Opening Hours",
      
      // Business Dashboard status messages
      "pendingApprovalTitle": "Pending Approval",
      "pendingApprovalDesc": "Your business is being reviewed by our team",
      "applicationRejected": "Application Rejected",
      "applicationRejectedDesc": "Please contact support for more information",
      "listingSuspended": "Listing Suspended",
      "listingSuspendedDesc": "Your listing has been suspended. Contact support for details.",
      "trialDaysRemaining": "{{days}} day(s) remaining",
      "subscribePrompt": "Subscribe now to keep your listing active",
      "paymentPendingTitle": "Payment required",
      "paymentPendingDesc": "Complete payment to make your listing visible. Your listing will go live as soon as payment is confirmed.",
      "paymentRequiredTitle": "Payment required to go live",
      "paymentRequiredDesc": "Your business is currently on the Founding Business Offer — free for 12 months, no card required.",
      "businessVisible": "Your business is visible to users",
      "subscriptionActive": "Subscription Active",
      "subscriptionExpired": "Subscription Expired",
      "subscriptionExpiredDesc": "Your listing is hidden. Subscribe to reactivate.",
      "renewsOn": "Renews {{date}} ({{days}} days)",
      "listingActive": "Your listing is active",
      
      // Photos
      "noPhotosDesc": "No photos yet. Add up to 5 photos of your business.",
      
      // Profile
      "businessAccount": "Business Account",
      "regularAccount": "User Account",
      "activeFilters": "Active Filters",
      "clearAll": "Clear All",
      "noBusinessesFound": "No businesses found.",
      "tryDifferentFilters": "Try adjusting your filters or search terms.",
      "businessesWillAppear": "Businesses will appear here once approved by admin.",
      "clearFilters": "Clear Filters",
      "registerBusiness": "Register Your Business",
      "businessFound": "Business Found",
      "businessesFound": "Businesses Found",
      
      // Filters
      "filters": "Filters",
      "groupSize": "Group Size",
      "dietaryOptions": "Dietary Options",
      "distance": "Distance",
      "openingHours": "Opening Hours",
      "openNow": "Open Now",
      "openLaterToday": "Open Later Today",
      "openWeekends": "Open Weekends",
      "eventType": "Event Type",
      "venueType": "Venue Type",
      "businessCategories": "Business Categories",
      "additionalOptions": "Additional Options",
      "accessibilityFacilities": "Accessibility & Facilities",
      "resetAll": "Reset All",
      "applyFilters": "Apply Filters",
      
      // Settings
      "settings": "Settings",
      "preferences": "Preferences",
      "language": "Language",
      "notifications": "Notifications",
      "darkMode": "Dark Mode",
      "comingSoon": "Coming soon",
      "account": "Account",
      "privacySecurity": "Privacy & Security",
      "support": "Support",
      "helpCenter": "Help Center",
      "aboutApp": "About Find-2-Day",
      
      // Help Center
      "faqTitle": "Frequently Asked Questions",
      "faq1q": "How do I register my business?",
      "faq1a": "Click 'Join as a Business' on the home page and follow the 5-step registration process.",
      "faq2q": "How does billing work?",
      "faq2a": "All businesses are currently free for 12 months under the Founding Business Offer — no card required, no payment during the founding period. Future pricing (if introduced) will be communicated well in advance.",
      "faq3q": "When will my business appear?",
      "faq3a": "Your business will be visible after admin approval, usually within 24-48 hours.",
      "contactUs": "Contact Us",
      
      // About
      "aboutDescription": "Find-2-Day is your local discovery platform. Find restaurants, events, activities, and special offers from businesses near you.",
      "trialPeriod": "Billing",
      "months": "months",
      "subscriptionPlans": "Subscription Plans",
      "allRightsReserved": "All rights reserved.",
      
      // Profile
      "changePassword": "Change Password",
      "profilePicture": "Profile Picture",
      
      // Events & Offers Pages
      "noEventsYet": "No Events Yet",
      "eventsWillAppear": "Events from local businesses will appear here soon!",
      "browseBusinesses": "Browse Businesses",
      "noOffersYet": "No Special Offers Yet",
      "offersWillAppear": "Check back soon for amazing deals from local businesses!",
      "noFavoritesYet": "No Favorites Yet",
      "startExploring": "Start exploring businesses and save your favorites here!",
      "discoverBusinesses": "Discover Businesses",
      
      // Common
      "save": "Save",
      "saved": "Saved",
      "removedFromSaved": "Removed",
      "followNeedsNotifications": "Allow notifications to follow businesses.",
      "couldNotSave": "Could not save — please try again.",
      "emailNotificationsUpdated": "Email notifications updated",
      "emailSaveFailed": "Failed to save. Please try again.",
      "cancel": "Cancel",
      "editName": "Edit display name",
      "displayNameSaved": "Display name updated.",
      "displayNameSaveFailed": "Could not update your name. Please try again.",
      "nameRequired": "Name is required.",
      "nameTooLong": "Please keep it under 80 characters.",
      "consentText": "I agree to the",
      "consentAnd": "and",
      "consentRequired": "You must agree to the Terms & Conditions and Privacy Policy",
      "termsAndConditions": "Terms & Conditions",
      "privacyPolicyTitle": "Privacy Policy",
      "disputePolicyTitle": "Dispute Policy",
      "disputePolicy": "Disputes",
      "date": "Date",
      "continue": "Continue",
      "back": "Back",
      "submit": "Submit",
      "or": "or",
      "loading": "Loading...",
      "error": "Error",
      "success": "Success",
      "goBack": "Go Back",
      "goHome": "Go Home",
      "admin": "Admin",
      
      // Legal footer links
      "terms": "Terms",
      "privacy": "Privacy",
      "cookies": "Cookies",
      
      // Signup page
      "createAccount": "Create your account",
      "confirmPassword": "Confirm Password",
      "creatingAccount": "Creating Account...",
      
      // Business Dashboard
      "businessDashboard": "Business Dashboard",
      "businessNotFound": "Business profile not found",
      "listingUnavailable": "This business listing is no longer available.",
      "duplicateViewBtn": "View listing",
      "claimRetiredTitle": "Claiming is no longer available",
      "claimRetiredBody": "Find-2-Day now works by self-registration. If you run a business, simply create your own listing — no need to claim an existing record.",
      "claimRetiredRegister": "Register your business",
      "backToHome": "Back to home",
      "townCityLabel": "Town / City", "townCityPlaceholder": "e.g. Madrid", "countryLabel": "Country", "countrySelect": "Select a country", "regionLabel": "Region / State / Province", "regionPlaceholder": "e.g. Andalucía", "localAreaLabel": "Local area / District", "postcodeLabel": "Postcode / ZIP", "regionLabel2": "Region / State", "provinceLabel": "Province", "provincePlaceholder": "e.g. Almería", "mapView": "Map", "mapNoCoords": "None of these businesses have map locations yet. Switch to list view to see all results.", "directionsBtn": "Directions", "filterCountry": "Country", "filterRegion": "Region / State", "filterProvince": "Province", "filterTown": "Town / City", "allCountries": "All countries", "allRegions": "All regions", "allProvinces": "All provinces", "allTowns": "All towns", "locationFilterLabel": "Location",
      "invalidBusinessLink": "Sorry, this business link appears to be invalid or expired.",
      "brokenLinkTitle": "This business link doesn't work",
      "browseBusinesses": "Browse Businesses",
      "returnHome": "Return Home",
      "emailNotVerified": "Email not verified",
      "emailNotVerifiedDesc": "Your listing won't be publicly visible until you verify your email. Check your console/inbox for the verification link.",
      "subscriptionDetails": "Subscription Details",
      "plan": "Plan",
      "noPlanSelected": "No plan selected",
      "status": "Status",
      "daysRemaining": "days remaining",
      "fullyActive": "Fully active",
      "approval": "Approval",
      "payment": "Payment",
      "confirmed": "Confirmed",
      "pending": "Pending",
      "trialEnds": "Subscription Ends",
      "subscriptionEnds": "Subscription Ends",
      "paymentNotCompleted": "Payment not completed",
      "completePaymentDesc": "Complete your payment to activate your business listing.",
      "completePayment": "Complete Payment",
      "processing": "Processing...",
      "photos": "Photos",
      "addPhoto": "Add Photo",
      "uploading": "Uploading...",
      "noPhotosYet": "No photos yet. Add up to 5 photos of your business.",
      "businessInfo": "Business Information",
      "editProfile": "Edit Profile",
      "saveChanges": "Save Changes",
      "cancelEdit": "Cancel",
      "website": "Website",
      "openingHoursLabel": "Opening Hours",
      "notProvided": "Not provided",
      "featuresServices": "Features & Services",
      "groupSizeAccommodation": "Group Size Accommodation",
      "eventTypesLabel": "Event Types",
      "venueTypeLabel": "Venue Type",
      "dietaryOptionsLabel": "Dietary Options",
      "additionalFeaturesLabel": "Additional Features",
      "accessibilityLabel": "Accessibility & Facilities",
      "noneSpecified": "None specified",
      "quickActions": "Quick Actions",
      "viewPublicProfile": "View Public Profile",
      "accountSettings": "Account Settings",
      "trialExpired": "Subscription expired",
      "trialExpiredDesc": "Your business listing is no longer visible to users. Subscribe now to restore it.",
      "trialEndsOn": "Your subscription ends on",
      "trialEndsOnDesc": "Renew to keep your listing visible.",
      "subscribeNow": "Subscribe Now",
      "day": "day",
      "days": "days",
      "left": "left",
      "remaining": "remaining",
      
      // Business Detail
      "about": "About",
      "share": "Share",
      "copyLink": "Copy Link",
      "linkCopied": "Copied!",
      "shareMore": "More",
      "shareDefaultText": "Check out this listing on Find-2-Day!",
      "groupSizesWelcome": "Group Sizes Welcome",
      "contactInfo": "Contact Information",
      
      // Verify Email
      "verifyingEmail": "Verifying your email...",
      "verifySuccessDesc": "You can now close this page or sign in to your dashboard.",
      
      // Data Deletion
      "deleteAccountTitle": "Delete Account & Data",
      "whatWillBeDeleted": "What will be deleted:",
      "deleteItem1": "Your user account and login credentials",
      "deleteItem2": "Business listing and photos (if applicable)",
      "deleteItem3": "Favourites and saved preferences",
      "deleteItem4": "All associated personal data",
      "reasonOptional": "Reason (optional)",
      "reasonPlaceholder": "Tell us why you're leaving...",
      "requestDeletion": "Request Account Deletion",
      "emailDirectly": "You can also email your request directly to:",
      "requestReceived": "Request Received",
      "requestReceivedDesc": "Your account and data deletion request has been submitted. We will process it within 30 days and confirm via email.",
      "contactDirectly": "You can also contact us directly at",
      
      // Profile
      "memberSince": "Member since",
      
      // Settings links
      "nearbyDeals": "Nearby Deals & Promotions",
      "nearbyDealsDesc": "Receive occasional sponsored offers from businesses you do not follow",
      "sponsoredDeals": {
        "label": "Show sponsored deals",
        "helper": "Turn on to see sponsored deals from businesses across your region."
      },
      "yourAlerts": {
        "label": "Your Alerts",
        "helper": "Tap to see updates from businesses you follow, your towns, and important updates."
      },
      "discoverOnboarding": {
        "stepN": "Step {{n}} of {{total}}",
        "skip": "Skip",
        "next": "Next",
        "finish": "Finish",
        "done": "Done",
        "chooseTowns": "Choose your towns",
        "step1": {
          "title": "Discover what's happening around you",
          "body": "Get updates from local businesses, events, and important alerts."
        },
        "step2": {
          "title": "Stay updated",
          "body": "Get alerts from businesses you follow, your towns, and important updates."
        },
        "step3": {
          "title": "Discover new places",
          "body": "Turn this on to see sponsored deals from across your region."
        },
        "step4": {
          "title": "You're all set",
          "body": "Want even more relevant updates? Choose the towns you care about."
        }
      },
      "enableNotificationsFirst": "Follow a business or enable notifications to manage your preferences.",
      "legal": "Legal",
      "termsOfService": "Terms of Service",
      "privacyPolicy": "Privacy Policy",
      "cookiePolicy": "Cookie Policy",
      "deleteData": "Delete My Data",
      
      // Admin Dashboard
      "adminDashboard": "Admin Dashboard",
      "viewSite": "View Site",
      "totalBusinesses": "Total Businesses",
      "pendingApproval": "Pending Approval",
      "approved": "Approved",
      "active": "Active (Visible)",
      "suspended": "Suspended",
      "rejected": "Rejected",
      "noBusinessesInCategory": "No businesses found in this category",
      "loadingBusinesses": "Loading businesses...",
      "approveBusiness": "Approve Business",
      "rejectBusiness": "Reject Business",
      "deleteBusiness": "Delete Business",
      "suspendBusiness": "Suspend Business",
      "approveDesc": "This will make the business visible to all users.",
      "rejectDesc": "Please provide a reason for rejection.",
      "deleteDesc": "This will soft-delete the business — it will be hidden from the public feed but recoverable via the Trash filter.",
      "suspendDesc": "This will hide the business from public view. Please provide a reason.",
      "enterRejectReason": "Enter reason for rejection...",
      "enterSuspendReason": "Enter reason for suspension...",
      "yesApprove": "Yes, Approve",
      "yesDelete": "Yes, Delete",
      "yesSuspend": "Yes, Suspend",
      "approving": "Approving...",
      "rejecting": "Rejecting...",
      "deleting": "Deleting...",
      "suspending": "Suspending...",
      "approve": "Approve",
      "reject": "Reject",
      "delete": "Delete",
      "suspend": "Suspend",
      "view": "View",
      
      // Business Signup form extras
      "selectCategory": "Select category",
      "selectDays": "Select days and set opening hours",
      "closed": "Closed",
      
      // Change Password
      "changePasswordTitle": "Change Password",
      
      // Admin login
      "adminAccess": "Access platform administration",
      
      // Business login
      "businessLogin": "Business Login",
      "businessLoginDesc": "Sign in to manage your business listing",
      
      // Analytics
      "listingAnalytics": "Listing Analytics",
      "views": "Views",
      "calls": "Calls",
      "directions": "Directions",
      
      // Event Management (Admin)
      "eventManagement": "Event Management",
      "businessManagement": "Business Management",
      "pendingEvents": "Pending Events",
      "approvedEvents": "Approved Events",
      "rejectedEvents": "Rejected Events",
      "expiredEvents": "Expired Events",
      "allEvents": "All Events",
      "approveEvent": "Approve Event",
      "rejectEvent": "Reject Event",
      "approveEventDesc": "This will make the event visible to all users.",
      "rejectEventDesc": "Please provide a reason for rejecting this event.",
      "enterEventRejectReason": "Enter reason for rejecting this event...",
      "yesApproveEvent": "Yes, Approve Event",
      "noEventsFound": "No events found in this category",
      "loadingEvents": "Loading events...",
      "eventBy": "by",
      "eventLocation": "Location",
      "eventDates": "Dates",
      "eventCreated": "Created",
      
      // Business Dashboard - My Events
      "myEvents": "My Events",
      "createEvent": "Create Event",
      "noEventsCreated": "You haven't created any events yet",
      "eventPending": "Pending Review",
      "eventApproved": "Approved",
      "eventRejected": "Rejected",
      "eventExpired": "Expired",
      "rejectionReasonLabel": "Rejection Reason",
      
      // Event Creation Form
      "newEvent": "New Event",
      "eventTitle": "Event Title",
      "eventTitlePlaceholder": "Enter event title",
      "eventDescription": "Description",
      "eventDescriptionPlaceholder": "Describe your event...",
      "eventCategory": "Category",
      "selectEventCategory": "Select a category",
      "eventDate": "Event Date",
      "startTime": "Start Time",
      "endTime": "End Time",
      "eventPrice": "Price (optional)",
      "eventPricePlaceholder": "e.g. 15.00",
      "free": "Free",
      "eventPoster": "Event Poster (optional)",
      "uploadPoster": "Upload Poster",
      "removePoster": "Remove",
      "eventVenue": "Venue / Location",
      "eventVenuePlaceholder": "Event location or venue name",
      "submitEvent": "Submit Event",
      "creatingEvent": "Creating...",
      "eventCreatedSuccess": "Event created and published!",
      "eventCreateFailed": "Could not create event. Please try again.",
      "eventUpdateFailed": "Could not update event. Please try again.",
      "eventsBlockedTrialEnded": "Your trial has ended. Add a payment method to publish events again.",
      "eventsBlockedPlan": "Your current plan doesn't include events. Upgrade to post events.",
      "eventsBlockedMax": "You've reached your limit of {{max}} active events. Archive an event below or upgrade to Featured for more.",
      "eventsActiveHint": "Active = live and upcoming events only. Past/expired events don't count against your limit.",
      "eventTitleRequired": "Title is required",
      "eventDateRequired": "Date is required",
      "eventStartTimeRequired": "Start time is required",
      "eventEndTimeRequired": "End time is required",
      "eventEndAfterStart": "End time must be after start time",
      "eventCategoryRequired": "Category is required",
      "customCategoryRequired": "Please describe the event type",
      "customCategoryPlaceholder": "Describe the event type (e.g. Car Boot Sale, Pet Show)",
      "eventVenueRequired": "Venue is required",
      "categoryMusic": "Music",
      "categorySports": "Sports",
      "categoryFood": "Food & Drink",
      "categoryArts": "Arts & Culture",
      "categoryNetworking": "Networking",
      "categoryCommunity": "Community",
      "categoryWorkshop": "Workshop",
      "categoryMarket": "Market",
      "categoryFestival": "Festival",
      "categoryCharity": "Charity",
      
      // Event Edit & Delete
      "editEvent": "Edit",
      "archiveEvent": "Archive",
      "updateEvent": "Update Event",
      "updatingEvent": "Updating...",
      "editEventTitle": "Edit Event",
      "eventUpdatedSuccess": "Event updated and published!",
      "eventArchivedSuccess": "Event archived successfully.",
      "archiveEventConfirm": "Archive this event?",
      "archiveEventDesc": "This will hide the event from all listings. This action cannot be undone.",
      "yesArchive": "Yes, Archive",
      "eventArchived": "Archived",
      
      // Category translations
      "categoryServices": "Services",
      "categoryShopping": "Shopping",
      "categoryHealth": "Health & Beauty",
      "categoryHealthWellness": "Health & Wellness",
      "categoryEvents": "Events & Entertainment",
      "categoryEducation": "Education",
      "categoryHomeServices": "Home Services",
      "categoryTravel": "Travel & Tourism",
      "categoryTechnology": "Technology",
      "categoryVets": "Vets",
      "categoryVolunteerWork": "Volunteer Work",
      "categoryJobs": "Jobs",
      "categoryCommunityHelp": "Community Help",
      "categoryCommunity": "Community",
      "categoryAutomotive": "Automotive",
      "categorySports": "Sports & Fitness",
      "categoryArts": "Arts & Culture",
      "categoryBeautySpa": "Beauty & Spa",
      "categoryProfessional": "Professional Services",
      "allCategories": "All Categories",
      "categoryOther": "Other",
      
      // Reviews
      "reviews": "Reviews",
      "writeReview": "Write a Review",
      "yourRating": "Your Rating",
      "yourReview": "Your Review",
      "reviewPlaceholder": "Share your experience with this business (min 10 characters)...",
      "submitReview": "Submit Review",
      "submittingReview": "Submitting...",
      "reviewSubmitted": "Review submitted! It will appear after admin approval.",
      "alreadyReviewed": "You have already reviewed this business",
      "reviewTextMin": "Review must be at least 10 characters",
      "reviewTextMax": "Review must be 500 characters or less",
      "ratingRequired": "Please select a rating",
      "noReviewsYet": "No reviews yet. Be the first!",
      "averageRating": "Average Rating",
      "reviewCount": "{{count}} reviews",
      "reviewPending": "Pending",
      "reviewApproved": "Approved",
      "reviewRejected": "Rejected",
      "loginToReview": "Sign in to leave a review",
      "reviewModeration": "Review Moderation",
      "pendingReviews": "Pending Reviews",
      "approvedReviews": "Approved Reviews",
      "rejectedReviews": "Rejected Reviews",
      "allReviews": "All Reviews",
      "approveReview": "Approve",
      "rejectReview": "Reject",
      "noReviewsFound": "No reviews found",
      "reviewBy": "by",
      "reviewFor": "for",
      "charsRemaining": "{{count}} characters remaining",

      // Reports/Disputes
      "reportTitle": "Report Content",
      "reportingItem": "Reporting: {{name}}",
      "reportReason": "Why are you reporting this?",
      "reportReasonRequired": "Please select a reason",
      "reportDetails": "Additional details",
      "reportDetailsPlaceholder": "Please describe the issue in detail (min 10 characters)...",
      "reportDetailsMin": "Details must be at least 10 characters",
      "submitReport": "Submit Report",
      "submitting": "Submitting...",
      "reportSubmitted": "Report Submitted",
      "reportSubmittedDesc": "Our team will review your report and take appropriate action. Content is not automatically removed.",
      "reportAlreadyExists": "You already have an open report for this item",
      "reportSubmitError": "Error submitting report. Please try again.",
      "done": "Done",
      "reportListing": "Report this listing",
      "reportReview": "Report review",
      "loginToReport": "Sign in to report this listing",
      "reportReason_incorrect_information": "Incorrect Information",
      "reportReason_offensive_content": "Offensive Content",
      "reportReason_spam": "Spam",
      "reportReason_duplicate": "Duplicate",
      "reportReason_scam_fraud": "Scam / Fraud",
      "reportReason_other": "Other",
      "reportManagement": "Reports",
      "openReports": "Open",
      "resolvedReports": "Resolved",
      "dismissedReports": "Dismissed",
      "allReports": "All",
      "allTypes": "All Types",
      "reportTypeBusiness": "Business",
      "reportTypeEvent": "Event",
      "reportTypeReview": "Review",
      "reportType_business": "Business",
      "reportType_event": "Event",
      "reportType_review": "Review",
      "reportStatus_open": "Open",
      "reportStatus_resolved": "Resolved",
      "reportStatus_dismissed": "Dismissed",
      "reportedBy": "Reported by",
      "reason": "Reason",
      "adminNotes": "Admin Notes",
      "resolveReport": "Resolve",
      "dismissReport": "Dismiss",
      "editNotes": "Notes",
      "saveNotes": "Save",
      "reportResolved": "Report marked as resolved",
      "reportDismissed": "Report dismissed",
      "notesUpdated": "Notes updated",
      "loadingReports": "Loading reports...",
      "noReportsFound": "No reports found",

      // Payment Pages
      "paymentVerifying": "Verifying Payment...",
      "paymentVerifyingDesc": "Please wait while we confirm your payment with Stripe.",
      "paymentChecking": "Checking... ({{current}}/{{total}})",
      "paymentSuccessTitle": "Payment Successful!",
      "paymentSuccessDesc": "Your business has been registered successfully.",
      "paymentNextSteps": "What happens next:",
      "paymentNextStep1": "Your subscription is active — your listing is live",
      "paymentNextStep2": "Your listing is pending admin approval",
      "paymentNextStep3": "You'll be notified once approved",
      "goToDashboard": "Go to Dashboard",
      "paymentExpiredTitle": "Session Expired",
      "paymentErrorTitle": "Payment Verification Issue",
      "paymentExpiredDesc": "Your payment session has expired. Please try signing up again.",
      "paymentErrorDesc": "We couldn't verify your payment right now. Your account has been created — please sign in and check your dashboard.",
      "goHome": "Go Home",
      "paymentCancelledTitle": "Payment Cancelled",
      "paymentCancelledDesc": "Your payment was not completed. Your account has been created but payment is still required to activate your business listing.",
      "paymentCancelledNote": "You can sign in to your account and complete the payment later from your business dashboard.",
      "signInToCompletePayment": "Sign In to Complete Payment",

      // Seed Listings
      "seedListing": "Seed",
      "unclaimed": "Unclaimed",
      "seedPlan": "Source",
      "allSources": "All Sources",
      "seedOnly": "Seed Listings",
      "paidOnly": "Paid Listings",

      // Feature labels (DB values)
      "feature": {
        "solo": "Solo",
        "couple": "Couple (2)",
        "smallGroup": "Small Group (3-5)",
        "mediumGroup": "Medium Group (6-10)",
        "largeGroup": "Large Group (10+)",
        "vegan": "Vegan",
        "vegetarian": "Vegetarian",
        "glutenFree": "Gluten-Free",
        "halal": "Halal",
        "kosher": "Kosher",
        "dairyFree": "Dairy-Free",
        "nutFree": "Nut-Free",
        "eventMusic": "Music",
        "eventFoodDrink": "Food & Drink",
        "eventWorkshop": "Workshop",
        "eventFamilyFriendly": "Family-Friendly",
        "eventArtsCulture": "Arts & Culture",
        "eventSports": "Sports",
        "eventNightlife": "Nightlife",
        "eventOther": "Other",
        "indoor": "Indoor",
        "outdoor": "Outdoor",
        "petFriendly": "Pet-Friendly",
        "kidFriendly": "Kid-Friendly",
        "adultsOnly": "Adults Only (18+)",
        "hasSpecialOffers": "Has Special Offers",
        "parkingAvailable": "Parking Available",
        "wheelchairAccess": "Wheelchair/Disabled Access",
        "disabledToilets": "Disabled Toilets Available",
        "hasToilets": "Has Toilets"
      },

      // Section headings
      "featuresAmenities": "Features & Amenities",
      "noFeaturesYet": "No features or services added yet.",
      "addFeatures": "Add Features & Services",
      "featureEditHint": "Click items to add or remove them. Save when done.",
      "visitWebsite": "Visit Website",

      // Business Claim Workflow
      "claimBanner": "Own this business? Claim it on Find-2-Day",
      "claimThisBusiness": "Claim This Business",
      "claimFormTitle": "Claim This Business",
      "claimFormDesc": "Submit a claim request and our team will verify your ownership.",
      "claimMessage": "Message (optional)",
      "claimMessagePlaceholder": "Tell us how you're connected to this business...",
      "submitClaim": "Submit Claim",
      "submittingClaim": "Submitting...",
      "claimSubmitted": "Claim Submitted",
      "claimSubmittedDesc": "Our team will review your claim and get back to you.",
      "claimAlreadyPending": "You already have a pending claim for this business",
      "claimAlreadyClaimed": "This business has already been claimed",
      "claimError": "Error submitting claim. Please try again.",

      // Notifications
      "favourite": "Follow",
      "favourited": "Following",
      "followAlerts": "Get Alerts",
      "following": "Alerts On",
      "enableNotifications": "Enable Notifications",
      "enabling": "Enabling...",
      "notificationSettings": "Notification Settings",
      "notificationPromptDesc": "Get notified about new events and offers from businesses you follow, and from across Almería.",
      "areaAlerts": "Area Alerts",
      "areaAlertsDesc": "Get notified about all new events and offers in these areas.",
      "followingCount": "Alerts on for {{count}} listings",
      "favouritesCount": "{{count}} saved favourites",
      "notificationUnavailable": "Push notifications are not available on this device or browser.",
      "notificationUnavailableHint": "Try using Chrome on Android or Safari on iOS. Make sure notifications are not blocked for this site.",
      "notificationEnableFailed": "Could not enable notifications. Please check your browser allows notifications for this site.",
      "notifIOSTitle": "Add to Home Screen to enable notifications",
      "notifIOSStep1": "Tap the Share button",
      "notifIOSStep1b": "at the bottom of Safari",
      "notifIOSStep2": "Scroll down and tap \"Add to Home Screen\"",
      "notifIOSStep3": "Open the app from your home screen and come back here",
      "notifIOSUseSafari": "Open this site in Safari first, then add it to your home screen.",
      "notifIOSStandalone": "Notifications require iOS 16.4 or later. Please update your device if possible.",
      "notifIOSShort": "Add this app to your home screen to enable notifications.",
      "notifAndroidChrome": "Please open this site in Chrome to enable notifications.",
      "notifAndroidChromeHint": "Copy the URL and paste it into Google Chrome.",
      "notifAndroidShort": "Open in Chrome to enable notifications.",
      "notifGeneric": "Push notifications are not supported in this browser.",
      "notifGenericHint": "Try Chrome on Android or Safari on iPhone.",
      "notifGenericShort": "Try Chrome (Android) or Safari (iPhone) for notifications.",
      "claimStatusPending": "Your claim is pending review",
      "claimStatusApproved": "Your claim has been approved!",
      "claimStatusRejected": "Your claim was not approved",
      "loginToClaim": "Sign in to claim this business",
      "claimManagement": "Claims",
      "pendingClaims": "Pending Claims",
      "approvedClaims": "Approved Claims",
      "rejectedClaims": "Rejected Claims",
      "allClaims": "All Claims",
      "noClaims": "No claims found",
      "loadingClaims": "Loading claims...",
      "approveClaim": "Approve Claim",
      "rejectClaim": "Reject Claim",
      "approveClaimDesc": "This will link the business to the claimant's account and reject other pending claims.",
      "rejectClaimDesc": "The listing will remain unclaimed.",
      "claimantName": "Claimant",
      "claimantEmail": "Email",
      "claimFor": "Business",
      "claimMessage_label": "Message",

      // Enhanced claim form
      "claimStepDetails": "Details",
      "claimStepVerify": "Verify Email",
      "claimStepProof": "Proof & Submit",
      "claimFullName": "Full Name",
      "claimFullNamePlaceholder": "Your full name",
      "claimRole": "Role",
      "claimRoleOwner": "Owner",
      "claimRoleManager": "Manager",
      "claimRoleRep": "Authorized Representative",
      "claimRoleAuthorized_rep": "Authorized Rep",
      "claimPhone": "Phone",
      "claimMessageLabel": "Additional Notes",
      "claimNext": "Continue",
      "claimBack": "Back",
      "claimSkipVerification": "Skip for Now",
      "claimNameRequired": "Full name is required",
      "claimPhoneRequired": "Phone number is required",
      "claimFillRequired": "Please fill in your name and phone number",
      "claimMaxFiles": "Maximum 3 files allowed",
      "claimUploadFailed": "File upload failed. Try again.",
      "claimEmailVerifyTitle": "Verify Your Access to This Business",
      "claimEmailVerifyDesc": "We'll send a 6-digit code to the email address on file for this listing. Entering it proves you have access.",
      "claimSendCode": "Send Code",
      "claimResendCode": "Resend",
      "claimSendingCode": "Sending...",
      "claimCodeSentTo": "Code sent to",
      "claimCodeSentCheck": "Check your inbox",
      "claimVerifyBtn": "Verify",
      "claimVerifySendFailed": "Could not send verification email",
      "claimVerifyFailed": "Verification failed. Check the code and try again.",
      "claimEmailVerified": "Email verified successfully",
      "claimEmailVerifyRequired": "Please verify your email before submitting",
      "claimYourEmail": "Your Email",
      "claimAutoApproved": "Claim Approved!",
      "claimAutoApprovedDesc": "Your details matched our records. You now have full access to manage this listing.",
      "claimProofTitle": "Supporting Documents",
      "claimProofDesc": "Upload documents like a business license, utility bill, tax ID, or Google Business screenshot. (Max 3 files, 5MB each)",
      "claimProofHint": "Business license, utility bill, or Google Business screenshot",
      "claimEmailRequired": "Please enter a valid email address",
      "optional": "optional",
      "claimUploadFile": "Upload Document",
      "claimUploading": "Uploading...",
      "claimSummary": "Claim Summary",
      "claimEmailStatus": "Email Verification",
      "claimVerifiedBadge": "Verified",
      "claimNotVerified": "Not verified",
      "claimProofDocs": "Proof Documents",
      "claimProofDoc": "Document",
      "claimSubmitFinal": "Submit Claim",
      "claimEmailVerifiedBadge": "Email Verified",
      "claimEmailMatchBadge": "Email Match",
      "claimPhoneMatchBadge": "Phone Match",
      "claimAutoApprovedBadge": "Auto-Approved",
      "claimantDetails": "Claimant",
      "listingDetails": "Listing on File",

      // Claim visibility & landing page
      "alreadyListedClaim": "Already listed? Claim your business",
      "unclaimedListing": "Unclaimed Listing",
      "unclaimedContactNotice": "This business is currently unclaimed. Some contact details are limited.",
      "claimToUnlock": "Claim this business to unlock full details and receive enquiries",
      "verifiedOwner": "Verified Business Owner",
      "claimTopBannerText": "Is this your business? Claim it now and start receiving customers today",
      "claimNowBtn": "Claim Now",
      "signInToClaim": "Sign In to Claim",
      "claimPageTitle": "Claim Your Business",
      "claimPageSubtitle": "Your business is already listed on Find-2-Day. Take control, update your details, and connect with new customers.",
      "claimFindMyBusiness": "Find My Business",
      "claimHowItWorks": "How It Works",
      "claimStep1Title": "Find Your Business",
      "claimStep1Desc": "Browse our directory and locate your business listing on Find-2-Day.",
      "claimStep2Title": "Submit Your Claim",
      "claimStep2Desc": "Click 'Claim This Business' and tell us how you're connected.",
      "claimStep3Title": "Get Verified",
      "claimStep3Desc": "Our team reviews your claim and you get full control of your listing.",
      "claimWhyClaim": "Why Claim Your Business?",
      "claimBenefit1": "Appear higher in search results and reach more local customers",
      "claimBenefit2": "Update your photos, hours, menu, and contact details anytime",
      "claimBenefit3": "Respond to reviews and build trust with your community",
      "claimBenefit4": "Post events and special offers to attract new visitors",
      "claimCtaTitle": "Ready to Take Control?",
      "claimCtaDesc": "Join hundreds of local businesses already growing with Find-2-Day.",
      "claimCreateAccount": "Create an Account",
      "claimNeedHelp": "Can't find your business or need help?",
      "claimSearchTitle": "Search for Your Business",
      "claimSearchPlaceholder": "Enter your business name...",
      "claimSearchBtn": "Search",
      "claimSearchNoResults": "No unclaimed businesses found with that name. Try a different search or browse all listings.",
      "claimCantFind": "Can't find your business?",
      "claimListYourselfDesc": "List your business directly on Find-2-Day and create your business profile.",
      "claimListMyBusinessBtn": "List My Business",

      // PWA Install Prompt — Iter 190 wording (sounds like an app, not a website)
      "pwaInstallTitle": "Install the Find-2-Day app",
      "pwaInstallDesc": "Get the Find-2-Day app — full-screen, faster, and with alerts.",
      "pwaInstallBtn": "Install app",
      "pwaInstallTapHint": "Tap here to see how",
      "pwaInstallDesktopHint": "Open this page on your phone to get the Find-2-Day app.",
      "pwaHelpIOSTitle": "Get the Find-2-Day app on iPhone",
      "pwaHelpIOSIntro": "iPhones add the Find-2-Day app from Safari's share menu. Just follow these three steps:",
      "pwaHelpIOSStep1": "Tap the Share button at the bottom of Safari (the square with an arrow pointing up).",
      "pwaHelpIOSStep2": "Scroll down in the share sheet and tap \"Add to Home Screen\".",
      "pwaHelpIOSStep3": "Tap \"Add\" in the top-right corner. The Find-2-Day app will now appear on your Home Screen like any other app.",
      "pwaHelpIOSNote": "Add to Home Screen only works inside Safari. If you're using Chrome or another browser on iPhone, open this page in Safari first.",
      "pwaHelpAndroidTitle": "Get the Find-2-Day app on Android",
      "pwaHelpAndroidIntro": "If the install prompt didn't appear automatically, get the app from your browser menu:",
      "pwaHelpAndroidStep1": "Tap the menu button (three dots) in the top-right of Chrome.",
      "pwaHelpAndroidStep2": "Choose \"Install app\" (preferred) — or \"Add to Home screen\".",
      "pwaHelpAndroidStep3": "Confirm to add the Find-2-Day app to your phone.",
      "pwaHelpAndroidNote": "For the proper app experience use Chrome, Samsung Internet, or Edge. DuckDuckGo, Firefox Focus, and Brave with strict shields will only create a website shortcut.",
      "pwaHelpDesktopTitle": "Install the Find-2-Day app",
      "pwaHelpDesktopIntro": "Installing is fastest on a phone, but you can also install the Find-2-Day app on a computer with Chrome or Edge:",
      "pwaHelpDesktopStep1": "Look for the install icon in the address bar (a small monitor with a down-arrow) or open the browser menu.",
      "pwaHelpDesktopStep2": "Choose \"Install Find-2-Day\" — it opens in its own window like a regular app.",
      "pwaHelpDesktopNote": "Firefox and desktop Safari don't currently support installable web apps. For the full app experience, open this page on your phone.",
      "pwaHelpGotIt": "Got it",
      "pwaDismissAria": "Dismiss install banner",
      "pwaIOSInstructions": "Add the Find-2-Day app to your home screen for the full experience:",
      "pwaIOSStep1": "Tap Share",
      "pwaIOSStep2": "Add to Home",
      "pwaAndroidInstructions": "Tap the menu (three dots) and choose \"Install app\" — not \"Add to Home screen\".",

      // What's New Modal
      "whatsNew_title": "What's New",
      "whatsNew_item1": "Get instant alerts from your favourite businesses — new events, offers, and more",
      "whatsNew_item2": "Works on all devices — push notifications plus email fallback",
      "whatsNew_cta": "Get Alerts for Events & Offers",

      // Follow Engagement
      "followPromptTitle": "Get notifications for events & offers near you",
      "followPromptDesc": "Tap 'Get Alerts' on any listing page to stay updated",
      "firstFollowBoost": "You'll now get alerts when they post new events or offers",
      "followEmptyTitle": "Get notified about events & offers",
      "followEmptyDesc": "Tap 'Get Alerts' on any listing to be the first to know about their events and offers",
      "followEmptyCta": "Explore Listings",

      // Offers & Notification Controls
      "offers": "Offers",
      "createOffer": "New Offer",
      "newOffer": "New Offer",
      "offerTitle": "Offer Title",
      "offerTitlePlaceholder": "e.g. 20% off this week only",
      "offerDescription": "Description",
      "offerDescPlaceholder": "What's the offer about?",
      "discountPercent": "Discount %",
      "validUntil": "Valid Until",
      "offerTitleRequired": "Title is required",
      "offerDescRequired": "Description is required",
      "offerValidUntilRequired": "Expiry date is required",
      "offerCreatedSuccess": "Offer published successfully!",
      "offerDeletedSuccess": "Offer deleted",
      "publishOffer": "Publish Offer",
      "creatingOffer": "Publishing...",
      "loadingOffers": "Loading offers...",
      "noOffersCreated": "No offers yet — create one to attract customers",
      "delete": "Delete",
      "notifyFollowers": "Notify followers",
      "boostPost": "Boost this post",
      "comingSoon": "Coming soon",

      // Post Analytics
      "analyticsViews": "views",
      "analyticsClicks": "clicks",
      "sent": "sent",

      // Public Offers & Events pages
      "activeOffers": "active offers",
      "upcomingEvents": "upcoming events",
      "expirestoday": "Expires today",
      "daysLeft": "days left",

      // Boost
      "boosted": "Boosted",
      "featured": "Featured",
      "boostAfterCreate": "Boost after creating",
      "boostModalDesc": "Boost this post to reach more people with area-wide notifications:",
      "boostPayNow": "Pay & Boost Now",
      "boostSecurePayment": "Secure payment via Stripe",
      "redirecting": "Redirecting...",
      "boostActivating": "Activating your boost...",
      "boostActivatingDesc": "Please wait while we confirm your payment",
      "boostActive": "Boost Activated!",
      "boostActiveDesc": "Your post is now featured and sent to all users in the area",
      "boostError": "Something went wrong",
      "boostErrorDesc": "We couldn't verify your payment. Please contact support.",
      "backToDashboard": "Back to Dashboard",
      "previewBanner": "This is how your listing appears to the public",
      "draftPreviewBanner": "Preview — not saved. Go back and click Save to apply these changes.",
      "closePreview": "Close Preview",
      "previewChanges": "Preview",
      "previewUnsavedHint": "Opens a preview of your unsaved changes in a new tab",
      "previewMyListing": "Preview My Listing",
      "previewYourListing": "Preview your listing",
      "previewYourListingHint": "See exactly how your listing will look before you pay. Opens in a new tab — nothing is saved yet.",
      "waiverTitle": "Immediate listing activation and acknowledgement",
      "waiverLabel": "By ticking this box, I request that my business listing be activated immediately once approved. I understand that once my listing becomes publicly visible on Find-2-Day, the digital service is considered delivered under EU Directive 2011/83/EU Art. 16(m).",
      "waiverRequired": "Please acknowledge immediate listing activation to continue.",
      "waiverLegalNote": "EU Directive 2011/83/EU, Art. 16(m) — digital service supplied immediately on request.",
      "contactSupportBeforeChargeback": "Need help with your subscription later? Email",
      "contactSupportBeforeChargebackTail": "we resolve refund and billing issues directly, faster than a chargeback.",

      // Boost History
      "boostHistory": "Boost History",
      "viewsBefore": "Before boost",
      "viewsDuring": "During boost",
      "moreViewsDuringBoost": "more views during boost",
      "topBoost": "Top boost",
      "boostStatusActive": "Active",
      "boostStatusEnded": "Ended",
      "boostAgain": "Boost Again",
      "boostAgainStrong": "Worked well — boost again",

      "showingOf": "Showing {{from}}-{{to}} of {{total}}",
      "manageBilling": "Manage Billing",
      "manageBillingDesc": "Update payment methods, view invoices, and manage your subscription.",
      "manageSubscription": "Manage Subscription",
      "manageSubscriptionDesc": "Update payment methods, view invoices, change or cancel your plan.",

      // Reset Filters
      "resetFilters": "Reset Filters",

      // Business Guide
      "businessGuideTitle": "How It Works — Your Business Guide",
      "guideSubscriptions": "Subscriptions",
      "guideSub12mo": "Founding Business Offer — FREE for 12 months. Includes full profile, photos, contact details, events and offers.",
      "guideSubTrial": "Your listing goes live as soon as payment is confirmed — no trial, no delay.",
      "guideSubInstant": "Your listing goes live as soon as payment is confirmed — no trial, no delay.",
      "upgradeTitle": "Save by Upgrading to 12 Months",
      "upgradeDesc": "You're currently on a legacy 6-month plan. All businesses are now free for 12 months under the Founding Business Offer — upgrade in one click to roll over with no payment required during the founding period.",
      "upgradeNow": "Upgrade Now",
      "guideEventsOffers": "Events & Offers",
      "guideEventLimit": "You can have up to 5 active events at a time. Archive old events to create new ones.",
      "guideOfferLimit": "You can have up to 5 active offers at a time. Delete expired offers to add new ones.",
      "guideEventApproval": "Events and offers go through a quick admin review before they go live.",
      "guideBoosts": "Boosts & Visibility",
      "guideBoostDesc": "Boost your events or offers to appear at the top of listings and reach more people.",
      "guideBoostTiers": "Choose from different boost durations to match your budget and campaign.",
      "guideReviews": "Reviews",
      "guideReviewsDesc": "Users can leave reviews on your listing. Good reviews help attract more customers.",
      "guideBilling": "Billing & Payments",
      "guideBillingPortal": "Use \"Manage Billing\" to update your card, view invoices, or change your plan.",
      "guideBillingUpgrade": "You can switch from a legacy 6-month plan to the 12-month plan at any time through the billing portal.",

      "offlineSync": {
        "offlineTitle": "You are offline",
        "offlineDesc": "Changes will be saved and sent when you reconnect.",
        "queued": "{{count}} pending submission(s) saved",
        "syncing": "Syncing saved submissions...",
        "syncComplete": "{{count}} submission(s) synced successfully",
        "stillPending": "{{count}} still pending",
        "failedTitle_one": "1 submission failed",
        "failedTitle_other": "{{count}} submissions failed",
        "tapToRetry": "Tap to retry",
        "dismiss": "Dismiss",
        "genericItem": "Item",
        "item": {
          "event": "Event",
          "offer": "Offer",
          "businessProfile": "Business profile update",
          "review": "Review",
          "report": "Report",
          "charity": "Charity application",
          "contact": "Contact message"
        }
      },

      // Onboarding Popup
      "onboardingTitle": "Welcome to Find-2-Day!",
      "onboardingStep1Title": "Discover Local Listings",
      "onboardingStep1Desc": "Browse businesses, charities, events, and offers happening near you.",
      "onboardingStep2Title": "Save Your Favourites",
      "onboardingStep2Desc": "Tap the heart to save listings you love and find them quickly later.",
      "onboardingStep3Title": "Get Notifications for Events & Offers",
      "onboardingStep3Desc": "Tap 'Get Alerts' on any listing to receive instant updates when they post something new.",
      "onboardingGotIt": "Got it!",
      "onboardingNext": "Next",
      "onboardingSkip": "Skip",
      "locationPicker": {
        "title": "Get alerts for your area",
        "intro": "Pick your town in Almería so we can send you nearby events, offers, and safety alerts.",
        "homeTownLabel": "Your town or village",
        "searchPlaceholder": "Search Almería towns…",
        "noTowns": "No towns match your search.",
        "nearbyLabel": "Also alert me about nearby towns",
        "nearbyHint": "Within {{km}} km of your town. Adjust if you want.",
        "radiusAria": "Nearby radius in kilometres",
        "regionLabel": "Alert me about all of Almería",
        "regionHint": "Get province-wide events, large offers, and safety alerts.",
        "followedTownsLabel": "Also follow these towns (optional)",
        "followedTownsHint": "Pick extra towns alongside your home town.",
        "followedTownsAddPlaceholder": "Add a town…",
        "removeTown": "Remove",
        "skip": "Skip for now",
        "save": "Save my area",
        "saving": "Saving…"
      },
      "maxActiveEventsReached": "Maximum 5 active events. Archive existing ones first.",
      "maxActiveOffersReached": "Maximum 5 active offers. Delete existing ones first.",

      // Charity / Community Listing
      "charity": {
        "formTitle": "Community & Charity Listing Application",
        "formSubtitle": "Apply for a free community or charity listing for your organisation",
        "eligibleTitle": "This application is for:",
        "eligibleItem1": "Registered charities",
        "eligibleItem2": "Non-profit organisations",
        "eligibleItem3": "Community groups",
        "eligibleItem4": "Volunteer organisations",
        "notForBusinesses": "This is not for commercial businesses. If you run a business, please sign up through our standard business registration.",
        "orgDetailsTitle": "Organisation Details",
        "organisationName": "Organisation Name",
        "organisationNamePlaceholder": "e.g. Almeria Food Bank",
        "contactName": "Contact Name",
        "contactNamePlaceholder": "Full name of the primary contact",
        "websiteSocial": "Website / Social Media Link",
        "aboutOrgTitle": "About Your Organisation",
        "orgType": "Organisation Type",
        "selectOrgType": "Select organisation type",
        "orgTypeCharity": "Registered Charity",
        "orgTypeNonProfit": "Non-Profit Organisation",
        "orgTypeCommunity": "Community Group",
        "orgTypeVolunteer": "Volunteer Organisation",
        "descriptionPlaceholder": "Describe what your organisation does, who you help, and your mission...",
        "areaServed": "Area Served",
        "areaServedPlaceholder": "e.g. Almeria, Andalusia",
        "registrationNumber": "Charity Registration Number (if applicable)",
        "registrationNumberPlaceholder": "e.g. G12345678",
        "proofTitle": "Proof of Organisation",
        "proofDesc": "Upload a document proving your organisation is a registered charity, non-profit, or community group. This will only be visible to our admin team.",
        "proofUploadLabel": "Click to upload or drag and drop",
        "proofFileTypes": "PDF, JPG, PNG or DOC (max 10MB)",
        "proofRequired": "Please upload proof of your organisation",
        "fileTooLarge": "File is too large. Maximum size is 10MB.",
        "declarationText": "I confirm that this organisation is not-for-profit and that the information provided in this application is accurate and truthful. I understand that providing false information may result in removal from the platform.",
        "declarationRequired": "You must accept the declaration to submit",
        "submitApplication": "Submit Application",
        "submitError": "Error submitting application. Please try again.",
        "submittedTitle": "Application Submitted",
        "submittedDesc": "Thank you for applying. Our team will review your application and supporting documents. We will notify you by email once a decision has been made.",
        "communityListing": "Community & Charity Listing",
        "nonProfit": "Non-Profit",
        "charityBadge": "Community & Charity",
        "applyForListing": "Apply for a Free Community or Charity Listing",
        "charityManagement": "Community & Charities",
        "pendingApplications": "Pending",
        "approvedApplications": "Approved",
        "rejectedApplications": "Rejected",
        "needsInfoApplications": "Needs Info",
        "allApplications": "All",
        "noApplications": "No charity applications found",
        "loadingApplications": "Loading applications...",
        "approveApplication": "Approve",
        "rejectApplication": "Reject",
        "needsMoreInfo": "Needs Info",
        "viewProof": "View Proof",
        "downloadProof": "Download Proof",
        "orgTypeLabel": "Organisation Type",
        "areaServedLabel": "Area Served",
        "regNumberLabel": "Registration Number",
        "convertToCharity": "Convert to Charity",
        "convertToStandard": "Convert to Standard",
        "charityFilter": "Community & Charity",
        "filterAll": "All Listings",
        "filterCharity": "Community Only",
        "filterStandard": "Businesses Only"
      },

      // Classifieds / Sell / Lost / Found
      "marketplace": {
        "tabSell": "Sell", "tabWanted": "Wanted", "tabSwap": "Swap", "tabFree": "Free",
        "hintMarketTitle": "Buying, selling, swapping or giving away",
        "swapTitle": "SWAP-2-DAY", "swapSubtitle": "What have you got? What would you swap it for?",
        "swapLabel": "Swap", "orSwap": "OR SWAP"
      },
      "swap": {
        "moneyNone": "None", "moneyBtoA": "Offerer pays owner €{n}", "moneyAtoB": "Owner pays offerer €{n}",
        "ownerProvides": "Owner provides", "offererProvides": "Offerer provides", "money": "Additional money",
        "negotiation": "Swap negotiation", "youAre": "You are the", "owner": "listing owner", "offerer": "person making the offer",
        "agreed": "SWAP AGREED", "agreedAt": "Agreed at", "finalTerms": "Final agreed terms", "currentTerms": "Current terms on the table",
        "confirmed": "confirmed ✓", "notConfirmed": "not yet", "ownerLabel": "Owner", "offererLabel": "Offerer",
        "iAgree": "I agree to this swap", "youConfirmed": "You have confirmed — waiting for the other person",
        "counter": "Counter offer", "decline": "Decline", "withdraw": "Withdraw my offer", "cancel": "Cancel this negotiation",
        "history": "Offer history", "makeCounter": "Make a counter offer", "commaSep": "comma separated",
        "moneyAmount": "Money (€)", "moneyDir": "Money direction", "dirNone": "No money", "dirBtoA": "Offerer pays owner", "dirAtoB": "Owner pays offerer",
        "notePlaceholder": "Optional note…", "sendCounter": "Send counter offer", "backToMarket": "Back to Marketplace",
        "loadFail": "Could not load this swap.", "actionFail": "Action failed.",
        "ownerGives": "The owner gives", "describeYours": "Describe what you would give in exchange (you can add money too).",
        "itemsPlaceholder": "e.g. Lawnmower, Strimmer (comma separated)", "sendOffer": "Send swap offer", "createFail": "Could not send offer.",
        "errName": "Please enter your name.", "errEmail": "Please enter a valid email.", "errItems": "Describe at least one item you are offering."
      },
      "classifieds": {
        "listingType": "Listing type", "typeSellOnly": "Sell only", "typeSwapOnly": "Swap only", "typeSellOrSwap": "Sell or Swap", "typeFree": "Free", "typeWanted": "Wanted",
        "swapWantedTitle": "What would you like in exchange?", "swapAddOwn": "Add your own…", "openToOffers": "Open to offers",
        "swapPrefsHint": "These are preferences only — people can still offer something different.", "sellSwapPriceHint": "Buyers can buy at this price OR make a swap offer.",
        "errSellSwapPrice": "Please enter a selling price for a \"Sell or Swap\" listing",
        "buyFor": "Buy for", "swapWantedShort": "Swap wanted", "swapOpenAny": "open to ideas", "otherOffersConsidered": "Other offers considered",
        "contactToBuy": "Contact to Buy", "makeSwapOffer": "Make a Swap Offer", "statusSwapped": "Swapped!",
        "swappedNote": "Swapped listings are hidden from search. Your record is kept for you.",
        "editTypePrice": "Change listing type / price (e.g. also sell this item)", "editSwapNote": "Buyers will see “€… OR SWAP” and can make a swap offer. Your photos, description and listing ID stay the same.",
        "offersReceived": "Swap offers received", "noOffersYet": "No swap offers yet.", "errSave": "Could not save changes.",
        "title": "Sell / Lost / Found",
        "sellTab": "Sell",
        "hintCardTitle": "Where should I post this?",
        "hintCardSell": "Selling an item",
        "hintCardSellDesc": "Use the Sell tab below",
        "hintCardLostFound": "Lost or found something",
        "hintCardLostFoundDesc": "Use the Lost & Found tab",
        "hintCardPetSafety": "Lost a pet or safety concern",
        "hintCardPetSafetyDesc": "Use the Community Safety Hub instead",
        "searchPlaceholder": "Search items for sale...",
        "postItem": "Post an Item",
        "noItems": "No items found",
        "tryDifferent": "Try adjusting your search or filters",
        "beFirst": "Be the first to post something for sale!",
        "itemFound": "item",
        "itemsFound": "items",
        "negotiable": "Negotiable",
        "sold": "Sold",
        "description": "Description",
        "contactSeller": "Contact Seller",
        "expiresOn": "This listing expires on",
        "ownerActions": "You have owner access to this listing.",
        "markSold": "Mark as Sold",
        "delete": "Delete",
        "deleteListing": "Delete Listing",
        "alreadySold": "This item has been marked as sold.",
        "confirmSold": "Mark as Sold?",
        "confirmSoldDesc": "This will mark your listing as sold. Buyers will see it is no longer available.",
        "confirmDelete": "Delete Listing?",
        "confirmDeleteDesc": "This will permanently remove your listing. This action cannot be undone.",
        "yesSold": "Yes, Sold",
        "yesDelete": "Yes, Delete",
        "marking": "Marking...",
        "deleting": "Deleting...",
        "errorMarkSold": "Could not mark as sold. Check your edit link.",
        "errorDelete": "Could not delete listing.",
        "posted": "Item Posted!",
        "postedDesc": "Your listing is now live. It will automatically expire after 30 days.",
        "saveLink": "Save this link to manage your listing:",
        "viewListing": "View Listing",
        "browseMore": "Browse More",
        "photos": "Photos",
        "itemTitle": "Title",
        "titlePlaceholder": "What are you selling?",
        "category": "Category",
        "price": "Price",
        "optional": "optional",
        "priceHint": "Leave empty for \"Negotiable\"",
        "descLabel": "Description",
        "descPlaceholder": "Describe the item, condition, features...",
        "location": "Location",
        "locationPlaceholder": "e.g. Almería Centro, Retamar...",
        "contactInfo": "Your Contact Details",
        "yourName": "Name",
        "namePlaceholder": "Your name",
        "yourEmail": "Email",
        "emailPlaceholder": "your@email.com",
        "yourPhone": "Phone",
        "addPhoto": "Add",
        "maxPhotos": "Maximum 6 photos allowed",
        "errTitle": "Title must be at least 3 characters",
        "errDesc": "Description must be at least 10 characters",
        "errCategory": "Please select a category",
        "errName": "Please enter your name",
        "errEmail": "Please enter a valid email",
        "errLocation": "Please enter a location",
        "errSubmit": "Failed to post listing. Please try again.",
        "posting": "Posting...",
        "postForFree": "Post for Free",
        "autoExpire": "Listings automatically expire after 30 days",
        "cat": {
          "Tools": "Tools",
          "Furniture": "Furniture",
          "Cars & Vehicles": "Cars & Vehicles",
          "Electronics": "Electronics",
          "Property": "Property",
          "Clothing & Fashion": "Clothing & Fashion",
          "Home & Garden": "Home & Garden",
          "Sports & Leisure": "Sports & Leisure",
          "Baby & Kids": "Baby & Kids",
          "General": "General"
        },
        "statusAvailable": "Available",
        "statusPending": "Pending Collection",
        "statusSold": "Sold",
        "changeStatus": "Change Status",
        "soldAutoRemove": "Sold listings are hidden from search and auto-removed after 3 days.",
        "errorStatus": "Could not update status.",
        "emailSent": "Manage link sent to your email",
        "emailSentDesc": "Check your inbox for a link to edit, update status, or delete your listing.",
        "manageHelp": "With your manage link you can:",
        "manageEdit": "Edit your listing details",
        "managePending": "Set status to Pending Collection",
        "manageSold": "Mark as Sold (hidden after 3 days)",
        "manageDelete": "Delete the listing",
        "manageListing": "Manage Listing",
        "manageYourListing": "Manage Your Listing",
        "resendLink": "Lost your manage link?",
        "resendTitle": "Get your manage links",
        "resendDesc": "Enter the email you used when posting. We'll resend your manage links.",
        "resendEmailPlaceholder": "your@email.com",
        "resendBtn": "Send",
        "resendSent": "Check your email!",
        "resendSentDesc": "If you have any active listings, we've sent the manage links to your email.",
        "resendErrEmail": "Please enter a valid email",
        "resendError": "Something went wrong. Please try again."
      },

      // Nearest Me
      "nearestMe": {
        "button": "Nearest Me",
        "notSupported": "Geolocation is not supported by your browser",
        "denied": "Location access denied. You can search by town instead.",
        "error": "Could not get your location. Try again.",
        "townPlaceholder": "Enter a town, e.g. Lyon, France",
        "useTown": "Use town",
        "townNotFound": "Could not find that town. Try a nearby larger town or add the country."
      },

      // Lost & Found
      "lostFound": {
        "title": "Lost & Found",
        "searchPlaceholder": "Search lost & found items...",
        "postItem": "Post Lost or Found Item",
        "noItems": "No items found",
        "beFirst": "Post a lost or found item to help your community.",
        "lost": "Lost",
        "found": "Found",
        "lostOn": "Lost",
        "foundOn": "Found",
        "resolved": "Resolved",
        "resolvedDesc": "This item has been marked as resolved.",
        "markResolved": "Mark as Resolved",
        "errorResolve": "Could not mark as resolved.",
        "contactPerson": "Contact",
        "messageOnWhatsApp": "Message on WhatsApp",
        "whatsappMessage": "Hi, I saw your Lost & Found post on Find-2-Day about {{title}}. Is it still available / can I collect it?",
        "typeLabel": "Is this item lost or found?",
        "iLostSomething": "I Lost Something",
        "iFoundSomething": "I Found Something",
        "itemTitle": "What was lost/found?",
        "titlePlaceholder": "e.g. Small brown dog, car keys with red tag...",
        "category": "Category",
        "dateLost": "Date lost",
        "dateFound": "Date found",
        "descPlaceholder": "Describe the item, where it was lost/found, any identifying features...",
        "locationLabel": "Area / Location",
        "locationPlaceholder": "e.g. Almeria Centro, Retamar beach...",
        "posted": "Item Posted!",
        "postedDesc": "Your post is now live. It will expire after 30 days.",
        "viewItem": "View Item",
        "browseMore": "Browse More",
        "postForFree": "Post for Free",
        "errTitle": "Title must be at least 3 characters",
        "errDesc": "Description must be at least 10 characters",
        "errCategory": "Please select a category",
        "errName": "Please enter your name",
        "errEmail": "Please enter a valid email",
        "errLocation": "Please enter a location",
        "errSubmit": "Failed to post. Please try again.",
        "cat": {
          "Pets": "Pets",
          "Keys": "Keys",
          "Wallets/Bags": "Wallets/Bags",
          "Documents/ID": "Documents/ID",
          "Phones/Electronics": "Phones/Electronics",
          "Other": "Other"
        }
      },

      // Saved Search Alerts
      "savedSearch": {
        "setAlert": "Set a search alert",
        "title": "Search Alert",
        "desc": "Get notified when new items matching your search are listed.",
        "queryPlaceholder": "e.g. sofa, bicycle, iPhone...",
        "anyCategory": "Any category",
        "emailPlaceholder": "Your email for notifications",
        "save": "Save Alert",
        "saving": "Saving...",
        "saved": "Alert saved!",
        "savedDesc": "We'll email you when new items matching your search are listed.",
        "errQuery": "Search term must be at least 2 characters",
        "errEmail": "Please enter a valid email",
        "errSave": "Failed to save search alert",
        "unsubscribed": "Unsubscribed",
        "unsubscribedDesc": "You will no longer receive notifications for this search.",
        "unsubscribeError": "Could not unsubscribe",
        "unsubscribeErrorDesc": "This link may have expired or already been used.",
        "backToClassifieds": "Browse Sell / Lost / Found"
      },

      // Emergency numbers
      "emergency": {
        "title": "Emergency Numbers",
        "subtitle": "Almería region — tap any number to call",
        "disclaimerTitle": "In a life-threatening emergency:",
        "disclaimerBody": "This page is informational and may not reflect the latest official contact changes.",
        "callNow": "call 112 now",
        "tapToCall": "Tap to call",
        "footerNote": "Emergency numbers last reviewed February 2026. Always verify critical information with official sources.",
        "euEmergency": "Emergency (EU)",
        "euEmergencyDesc": "All-in-one number for police, ambulance and fire across the EU",
        "nationalPolice": "Policía Nacional",
        "nationalPoliceDesc": "National police — serious crime, identity documents",
        "guardiaCivil": "Guardia Civil",
        "guardiaCivilDesc": "Rural areas, roads and borders",
        "localPolice": "Policía Local",
        "localPoliceDesc": "Local incidents, parking, municipal issues",
        "medical": "Medical Emergency",
        "medicalDesc": "Ambulance and urgent health services",
        "fire": "Fire Brigade",
        "fireDesc": "Bomberos — fires, rescues, floods",
        "dvSupport": "Domestic Violence Support",
        "dvSupportDesc": "24/7 confidential helpline (free, does not appear on phone bills)",
        "torrecardenas": "Hospital Torrecárdenas",
        "torrecardenasDesc": "Main public hospital in Almería city",
        "ukConsulate": "UK Consulate (Málaga)",
        "ukConsulateDesc": "Support for British citizens in southern Spain",
        "ieConsulate": "Irish Embassy (Madrid)",
        "ieConsulateDesc": "Support for Irish citizens — covers all of Spain",
        "cat": {
          "primary": "Emergency",
          "police": "Police",
          "medical": "Medical",
          "fire": "Fire",
          "support": "Support",
          "consulate": "Consulate"
        }
      },

      // Holidays (National / Andalucía / Local municipality)
      "holidays": {
        "title": "Holidays",
        "subtitle": "Spain & Andalucía — 2026",
        "nextHoliday": "Next holiday",
        "today": "Today",
        "tomorrow": "Tomorrow",
        "inNDays": "in {{count}} days",
        "footerNote": "Local holidays are municipality-specific. Verify with your local council for certainty.",
        "scope": { "national": "National", "andalusia": "Andalucía", "almeria": "Almería", "local": "Local" },
        "localHoliday": "Local Holiday",
        "allTowns": "All municipalities (local holidays)",
        "local": { "generic": "Local Holiday" },
        "month": {
          "january": "January", "february": "February", "march": "March", "april": "April",
          "may": "May", "june": "June", "july": "July", "august": "August",
          "september": "September", "october": "October", "november": "November", "december": "December"
        },
        "newYear": "New Year's Day",
        "epiphany": "Epiphany (Three Kings)",
        "andalusiaDay": "Day of Andalucía",
        "maundyThursday": "Maundy Thursday",
        "goodFriday": "Good Friday",
        "labourDay": "Labour Day",
        "sanJuan": "San Juan",
        "assumption": "Assumption of Mary",
        "virgenDelMar": "Virgen del Mar",
        "nationalDay": "Hispanic Day (National Day)",
        "allSaints": "All Saints' Day (observed)",
        "constitutionDay": "Constitution Day (observed)",
        "immaculate": "Immaculate Conception",
        "christmas": "Christmas Day"
      },

      // Add to calendar
      "calendar": {
        "addToCalendar": "Add to Calendar",
        "icsDownload": "Apple / Outlook (.ics)",
        "icsHint": "Universal calendar file",
        "googleCalendar": "Google Calendar",
        "googleHint": "Opens in a new tab"
      },

      // Local Safety Bulletin (Almería pilot)
      "safety": {
        "title": "Community Safety Hub",
        "subtitle": "Official emergency updates, community reports and local help during incidents.",
        "disclaimer": "Not a police service. In an emergency, call 112. Posts publish immediately. Admin may edit or remove alerts that break safety rules. We never accept names, accusations, license plates or private information.",
        "filterTown": "Filter by town",
        "filterCategory": "Category",
        "allTowns": "All towns",
        "allCategories": "All categories",
        "category": {
          "incident_factual": "Factual Incident",
          "safety_tip": "Safety Tip",
          "lost_pet": "Lost Pet",
          "community_alert": "Community Alert (24h)"
        },
        "categoryHint": {
          "incident_factual": "A short, factual note about something that has already happened.",
          "safety_tip": "Help neighbours stay safe with useful advice.",
          "lost_pet": "Help reunite a missing pet with its owner.",
          "community_alert": "A short-term warning that will disappear automatically after 24 hours."
        },
        "categoryNote": {
          "incident_factual": "No names. No accusations."
        },
        "categoryEmoji": {
          "safety_tip": "🟧",
          "incident_factual": "🟥",
          "lost_pet": "🟩",
          "community_alert": "🟪"
        },
        "categoryExamples": {
          "safety_tip": [
            "\"Lock car doors at night — recent reports of cars being checked.\"",
            "\"Be careful on the main road — loose gravel causing slips.\""
          ],
          "incident_factual": [
            "\"Person seen trying car handles near Calle X around 2am.\"",
            "\"Package taken from outside a property this morning.\""
          ],
          "lost_pet": [
            "\"Small brown dog missing near Arboleas — last seen near the park.\"",
            "\"Black cat not returned home since yesterday evening.\""
          ],
          "community_alert": [
            "\"Road closed today near Albox due to works.\"",
            "\"Water outage expected this afternoon in the area.\""
          ]
        },
        "examplesLabel": "Examples:",
        "empty": "No safety alerts yet for this filter.",
        "emptyTitle": "Nothing reported yet in your area — that's a good sign",
        "emptyBody": "Be the first to share something helpful for your community.",
        "submitCta": "Submit Safety Alert",
        "myPosts": "My submissions",
        "verifiedResident": "Verified resident of {{town}}",
        "expiresIn": "Visible for {{value}}",
        "publishedOn": "Published {{date}}",
        "report": {
          "button": "Report",
          "title": "Report this alert",
          "placeholder": "Why is this alert a problem?",
          "submit": "Send report",
          "thanks": "Thanks — we'll review it.",
          "alreadyReported": "You already reported this alert."
        },
        "submit": {
          "title": "Submit a Safety Alert",
          "intro": "Share a short, factual safety alert with neighbours in your town. Posts publish immediately. Admin may edit or remove alerts that break safety rules.",
          "town": "Town (Almería province only)",
          "townPlaceholder": "Select your town",
          "categoryLabel": "What kind of post is this?",
          "titleLabel": "Title",
          "titlePlaceholder": "Short summary (max 80 characters)",
          "bodyLabel": "Details",
          "bodyPlaceholder": "Plain text only. No links, no names, no accusations, no plates, no private info. Max 2000 characters.",
          "rules": "By submitting, I confirm:",
          "rulesNoNames": "I have not named any person.",
          "rulesNoAccusations": "I am not accusing anyone of a crime.",
          "rulesNoPlates": "I have not included a license plate.",
          "rulesNoPrivate": "I have not included an address, phone number, or other private info.",
          "confirmCheckbox": "I confirm: no names, no accusations, no plates, no private info.",
          "submitButton": "Submit Safety Alert",
          "needTown": "Select a town",
          "needTitle": "Add a title (at least 4 characters)",
          "needBody": "Add details (at least 10 characters)",
          "needConfirm": "Tick the confirmation box",
          "submitting": "Submitting…",
          "successTitle": "Safety Alert Published",
          "successBody": "Your safety alert is live and visible to neighbours in your town.",
          "characters": "{{count}} characters",
          "loginRequired": "Please sign in to submit an alert.",
          "signIn": "Sign in",
          "rateLimitHit": "Rate limit reached — you can submit up to 3 safety alerts per 24 hours.",
          "categoryFooter": "Keep posts short, factual and helpful — no names, accusations or private information."
        },
        "mine": {
          "empty": "You haven't submitted any safety alerts yet.",
          "status": {
            "pending": "Pending review",
            "approved": "Published",
            "rejected": "Not published",
            "expired": "Expired",
            "takedown": "Removed",
            "withdrawn": "Withdrawn"
          },
          "rejectionReason": "Reason: {{reason}}",
          "back": "Back to alert",
          "withdraw": "Withdraw",
          "withdrawHeading": "Withdraw alert",
          "withdrawNote": "This removes your alert from all public feeds and clears related notifications for everyone. It stays in your history as Withdrawn.",
          "withdrawConfirm": "Withdraw alert",
          "withdrawSuccess": "Alert withdrawn — it is no longer visible to the public.",
          "withdrawFailed": "Could not withdraw the alert.",
          "markFound": "Mark as Found",
          "markFoundSuccess": "Marked as found — thanks for letting us know!",
          "markFoundFailed": "Could not mark as found.",
          "closedFound": "Closed — pet found",
          "edit": "Edit",
          "editHeading": "Edit alert",
          "editTown": "Town",
          "editCategory": "Category",
          "editTitleField": "Title",
          "editBody": "Details",
          "editPendingNote": "Editing a published alert sends it back for review before it goes live again.",
          "editCancel": "Cancel",
          "editSave": "Save changes",
          "editSuccessPending": "Saved — your alert is back in review and will go live once approved.",
          "editSuccessLive": "Your alert has been updated.",
          "editFailed": "Could not save changes. Please try again.",
          "editLiveNote": "Your edit stays live immediately — followers are not notified unless you choose to below.",
          "editNotifyFollowers": "Notify followers of this update",
          "editSuccessNotified": "Saved — your followers have been notified of the update."
        },
        "follow": {
          "banner": "Follow your town to receive local safety alerts.",
          "bannerSub": "Pick one or more towns — we'll ping you when something new is posted.",
          "bannerCta": "Choose my towns",
          "followYourTown": "Follow Your Town",
          "followingYouAre": "You're following: {{towns}}",
          "followingHelp": "You'll get a notification each time a safety alert is posted for these towns.",
          "onboardTitle": "Pick the towns you want safety alerts for",
          "onboardBody": "Choose one or more towns in your area. We'll notify you the moment a neighbour posts a safety alert there.",
          "onboardSkip": "Skip for now",
          "onboardChoose": "Choose my towns",
          "pickerTitle": "My safety alerts",
          "pickerSubtitle": "Pick one or more towns. You'll be notified when a safety alert is posted there.",
          "searchPlaceholder": "Search towns…",
          "noResults": "No towns match your search.",
          "followingCount_one": "Following {{count}} town",
          "followingCount_other": "Following {{count}} towns",
          "followingCount": "Following {{count}} towns",
          "followingBadge": "Following",
          "doneBtn": "Done",
          "channelsNote": "Alerts come through push and your in-app inbox. Email stays off unless you turn it on in notification settings.",
          "followingSummary_one": "You'll get alerts for: {{towns}}",
          "followingSummary_other": "You'll get alerts for: {{towns}}",
          "followingSummary": "You'll get alerts for: {{towns}}",
          "manage": "Manage",
          "afterSubmitTitle": "Want alerts next time something is posted in {{town}}?",
          "afterSubmitBody": "We'll ping you in the app when another safety alert is published.",
          "followTown": "Follow {{town}}",
          "pickMore": "Pick more towns",
          "nowFollowing": "You'll get alerts for {{town}}",
          "failed": "Could not follow this town. Try again.",
          "alreadyFollowing": "You're already following {{town}} — we'll keep you posted.",
          "settingsTitle": "Towns for safety alerts",
          "settingsDesc": "Get notified when a safety alert is posted in the towns you follow.",
          "noneYet": "You haven't followed any towns yet.",
          "pickTowns": "Pick towns to follow",
          "manageTowns": "Manage towns"
        }
      },

      // Community Tools card on Discover
      "community": {
        "title": "Community Tools",
        "safetySub": "Verified neighbours, factual notes",
        "emergencySub": "Police, medical, 112",
        "holidaysSub": "Spain & Andalucía 2026"
      },

      // Added Feb 2026 — user-facing i18n keys for auth, inbox, data deletion
      "common": {
        "back": "Back",
        "loading": "Loading…",
        "optional": "optional"
      },
      "auth": {
        "pwStrongPlaceholder": "Create a strong password",
        "pwReenter": "Re-enter password",
        "pwReenterFull": "Re-enter your password",
        "pwMin6": "At least 6 characters",
        "pwConfirm": "Confirm Password"
      },
      "businessAuth": {
        "loginTitle": "Business Login",
        "loginSubtitle": "Sign in to manage your business listing",
        "signIn": "Sign In",
        "signingIn": "Signing in…",
        "noAccount": "Don't have an account?",
        "registerHere": "Register here",
        "backHome": "Back to Home",
        "errInvalidType": "Invalid account type. Please use business account.",
        "errLoginFailed": "Login failed. Please try again.",
        "registerTitle": "Register Your Business",
        "step1": "Step 1: Create your account",
        "step2": "Step 2: Business details",
        "phName": "Your Business Name",
        "phCategory": "Select category",
        "phAddress": "Street address"
      },
      "helperAuth": {
        "loginTitle": "Helper Login",
        "loginSubtitle": "Sign in to view your referrals and commission"
      },
      "inbox": {
        "title": "Notifications",
        "description": "Your alerts: businesses you follow, towns you subscribed to, and sponsored deals if you enabled them.",
        "typeEvent": "Event",
        "typeOffer": "Offer",
        "typeSystem": "System",
        "typeSafety": "Safety",
        "justNow": "just now",
        "minsAgo": "{{n}}m ago",
        "hoursAgo": "{{n}}h ago",
        "daysAgo": "{{n}}d ago",
        "unreadCount": "{{n}} unread",
        "sponsoredCount": "{{n}} sponsored",
        "settingsTitle": "Notification settings",
        "markAllRead": "Mark all as read",
        "errLoad": "Could not load notifications",
        "errMarkAll": "Failed to mark all as read",
        "emptyEnableTitle": "Enable notifications",
        "emptyEnableBody": "Turn on notifications from a business page or in Settings to start receiving alerts here.",
        "openSettings": "Open Settings",
        "emptyTitle": "No alerts yet",
        "emptyBody": "Follow businesses or towns to start receiving updates.",
        "sponsored": "Sponsored",
        "sponsoredTitle": "This is a promoted offer",
        "unread": "Unread",
        "fallbackTitle": "Notification"
      },
      "dataDeletion": {
        "title": "Delete your account",
        "subtitle": "This action is permanent and cannot be undone.",
        "doneTitle": "Account deleted",
        "doneBody": "Your account has been permanently removed. Public reviews have been anonymised. You can sign up again any time with a fresh account.",
        "doneSubCancelled": "Your active subscription was cancelled.",
        "doneUnclaimed": "Your business listing has been returned to an unclaimed state so the community can still find it.",
        "backHome": "Back to home",
        "signInRequired": "Sign in required",
        "signInBody": "You need to be signed in to delete your account.",
        "goSignIn": "Go to sign in",
        "deletedHeading": "Permanently deleted",
        "deletedItem1": "Your account, email and password",
        "deletedItem2": "Saved favourites and notification inbox",
        "deletedItem3": "Notification subscriptions and device links",
        "deletedItem4": "Any pending reports or claim requests you made",
        "anonHeading": "Anonymised (kept publicly, your name removed)",
        "anonItem1": "Reviews you've written — shown as 'Deleted user' on the business page",
        "anonItem2": "Moderation/audit records that have already been actioned",
        "bizHeading": "If you own a business listing",
        "bizItem1": "Your subscription will be cancelled immediately via Stripe",
        "bizItem2": "The listing will be returned to an unclaimed state (so locals can still find it)",
        "bizItem3": "You can claim it again later with a new account if you come back",
        "legalHeading": "Retained for legal/tax reasons",
        "legalItem1": "Stripe invoices (held by Stripe — Spanish tax law requires 6 years retention)",
        "legalItem2": "Anonymised deletion audit record (kept 2 years to prove compliance)",
        "reasonLabel": "Reason for leaving",
        "reasonPlaceholder": "Help us improve — what prompted you to leave?",
        "typeDeleteLead": "Type",
        "typeDeleteTrail": "to confirm",
        "deleting": "Deleting…",
        "deletePermanent": "Delete my account permanently",
        "cancelKeep": "Cancel, keep my account",
        "errDefault": "Could not delete account. Please try again or contact support."
      },
      // 60-day hybrid free trial (12-month plan only)
      "trial": {
        "signupTitle": "Start with a free 60-day trial — no card needed",
        "signupBody": "Try Find-2-Day free for 60 days. Add a payment method any time before day 60 to keep going. If you don't add one, your events and offers pause automatically — your listing stays visible.",
        "signupCta": "Start 60-day free trial — no card needed",
        "addPaymentMethod": "Add payment method",
        "lockInVisibility": "Lock in your visibility",
        "stayLive": "Stay live — add payment method",
        "goLiveAgain": "Go live again",
        "calmTitleV2": "You're live on Find-2-Day right now",
        "calmBodyV2": "People can find you in Discover, your events show up in search, and your offers are reaching customers. {{days}} days left in your trial — add a payment method any time and you'll never see a service interruption.",
        "amberTitleV2": "{{days}} days left — keep your visibility",
        "urgentTitleV2": "You'll vanish from Discover in {{days}} days",
        "endingTomorrowTitleV2": "Tomorrow your events disappear from search",
        "endingTodayTitleV2": "Your last day live — events go dark tomorrow",
        "urgentBodyV2": "Locals are searching today and you're showing up. Add a payment method now and you stay live with no break in visibility — your events, offers and listing all keep working as if nothing changed.",
        "cardAddedBody": "Card added — you'll be billed automatically when the trial ends in {{days}} day(s). No further action needed.",
        "pausedTitleV2": "Your events and offers are hidden right now",
        "pausedBodyV2": "Customers are searching today and they can't see your events or offers. Add a payment method to go live again in seconds — your listing, photos, and reviews are all preserved.",
        "feedbackOpenLink": "Not now — tell us why",
        "feedbackPrompt": "Mind sharing why? It helps us improve.",
        "feedbackThanks": "Thanks — we appreciate it.",
        "feedbackError": "Could not send feedback right now.",
        "feedbackSend": "Send",
        "feedbackNotePlaceholder": "Anything you want to add (optional)",
        "reasonNotReady": "Not ready yet",
        "reasonPriceTooHigh": "Price too high",
        "reasonPreferMonthly": "Prefer to pay monthly",
        "reasonOther": "Other",
        "paymentLabel": "Free trial",
        "pausedLabel": "Paused"
      },
      "foundingBadge": {
        "label": "Founding Business",
        "tooltip": "One of the founding businesses on Find-2-Day"
      },
      "foundersPricingNote": "Your business listing, events and offers are included free during the Founding Business launch period. Optional boosts are available if you want extra visibility across the Almería region.",
      "seededNotice": {
        "body": "This listing was created using publicly available business information to help local users discover businesses in the area. Business owners can claim, edit, or request removal of the listing at any time.",
        "disclaimer": "Information shown may come from publicly available sources.",
        "claimCta": "Claim this listing",
        "removalCta": "Request edit or removal"
      },
      "seededListings": {
        "gridNotice": "Some listings are created using publicly available business information to help local users discover businesses in the area. Business owners can claim, edit or request removal at any time."
      },
      "removalRequest": {
        "title": "Request edit or removal",
        "typeRemoval": "Remove listing",
        "typeEdit": "Suggest edit",
        "namePh": "Your name",
        "emailPh": "Your email",
        "roleOwner": "I am the business owner",
        "roleEmployee": "I work for this business",
        "roleOther": "Other",
        "reasonPh": "Tell us why (min 10 characters)",
        "submit": "Submit request",
        "submitting": "Sending…",
        "successTitle": "Thank you — we have received your request.",
        "successBody": "Our team will review and reply to your email within 5 working days.",
        "error": "Could not submit — please try again later.",
        "privacyNote": "We use your email only to follow up on this request. See our Privacy Policy."
      },
      "frontpage": {
        "foundersHeadline": "Founding Business Offer — 12 Months Free",
        "foundersSubline": "List your business, events and offers with no upfront payment.",
        "differentiator": "Customers get updates directly to their phones — no missed posts on social media.",
        "browseAndFollow": "Click here to start",
        "browseAndFollowSub": "Explore events, offers & local businesses near you.",
        "businessOptionsHeading": "Business Options",
        "alreadyHaveAccount": "Already have an account? Sign in",
        "businessSignIn": "Already have a business account? Sign in here",
        "claimExistingBusiness": "Already listed and want to claim your business? Click here",
        "charityApply": "Apply for a Free Community or Charity Listing",
        "discoverTagline": "Discover local events, offers & updates across Almería",
        "trust": {
          "noUpfront": "No upfront payment",
          "publishContent": "Publish events & offers",
          "reachCustomers": "Reach local customers across Almería"
        },
        "foundersCount_one": "{{count}} founding business already on board across Almería",
        "foundersCount_other": "{{count}} founding businesses already on board across Almería",
        "claimExistingBusinessTitle": "Claim or Add a Business",
        "charityApplyTitle": "Community / Charity Listing"
      },
      "foundersChecklist": {
        "title": "Your first-week success checklist",
        "titleComplete": "You're all set up — nicely done!",
        "subtitle": "Quick wins that help local customers find you.",
        "subtitleComplete": "Your business is ready to attract customers.",
        "completed": "completed",
        "bonus": "Bonus",
        "dismiss": "Hide checklist",
        "celebration": "Great work — you have unlocked the most effective parts of your listing. Customers in your region will start noticing you.",
        "items": {
          "profile_complete": {
            "label": "Complete your business profile",
            "hint": "Description, phone and address — the basics customers expect."
          },
          "photos_3plus": {
            "label": "Upload at least 3 photos",
            "hint": "Listings with photos get many more views."
          },
          "first_event": {
            "label": "Publish your first event",
            "hint": "Something happening this week? Post it."
          },
          "first_offer": {
            "label": "Publish your first offer",
            "hint": "A simple discount or special draws first-time customers."
          },
          "first_follower": {
            "label": "Gain your first follower",
            "hint": "Share your listing link with a happy customer."
          },
          "opening_hours": {
            "label": "Add opening hours",
            "hint": "Help locals know when to drop by."
          },
          "amenities": {
            "label": "Add amenities or services",
            "hint": "Group sizes, dietary options or accessibility info."
          },
          "saved_card": {
            "label": "Save a card for one-click boosts",
            "hint": "Optional — only used if you decide to boost a post later."
          }
        }
      },
      "renewal": {
        "title": {
          "30d": "Your Founding year ends in 30 days",
          "14d": "Your Founding year ends in 2 weeks",
          "7d": "Your Founding year ends in 1 week"
        },
        "body": "Keep your events, offers, followers and direct customer notifications active without interruption.",
        "ctaContinue": "Continue Full Access",
        "ctaSaveCard": "Save payment method",
        "opening": "Opening…",
        "dismiss": "Dismiss"
      }
    }
  },
  es: {
    translation: {
      "partnersStep3": "Cuando un negocio que has traído paga — su primera suscripción, una renovación o un impulso — ganas el 50% de ese pago (sin IVA). Aparece automáticamente en tu Panel de Socio, cada vez que pagan.",
      "partnersLede": "¿Eres Embajador de tu pueblo, grupo comunitario o líder local? Presenta negocios cercanos a Find-2-Day: ganas el 50% de cada pago que hagan (sin IVA), mientras sigan con nosotros: su primera suscripción, cada renovación y cada impulso o publicación patrocinada.",
      "partnersCommissionExplainer": "Ganas el 50% de todo lo que paga un negocio que has traído: su primera suscripción, cada renovación mensual o anual y cada impulso o publicación patrocinada. Los precios incluyen el 21% de IVA; tu comisión se calcula sobre el importe sin IVA.",
      "partnersRenewalHeading": "Suscripciones y renovaciones",
      "partnersRenewalDesc": "50% del primer pago y de cada renovación que haga un negocio referido, mientras siga suscrito.",
      "partnersPoint2": "En el momento en que pagan una suscripción, una renovación, un impulso o una publicación patrocinada, recibes el 50% de lo que pagaron (sin IVA).",
      "partnersPoint3": "Cada negocio que traes queda vinculado a ti para siempre. Mientras sigan pagando, sigues ganando el 50%, de por vida.",
      "affEarnTitle": "Lo que ganas por plan",
      "affEarnIntro": "Los precios incluyen el 21% de IVA. Tu comisión del 50% se calcula sobre el importe sin IVA. Las suscripciones válidas te pagan de por vida: el primer pago y cada renovación.",
      "affEarnColPlan": "Plan",
      "affEarnColPrice": "Precio (IVA incl.)",
      "affEarnColCommission": "Tu comisión (por pago)",
      "affEarnPerMonth": "/mes",
      "affEarnPerYear": "/año",
      "affEarnPlanBasic": "Basic",
      "affEarnPlanPlus": "Business Plus",
      "affEarnLifetimeNote": "Ganas la comisión anterior en su primer pago y en cada renovación, mientras sigan suscritos.",
      "affEarnBoostNote": "Impulsos y publicaciones patrocinadas: también ganas el 50% de cada impulso o publicación patrocinada que compre un negocio referido.",
      "verifyEmailWarn": "Verifica tu correo electrónico para habilitar los pagos de comisiones.",
      "resendVerification": "Reenviar correo",
      "sending": "Enviando…",
      "verifyResent": "Correo de verificación enviado — revisa tu bandeja de entrada.",
      "payoutHoldWarn": "Tus datos bancarios cambiaron recientemente, por lo que los pagos están en espera por seguridad.",
      "clawbackWarn": "Un saldo pendiente de €{{amt}} por un reembolso/contracargo se deducirá de futuras comisiones.",
      "downloadEarnings": "Extracto de ganancias (CSV)",
      "downloadPayouts": "Extracto de pagos (CSV)",
      "partnersPoint1": "Los negocios pueden unirse a Find-2-Day con una ficha básica de directorio GRATIS permanente. Hay suscripciones de pago y servicios promocionales opcionales. Los negocios fundadores existentes conservan sus 12 meses gratis prometidos originalmente.",
      "partnersPoint4": "Las comisiones se retienen durante 30 días. Si un pago se reembolsa o se disputa con éxito, su comisión puede revertirse. Las comisiones ya pagadas pueden deducirse de ganancias futuras.",
      "howPayoutsRule6": "Si un pago se reembolsa o se disputa con éxito, su comisión puede revertirse, incluidas las comisiones ya pagadas, que pueden deducirse de ganancias futuras.",
      "lifetimeRenewalEarningsHint": "Empezarás a ganar esto cuando tus negocios referidos renueven. No tienes que hacer nada más: lo registramos por ti.",
      "renewalCommissionTooltip": "Comisión ganada porque este negocio renovó su suscripción",
      "communityPartner": {
        "title": "Hazte Socio Comunitario",
        "label": "Hazte Socio Comunitario",
        "tagline": "Gana el ",
        "taglineRest": " de comisión de por vida sobre todo lo que paguen los negocios que refieras (sin IVA).",
        "cta": "Más información"
      },
      "membership": {
        "chooseTitle": "Elige tu membresía",
        "chooseSubtitle": "Empieza gratis o desbloquea tu perfil completo. Precios con IVA incluido.",
        "freeForever": "Gratis para siempre · sin tarjeta",
        "chooseFree": "Elegir Directorio Gratis",
        "month": "mes",
        "year": "año",
        "trial60": "Primeros 60 días gratis",
        "ivaIncluded": "IVA incluido",
        "chooseBasic": "Elegir Básico",
        "plusName": "Business Plus",
        "orAnnual": "o",
        "choosePlusMonthly": "Mensual",
        "choosePlusAnnual": "Anual",
        "consentNote": "Los planes de pago empiezan con una prueba gratuita de 60 días. No se te cobrará hasta que termine la prueba, y solo si has dado tu consentimiento. Cancela cuando quieras.",
        "freeBadge": "Directorio Gratis",
        "freeTitle": "Listado gratis — €0 para siempre",
        "freeBody": "Tu listado básico (nombre del negocio, categoría y población) se publica tras la aprobación. Sin tarjeta. Puedes mejorar a Básico o Business Plus cuando quieras para mostrar todos tus datos.",
        "paidBody": "Tu listado se publica tras la aprobación. NO se te cobrará durante los primeros 60 días. Cuando termine la prueba gratuita, se cobrará automáticamente en tu tarjeta solo si diste tu consentimiento, y puedes cancelar en cualquier momento antes.",
        "firstChargeOn": "Primer cobro (si continúas):",
        "checkoutNotReady": "El pago seguro con tarjeta para los planes de pago se activará en el siguiente paso (modo de prueba de Stripe). Tus datos de registro están completos y listos; no se ha realizado ningún cargo.",
        "freeCta": "Crear mi listado gratis",
        "paidCta": "Continuar — empezar mis 60 días gratis",
        "freeName": "Directorio Gratis",
        "basicName": "Básico",
        "feat": {
          "free": ["Nombre del negocio", "Categoría del negocio", "Población", "Visibilidad básica en el directorio"],
          "basic": ["Primeros 60 días gratis", "Descripción completa del negocio", "Teléfono y datos de contacto", "Web y redes sociales", "Fotos del negocio", "Dirección, mapa e indicaciones", "Visibilidad estándar en búsquedas"],
          "plus": ["Primeros 60 días gratis", "Todo lo de Básico", "Insignia Business Plus", "Promociona eventos y ofertas especiales"]
        }
      },
      "listShare": {
        "today": "Compra de Hoy",
        "todayHint": "Tu compra actual · compara precios",
        "myLists": "Mis Listas",
        "noLists": "Crea listas para lo que quieras: comida, bricolaje, leña, mascotas, jardín…",
        "newList": "Nueva Lista",
        "namePh": "Nombre de la lista (p. ej. Compra Semanal, Bricolaje, Leña)",
        "nameRequired": "Ponle un nombre a la lista",
        "kind_personal": "Personal",
        "kind_calendar": "Hogar",
        "kind_direct": "Compartida",
        "hintPersonal": "Privada para ti.",
        "hintCalendar": "Compartida con todos los de ese calendario.",
        "hintDirect": "Comparte con personas concretas por correo.",
        "pickCalendar": "Elige un calendario…",
        "create": "Crear",
        "created": "Lista creada",
        "rename": "Renombrar",
        "delListConfirm": "¿Eliminar esta lista? Se borrarán sus artículos.",
        "share": "Compartir con alguien",
        "shared": "Lista compartida",
        "invite": "Invitar",
        "emailPh": "Correo del usuario de Find-2-Day",
        "editingShared": "Estás trabajando en una lista compartida",
        "shareHelp": "Las personas que invites pueden ver y editar esta lista, y pueden ver los nombres y correos de las demás personas que la comparten.",
        "remove": "Quitar",
        "addToWhich": "Añadir a…",
        "addedToList": "Se añadió {{n}} a {{list}}",
        "addToListN": "Añadir a la lista ({{n}})",
        "copySelectedToList": "Copiar a la lista ({{n}})",
        "listEmpty": "Aún no hay productos. Añade lo que compres.",
        "select": "Seleccionar",
        "compareThis": "Comparar Esta Lista",
        "addAll": "Añadir toda la lista",
        "addSelected": "Añadir seleccionados ({{n}})",
        "addSelectedShort": "Añadir seleccionados",
        "addWholeShop": "Añadir toda la compra",
        "pickItems": "Marca los productos que necesitas",
        "copiedToday": "Se añadieron {{n}} a la Compra de Hoy",
        "itemsN": "{{n}} artículos",
        "repeatShop": "Repetir Última Compra",
        "repeatHint": "Añade productos de tu último ticket",
        "repeatEmpty": "No se encontró una compra anterior. Escanea un ticket primero.",
        "favourite": "Favorito",
        "favAdded": "Añadido a Favoritos",
        "favRemoved": "Eliminado de Favoritos"
      },
      "priceEdit": {
        "title": "Detalles del precio", "edit": "Editar precio", "delete": "Eliminar", "save": "Guardar cambios", "cancel": "Cancelar",
        "confirmDelete": "¿Eliminar este precio permanentemente? No se puede deshacer.", "confirmYes": "Sí, eliminar",
        "productName": "Nombre del producto", "price": "Precio", "brand": "Marca", "packSize": "Formato / tamaño", "quantity": "Cantidad",
        "shop": "Proveedor / tienda", "town": "Pueblo / ubicación", "date": "Fecha registrada", "notes": "Notas",
        "notesPh": "p. ej. en oferta, precio con tarjeta, pack grande…", "optional": "opcional",
        "saved": "Precio actualizado", "deleted": "Precio eliminado",
        "errName": "Introduce un nombre de producto", "errPrice": "Introduce un precio válido",
        "errSave": "No se pudo actualizar el precio", "errDelete": "No se pudo eliminar el precio"
      },
      "priceHistory": {
        "title": "Historial de precios", "none": "Aún no hay historial de precios",
        "noneHint": "Cuando haya más precios registrados de este producto en esta tienda, se mostrará la tendencia aquí.",
        "latest": "ÚLTIMO", "rose": "El precio subió", "fell": "El precio bajó", "flat": "Más o menos igual"
      },
      "pricePromo": {
        "title": "Promociones Price-2-Day",
        "subtitle": "Promociona una oferta de producto a compradores cercanos",
        "slots": "Espacios",
        "suspendedNote": "Tu negocio está suspendido de las promociones. Contacta con soporte.",
        "new": "Nueva promoción",
        "embedTitle": "Mis promociones",
        "promoteItem": "Promocionar un producto",
        "boostAgain": "Impulsar de nuevo",
        "noSlots": "No quedan espacios de promoción. Archiva una activa para liberar un espacio.",
        "editTitle": "Editar promoción",
        "createTitle": "Crear promoción",
        "product": "Producto",
        "productPh": "p. ej. Aceite de Oliva 2L",
        "brand": "Marca",
        "pack": "Formato / tamaño",
        "normalPrice": "Precio normal",
        "promoPrice": "Precio promocional",
        "description": "Descripción",
        "town": "Pueblo / zona",
        "branch": "Tienda / sucursal",
        "optional": "opcional",
        "start": "Fecha de inicio",
        "end": "Fecha de fin",
        "availability": "Texto de disponibilidad",
        "availabilityPh": "p. ej. Hasta agotar existencias",
        "photo": "Foto",
        "photoError": "No se pudo subir la foto",
        "save": "Guardar",
        "publish": "Publicar",
        "updated": "Promoción actualizada",
        "published": "La promoción ya está activa",
        "submitted": "Promoción enviada para revisión",
        "archived": "Promoción archivada",
        "viewActive": "Activas",
        "viewExpired": "Caducadas",
        "empty": "Aún no hay promociones aquí.",
        "ends": "termina",
        "sponsored": "Patrocinado",
        "sponsoredHint": "Oferta pagada — no forma parte de la comparación de precios",
        "needProduct": "Introduce el nombre del producto",
        "needPromoPrice": "Introduce un precio promocional",
        "needEnd": "Elige una fecha de fin",
        "saveError": "No se pudo guardar la promoción",
        "needDuration": "Elige una duración de promoción",
        "publishedFree": "Tu promoción GRATIS ya está activa",
        "ended": "Promoción finalizada",
        "startHint": "déjalo en blanco para hoy",
        "duration": "Duración de la promoción",
        "days": "días",
        "free": "GRATIS",
        "willEnd": "Termina",
        "testMode": "Modo de prueba — precio mostrado, sin cargo real",
        "introAvailable": "Tu primera promoción de 7 días es GRATIS (una por negocio).",
        "introUsed": "Promoción introductoria gratuita usada.",
        "viewLive": "Activas",
        "viewScheduled": "Programadas",
        "edit": "Editar",
        "endEarly": "Finalizar antes",
        "archive": "Archivar",
        "status": { "live": "Activa", "scheduled": "Programada", "pending": "En revisión", "suspended": "Suspendida", "expired": "Caducada", "archived": "Archivada" }
      },
      "price": {
        "tagline": "Escanea un ticket para leer sus precios",
        "homeHeadline": "Ahorremos juntos",
        "homeTagline": "Precios reales. Personas reales. Una comunidad más fuerte.",
        "homeIntro": "Escanea tus tickets, comparte precios, compara tiendas y ayuda a todos a encontrar las mejores ofertas.",
        "actListTitle": "Mi lista de la compra",
        "actListSub": "¿Qué necesito comprar?",
        "actSearchTitle": "Buscar el precio más barato",
        "actSearchSub": "Compara precios cerca de ti",
        "actScanTitle": "Escanear y compartir precios",
        "actScanSub": "Añade los precios de mi compra",
        "helpTitle": "¡Tus tickets ayudan a todos!",
        "helpBody": "Cuando escaneas el ticket de tu compra, solo usamos la información de los productos y los precios para ayudarte a ti y a otros a encontrar las mejores ofertas.",
        "helpPrivacy": "NO recopilamos ni guardamos tus datos personales, la información de pago/tarjeta ni la imagen del ticket.",
        "buyOftenTitle": "Lo que compro a menudo",
        "buyOftenSub": "Añadir rápido a mi lista",
        "dealsBtnTitle": "Ofertas de hoy",
        "dealsBtnSub": "Ofertas especiales cerca de ti",
        "backToMenu": "Atrás",
        "scanWhyTitle": "Ayuda a todos a ahorrar",
        "scanWhyBody": "Escanea el ticket de tu compra y leeremos los precios. Tus precios te ayudan a ti y a otras personas de la comunidad a ver dónde están más baratos los productos.",
        "savedCommunityTitle": "¡Gracias! \u2764\ufe0f",
        "savedCommunityBody": "Tus precios se han añadido. Acabas de ayudar a otros compradores a encontrar mejores precios.",
        "slAddHeading": "¿Qué necesitas?",
        "quickAddTitle": "Añadir rápido",
        "regularTitle": "Lo que compro habitualmente",
        "addToListBtn": "Añadir",
        "buyOftenEmpty": "Los productos que compras a menudo aparecerán aquí, listos para añadir con un toque.",
        "manageProductsLink": "Gestionar mis productos guardados",
        "addedToListToast": "Añadido a tu lista de la compra",
        "tabScan": "Escanear",
        "tabSearch": "Buscar",
        "tabFav": "Favoritos",
        "tabDeals": "Ofertas",
        "dist": { "pick": "Elige tu municipio para ver distancias", "list": "Lista", "map": "Mapa", "soon": "Próximamente", "directions": "Cómo llegar", "unavailable": "Ubicación aún no disponible", "approxTravel": "Distancia aprox.", "extraTravel": "Viaje extra aprox.", "productSaving": "Ahorro en productos", "vsOne": "frente a Una Tienda", "partial": "Algunas ubicaciones aún no disponibles", "nearestNone": "Aún no hay una tienda ubicada de forma fiable para estos artículos", "knownFor": "Precios recientes conocidos para {{covered}}/{{total}} artículos" },
        "reviewNote": "Revisa lo que leyó la IA, corrige lo que esté mal y guarda. La foto del ticket nunca se almacena, solo los precios.",
        "scanTitle": "Escanear ticket",
        "scanBlurb": "Fotografía un ticket o elige uno de tu teléfono. Funciona con cualquier tienda e idioma.",
        "receiptPrivacy": "Privacidad del ticket: Tu ticket se usa únicamente para leer la información de productos y precios. La imagen del ticket no se muestra públicamente y Price-2-Day no la almacena de forma permanente. La información personal y de pago no se añade a la base de datos de precios de Price-2-Day.",
        "signInToScan": "Inicia sesión para escanear y enviar un ticket.",
        "signIn": "Iniciar sesión",
        "takePhoto": "Hacer foto",
        "choosePhoto": "Elegir foto existente",
        "readingReceipt": "Leyendo ticket…",
        "receiptPreviewAlt": "vista previa del ticket",
        "reviewTitle": "Revisar y corregir",
        "reviewSubtitle": "Edita cualquier campo que la IA haya leído mal.",
        "fShop": "Tienda",
        "fLocation": "Ubicación / sucursal",
        "fDate": "Fecha",
        "fCurrency": "Moneda",
        "fTotal": "Total del ticket",
        "itemsLabel": "ARTÍCULOS ({{count}})",
        "noItems": "No se reconocieron artículos.",
        "itemN": "Artículo {{n}}",
        "removeItem": "Eliminar artículo",
        "fDesc": "Descripción (tal cual)",
        "fBrand": "Marca",
        "fPackSize": "Tamaño / envase",
        "fQty": "Cantidad",
        "fUnitPrice": "Precio unitario",
        "fLineTotal": "Total línea",
        "fDiscount": "Descuento / oferta",
        "confirmSave": "Confirmar y guardar precios",
        "saving": "Guardando…",
        "cancel": "Cancelar",
        "signInHint": "Debes iniciar sesión para guardar.",
        "savedTitle": "Ticket añadido correctamente",
        "savedBody": "Gracias por contribuir. Puedes añadir todos los tickets que quieras.",
        "scanAnother": "Escanear otro ticket",
        "searchPlaceholder": "Busca un producto, p. ej. Aceite de oliva o ACEITE",
        "searchBtn": "Buscar",
        "sortLabel": "Ordenar:",
        "sortLowest": "Precio más bajo",
        "sortRecent": "Más reciente",
        "sortNearest": "Más cercano",
        "noResults": "No se encontraron productos. Prueba con otras palabras.",
        "asPrinted": "tal cual",
        "lowestPrice": "Precio más bajo",
        "lowestUnit": "Precio unitario más bajo",
        "unknownShop": "Tienda desconocida",
        "shopsCount": "{{count}} tienda(s)",
        "receiptDated": "Ticket con fecha",
        "offer": "Oferta",
        "freshRecent": "Reciente",
        "freshOlder": "Antiguo",
        "freshHistorical": "Histórico",
        "freshUnknown": "Desconocido",
        "disclaimer": "Los precios en Price-2-Day se basan en información aportada por la comunidad. Pueden estar desactualizados o variar según la tienda y el momento. Comprueba siempre en la tienda antes de comprar.",
        "errMin2": "Escribe al menos 2 caracteres para buscar.",
        "errHeic": "No se pudo leer ese formato de foto. Prueba con JPG o PNG.",
        "errChooseImage": "Elige una imagen del ticket.",
        "errNoData": "No se pudieron extraer datos. Prueba con una foto más clara.",
        "errScan": "No se pudo leer el ticket. Inténtalo de nuevo.",
        "errNoItems": "Añade al menos un artículo con descripción antes de guardar.",
        "errSignIn": "Inicia sesión para guardar los precios del ticket.",
        "errDuplicate": "Ya has enviado este ticket.",
        "errSaveGeneric": "No se pudo guardar. Inténtalo de nuevo.",
        "errSearch": "La búsqueda falló. Inténtalo de nuevo.",
        "tabProducts": "Mis productos",
        "tabList": "Lista de compra",
        "signInCta": "Iniciar sesión",
        "errGeneric": "Algo salió mal. Inténtalo de nuevo.",
        "edit": "Editar",
        "delete": "Eliminar",
        "back": "Atrás",
        "mpSignIn": "Inicia sesión para guardar tu lista privada de productos que compras.",
        "mpAddTitle": "Añadir un producto",
        "mpNamePh": "Nombre del producto (p. ej. Leche)",
        "mpBrandPh": "Marca (opcional)",
        "mpPackPh": "Tamaño/envase (opcional)",
        "mpAddBtn": "Añadir",
        "mpFromReceipt": "Desde ticket",
        "mpNameRequired": "Introduce el nombre del producto.",
        "mpDuplicate": "Ese producto ya está en Mis productos.",
        "mpAdded": "Añadido a Mis productos.",
        "mpEmpty": "Aún no hay productos. Añade lo que compras habitualmente.",
        "mpToList": "Necesito comprar",
        "mpPickReceipt": "Elige uno de tus tickets guardados",
        "mpNoReceipts": "Todavía no tienes tickets guardados.",
        "mpTickItems": "Marca los productos para añadir a Mis productos",
        "mpAddSelected": "Añadir seleccionados",
        "mpPickOne": "Selecciona al menos un producto.",
        "mpFromReceiptDone": "Añadidos {{added}} · {{skipped}} ya estaban",
        "itemsCount": "{{count}} producto(s)",
        "status_have": "Tengo de sobra",
        "status_low": "Se está acabando",
        "status_need": "Necesito comprar",
        "slSignIn": "Inicia sesión para guardar tu lista de compra privada.",
        "slAddTitle": "Añadir a la lista de compra",
        "slNamePh": "Escribe un artículo, p. ej. Pan",
        "slNameRequired": "Introduce un artículo.",
        "slIncreased": "Se aumentó la cantidad en tu lista.",
        "slAdded": "Añadido a tu lista de compra.",
        "slFromProductsHint": "Consejo: pulsa \"Necesito comprar\" en un producto de Mis productos para añadirlo aquí.",
        "slEmpty": "Tu lista de compra está vacía.",
        "slTick": "Marcar como comprado",
        "slUntick": "Volver a la lista",
        "slBought": "Comprados ({{n}})",
        "slClearCompleted": "Borrar completados",
        "slCleared": "Se borraron {{n}} artículo(s).",
        "buyAgain": "Comprar de nuevo",
        "buyAgainDone": "Añadido a tu lista de compra.",
        "addToMyProductsTitle": "¿Añadir estos a Mis productos?",
        "addToMyProductsBtn": "Añadir seleccionados a Mis productos",
        "addedToMyProducts": "Añadido a Mis productos.",
        "cmpButton": "Comparar mi compra",
        "cmpTitle": "Comparar mi compra",
        "cmpOneShop": "Una tienda",
        "cmpTwoShops": "Dos tiendas",
        "cmpCheapest": "Más barato",
        "cmpNearest": "Más cercano",
        "cmpEstTotal": "Total estimado",
        "cmpSaving": "Ahorro estimado",
        "cmpSubtotal": "Subtotal",
        "cmpCoverage": "Precios encontrados para {{found}} de {{total}} artículos.",
        "cmpNoRecent": "Sin comparación reciente disponible",
        "cmpNoData": "Aún no hay precios disponibles para esta opción.",
        "cmpDisclaimer": "Estimado a partir de precios de tickets de la comunidad. Los precios pueden haber cambiado."
      },
      "notes": {
        "back": "Notas", "overviewHint": "Notas privadas y compartidas para este calendario.",
        "createNote": "Crear nota", "editNote": "Editar nota", "title": "Título", "text": "Texto",
        "textPlaceholder": "Escribe tu nota aquí…", "colour": "Color", "privacy": "Privacidad",
        "private": "Privada", "shared": "Compartida", "privateHint": "Solo tú puedes ver esta nota.",
        "sharedHint": "Elige miembros del calendario para compartirla después de guardar.",
        "formHint": "Dale un título y un texto a tu nota. Las notas son privadas a menos que las compartas.",
        "save": "Guardar", "cancel": "Cancelar", "open": "Abrir", "edit": "Editar", "delete": "Eliminar",
        "copy": "Copiar texto", "share": "Compartir", "manageSharing": "Gestionar uso compartido", "memberEmail": "Correo del miembro",
        "shareDialogHint": "Añade el correo exacto de un miembro del calendario. Ya debe pertenecer a este calendario.",
        "noneShared": "Aún no se ha compartido con nadie.", "sharedWith": "Compartida con", "sharedTag": "compartida contigo",
        "readOnly": "Tienes acceso de solo lectura a esta nota.", "empty": "Esta nota aún no tiene texto.",
        "emptyTitle": "Aún no hay notas", "emptyHint": "Crea tu primera nota privada o compartida.",
        "deleteNote": "Eliminar nota", "deleteConfirm": "Esto elimina la nota de forma permanente. No se puede deshacer.",
        "created": "Nota creada", "updated": "Nota actualizada", "deleted": "Nota eliminada",
        "copied": "Nota copiada al portapapeles", "shareCopied": "Aquí no se puede compartir — nota copiada en su lugar",
        "shareSaved": "Uso compartido actualizado", "loadError": "No se pudieron cargar las notas", "openError": "Esta nota no está disponible",
        "saveError": "No se pudo guardar la nota", "deleteError": "No se pudo eliminar la nota", "copyError": "No se pudo copiar la nota",
        "shareError": "No se pudo compartir la nota", "shareUpdateError": "No se pudo actualizar el uso compartido",
        "dragHandle": "Arrastra para reordenar", "reorderHint": "Arrastra una nota por su controlador para reordenar, o usa las flechas ▲ ▼",
        "moveUp": "Subir", "moveDown": "Bajar", "reorderError": "No se pudo reordenar"
      },
      "lists": {
        "back": "Listas", "overviewHint": "Listas privadas y compartidas para este calendario.",
        "createList": "Crear lista", "editList": "Editar lista", "name": "Nombre", "colour": "Color",
        "privacy": "Privacidad", "private": "Privada", "shared": "Compartida",
        "privateHint": "Solo tú puedes ver y gestionar esta lista.",
        "sharedHint": "Tú eliges exactamente qué miembros del calendario pueden acceder.",
        "formHint": "Nombra tu lista, elige un color y decide quién puede verla.",
        "save": "Guardar", "cancel": "Cancelar", "delete": "Eliminar", "duplicate": "Duplicar", "editItem": "Editar elemento",
        "deleteList": "Eliminar lista", "deleteConfirm": "Esto elimina la lista y todos sus elementos de forma permanente. No se puede deshacer.",
        "manageSharing": "Gestionar uso compartido", "memberEmail": "Correo del miembro",
        "shareHint": "Añade el correo exacto de un miembro del calendario. Ya debe pertenecer a este calendario.",
        "noneShared": "Aún no se ha compartido con nadie.", "sharedWith": "Compartida con", "sharedTag": "compartida contigo",
        "readOnly": "Tienes acceso de solo lectura a esta lista.", "noItems": "Aún no hay elementos.", "newItem": "Nuevo elemento",
        "itemText": "Elemento", "itemPlaceholder": "p. ej. Leche", "quantity": "Cantidad", "qty": "Cant.", "done": "hecho",
        "emptyTitle": "Aún no hay listas", "emptyHint": "Crea tu primera lista privada o compartida.",
        "dragHandle": "Arrastra para reordenar", "reorderHint": "Arrastra el controlador para reordenar, o usa las flechas ▲ ▼", "reorderListsHint": "Arrastra una lista por su controlador para reordenar",
        "itemTextRequired": "El texto del elemento es obligatorio", "created": "Lista creada", "updated": "Lista actualizada",
        "deleted": "Lista eliminada", "duplicated": "Se creó una copia privada", "itemUpdated": "Elemento actualizado",
        "loadError": "No se pudieron cargar las listas", "openError": "Esta lista no está disponible", "saveError": "No se pudo guardar la lista",
        "deleteError": "No se pudo eliminar la lista", "dupError": "No se pudo duplicar la lista", "itemError": "No se pudo añadir el elemento",
        "itemError2": "No se pudo actualizar el elemento", "itemDelError": "No se pudo eliminar el elemento", "reorderError": "No se pudo reordenar",
        "shareSaved": "Uso compartido actualizado", "shareError": "No se pudo actualizar el uso compartido"
      },
      "bookingCal": {
        "repeatEditHint": "cambia cómo se repite esta reserva", "repeatUpdated": "Repetición actualizada — {n} reservas",
        "repeatRemoved": "Esta reserva ya no se repite", "skipDate": "Omitir esta fecha", "skipDateQ": "¿Omitir esta fecha?",
        "skipDateDesc": "Solo se eliminará {date}. El resto de la serie repetida permanece sin cambios. Puedes restaurarla más tarde desde la Papelera.",
        "dateSkipped": "Se omitió esta fecha — el resto de la repetición permanece sin cambios",
        "repeatsPrefix": "Se repite", "recEveryDay": "cada día", "recEveryNDays": "cada {n} días",
        "recEveryWeek": "cada semana", "recEvery2Weeks": "cada 2 semanas", "recEveryNWeeks": "cada {n} semanas",
        "recOn": "el", "recMonthly": "mensualmente", "recEveryNMonths": "cada {n} meses", "recUntil": "hasta el {d}",
        "recForN": "durante {n} repeticiones", "dupDelFailed": "No se pudieron eliminar las reservas duplicadas",
        "listsNav": "Listas", "notesNav": "Notas",
        "agenda": "Agenda",
        "recycleBin": "Papelera",
        "yourRole": "Tu rol",
        "backupNow": "Copia ahora",
        "lastBackup": "Última copia automática",
        "verified": "VERIFICADA",
        "backupFailed2": "COPIA DEL CALENDARIO FALLIDA",
        "noBackupYet": "Aún no hay copia automática",
        "myCalendars": "Mis calendarios",
        "sharedWithMe": "Compartidos conmigo",
        "createCalendar": "Crear calendario",
        "renameCalendar": "Renombrar calendario",
        "deleteCalendar": "Eliminar calendario",
        "restore": "Restaurar",
        "recentlyDeleted": "Eliminados recientemente",
        "createFirstBody": "Crea tu propio calendario privado o espera a que te inviten a uno.",
        "createCalHint": "Tu nuevo calendario es privado. Serás su propietario.",
        "calNamePlaceholder": "p. ej. Personal, Trabajo, Familia",
        "calNameRequired": "El nombre del calendario es obligatorio",
        "calCreated": "Calendario creado",
        "calRenamed": "Calendario renombrado",
        "calDeleted": "Calendario eliminado — recuperable durante 30 días",
        "calRestored": "Calendario restaurado",
        "deleteCalendarQ": "¿Eliminar este calendario?",
        "deleteCalendarDesc": "El calendario y sus reservas se ocultarán y podrán restaurarse en un plazo de 30 días.",
        "members": "Miembros",
        "membersHint": "Personas que pueden acceder a este calendario y sus roles.",
        "inviteByEmail": "Invitar por correo",
        "sendInvite": "Invitar",
        "invitePending": "Invitación guardada — tendrán acceso cuando se unan a Find-2-Day",
        "inviteAdded": "Miembro añadido",
        "emailRequired": "El correo es obligatorio",
        "pendingInvites": "Invitaciones pendientes",
        "makeOwner": "Hacer propietario",
        "remove": "Eliminar",
        "ownershipTransferred": "Propiedad transferida",
        "memberRemoved": "Miembro eliminado",
        "removeMemberTitle": "¿Eliminar a esta persona?",
        "removeMemberBody": "Esto revocará inmediatamente el acceso de {name} a este calendario. Las reservas que haya creado se conservarán. Puedes volver a invitarle más adelante.",
        "removeAccess": "Eliminar acceso",
        "thisPerson": "esta persona",
        "reminders": "Recordatorios",
        "remindersHint": "Avisa a los miembros del calendario antes de esta reserva. Opcional: elige los que quieras.",
        "remind15": "15 minutos antes",
        "remind60": "1 hora antes",
        "remind1d": "1 día antes",
        "remindNone": "Sin recordatorio",
        "reminderSet": "Recordatorio activado",
        "birthday": "Cumpleaños",
        "birthdays": "Cumpleaños",
        "addBirthday": "Añadir cumpleaños",
        "editBirthday": "Editar cumpleaños",
        "birthdayHint": "Los cumpleaños se repiten cada año hasta que los elimines. Son independientes de las reservas.",
        "personName": "Nombre",
        "personNamePlaceholder": "p. ej. Adele",
        "day": "Día",
        "month": "Mes",
        "bdayOnDay": "El mismo día",
        "bday1Day": "1 día antes",
        "bday1Week": "1 semana antes",
        "birthdayReminderHint": "Los recordatorios avisan a los miembros del calendario a las 09:00 (hora de España).",
        "feb29Note": "En años no bisiestos, este cumpleaños se muestra y se recuerda el 28 de febrero.",
        "bdayNameRequired": "Introduce un nombre",
        "bdayAdded": "Cumpleaños añadido",
        "bdaySaved": "Cumpleaños actualizado",
        "bdayDeleted": "Cumpleaños eliminado",
        "noBirthdays": "Aún no hay cumpleaños.",
        "repeatsYearly": "Se repite cada año",
        "deleteBirthdayTitle": "¿Eliminar este cumpleaños?",
        "deleteBirthdayBody": "Esto eliminará a {name} y detendrá todos los recordatorios anuales futuros.",
        "roleOwner": "Propietario",
        "roleAdmin": "Administrador",
        "roleMember": "Miembro",
        "roleView": "Solo lectura",
        "repeat": "Repetir",
        "repeatNone": "No se repite",
        "repeatDaily": "Cada día",
        "repeatWeekly": "Cada semana",
        "repeatBiweekly": "Cada 2 semanas",
        "repeatMonthly": "Cada mes",
        "repeatCustom": "Personalizado",
        "every": "Cada",
        "days": "días",
        "weeks": "semanas",
        "months": "meses",
        "ends": "Termina",
        "endAfter": "Tras N veces",
        "endOn": "En fecha",
        "endDateRequired": "Elige una fecha de fin para la repetición",
        "applyTo": "Aplicar cambios a",
        "scope_this": "Solo esta reserva",
        "scope_future": "Esta y las futuras",
        "scope_series": "Toda la serie",
        "repeats": "Reserva periódica",
        "repeatsShort": "Se repite",
        "savedSeries": "Se añadieron {n} reservas",
        "payment": "Pago",
        "unpaid": "Sin pagar",
        "paidStatus": "Pagado",
        "free": "Gratis",
        "price": "Precio",
        "amountPaid": "Pagado",
        "balance": "Saldo",
        "notSpecified": "Sin especificar",
        "cash": "Efectivo",
        "card": "Tarjeta",
        "bankTransfer": "Transferencia",
        "other": "Otro",
        "paymentNotePh": "Nota de pago (p. ej. €10 de depósito)",
        "paidBadge": "PAGADO",
        "unpaidBadge": "SIN PAGAR",
        "dueBadge": "DEBE",
        "paidExceeds": "El importe pagado no puede superar el precio",
        "payAll": "Todos los pagos",
        "payDue": "Pendiente de pago",
        "payPaid": "Pagado",
        "payFree": "Gratis",
        "today": "Hoy",
        "addBooking": "Añadir reserva",
        "add": "Añadir",
        "edit": "Editar",
        "reschedule": "Reprogramar",
        "pickDate": "Elegir una fecha",
        "cancel": "Cancelar",
        "delete": "Eliminar",
        "history": "Historial",
        "noBookings": "No hay reservas a partir de esta fecha.",
        "newBooking": "Nueva reserva",
        "editBooking": "Editar reserva",
        "formHint": "Introduce los datos del cliente y de la cita.",
        "date": "Fecha",
        "start": "Inicio",
        "end": "Fin",
        "clientName": "Nombre del cliente",
        "service": "Servicio / cita",
        "phone": "Teléfono",
        "email": "Correo electrónico",
        "status": "Estado",
        "notes": "Notas",
        "savedNew": "Reserva añadida",
        "savedEdit": "Reserva actualizada",
        "savedDupGroup": "Todas las reservas duplicadas actualizadas",
        "dupEditTitle": "Aplicar cambios a",
        "dupEditDesc": "Esta reserva forma parte de un grupo duplicado de {n} reservas. ¿Cómo quieres guardar los cambios?",
        "dupEditThis": "Solo esta reserva",
        "dupEditAll": "Todas las reservas duplicadas",
        "actions": "Acciones",
        "saveFailed": "No se pudo guardar la reserva",
        "clientRequired": "El nombre del cliente es obligatorio",
        "notesTooLong": "Las notas son demasiado largas. Máximo {n} caracteres.",
        "duplicate": "Duplicar",
        "duplicateBooking": "Duplicar reserva",
        "dupHint": "Elige una o varias fechas. Cada una será una reserva independiente (no una serie repetida).",
        "dupModePick": "Elegir fechas concretas",
        "dupModeEveryDay": "Todos los días",
        "dupFrom": "Desde",
        "dupTo": "Hasta",
        "dupEveryDayHint": "Se creará una reserva para cada día de este rango (ambos incluidos).",
        "dupPickRange": "Elige una fecha Desde y Hasta válidas.",
        "dupTooMany": "El rango es demasiado grande (máx. {n} días). Elige un rango más corto.",
        "dupOriginal": "Original",
        "dupCopies": "Se copia: cliente, servicio, teléfono/email, estado, categoría, notas y pago.",
        "dupSelectDates": "Selecciona nueva(s) fecha(s)",
        "dupCopyReminders": "Copiar también los recordatorios ({n})",
        "dupNoDates": "Aún no has seleccionado fechas.",
        "dupWillCreate": "Esto creará {n} reserva(s) independiente(s).",
        "dupPickDate": "Selecciona al menos una fecha.",
        "dupNeedTimes": "Elige una hora de inicio y de fin.",
        "dupEndAfterStart": "La hora de fin debe ser posterior a la de inicio.",
        "endAfterStart": "La hora de fin debe ser posterior a la de inicio.",
        "dupCreate": "Duplicar",
        "dupCreating": "Creando...",
        "dupCreated": "Se han creado {n} reserva(s) independiente(s)",
        "dupPartial": "Creadas {ok}; {fail} no se pudieron crear: {list}",
        "dupAllFailed": "No se pudo crear ninguna reserva",
        "dupFailedRetry": "No se pudieron crear las reservas. Inténtalo de nuevo en un momento.",
        "dupDelTitle": "Eliminar reserva duplicada",
        "dupDelIntro": "Esta reserva es una de {n} creadas juntas con Duplicar.",
        "dupDelThis": "Eliminar solo esta reserva",
        "dupDelSelect": "Seleccionar reservas para eliminar",
        "dupDelAll": "Eliminar todas las reservas duplicadas",
        "dupThis": "esta",
        "dupDelSelectedGo": "Eliminar seleccionadas",
        "dupDelAllConfirm": "¿Eliminar las {n} reservas duplicadas? Se moverán a la Papelera.",
        "dupDelAllGo": "Eliminar todas",
        "dupDelPickOne": "Selecciona al menos una reserva.",
        "dupDelDone": "Se han eliminado {n} reserva(s)",
        "dupDelPartial": "Eliminadas {ok}; {fail} con error",
        "removeMemberTitleName": "¿Eliminar a {name} de este calendario?",
        "removeMemberBody2": "Perderá el acceso a este calendario de inmediato. Sus reservas existentes NO se eliminarán.",
        "rescheduled": "Reserva reprogramada",
        "cancelled": "Reserva cancelada",
        "deleted": "Reserva movida a la Papelera",
        "restored": "Reserva restaurada",
        "backupDone": "Copia CSV descargada",
        "backupFailed": "Error en la copia",
        "signInNeeded": "Inicia sesión para ver tu calendario.",
        "noAccessTitle": "Aún no hay calendarios",
        "noAccessBody": "No tienes acceso a ningún calendario. Aparecerá aquí cuando te inviten.",
        "recycleHint": "Las reservas eliminadas se conservan 30 días y se pueden restaurar.",
        "recycleEmpty": "La Papelera está vacía.",
        "deletedBy": "Eliminada por",
        "restore": "Restaurar",
        "cancelBookingQ": "¿Cancelar esta reserva?",
        "cancelBookingDesc": "Se marcará como cancelada pero permanecerá en el calendario.",
        "keepIt": "Mantener",
        "cancelBooking": "Cancelar reserva",
        "deleteBookingQ": "¿Eliminar esta reserva?",
        "deleteBookingDesc": "Se moverá a la Papelera y se podrá restaurar en 30 días.",
        "noHistory": "Aún no hay historial.",
        "loadError": "No se pudo cargar tu calendario. Inténtalo de nuevo.",
        "calendarNav": "Calendario",
        "agendaNav": "Agenda",
        "searchNav": "Buscar",
        "upcoming": "Próximas",
        "past": "Pasadas",
        "cancelledFilter": "Canceladas",
        "searchPlaceholder": "Buscar cliente, servicio o cumpleaños…",
        "searchPrompt": "Escribe el nombre de un cliente, servicio o cumpleaños para buscar reservas pasadas y futuras.",
        "noResults": "Ninguna reserva o cumpleaños coincide con tu búsqueda.",
        "noBookingsDay": "No hay reservas este día.",
        "bookingsOn": "Reservas del",
        "category": "Categoría / color",
        "noCategory": "Sin categoría",
        "allCategories": "Todas las categorías",
        "manageCategories": "Gestionar categorías",
        "manageCatHint": "Categorías de color compartidas para este calendario.",
        "newCatName": "Nombre de la nueva categoría",
        "addCategory": "Añadir categoría",
        "noCategories": "Aún no hay categorías.",
        "catAdded": "Categoría añadida",
        "catDeleted": "Categoría eliminada — reservas conservadas",
        "catNameRequired": "El nombre de la categoría es obligatorio",
        "deleteCatQ": "¿Eliminar esta categoría?",
        "deleteCatDesc": "Las reservas que la usen NO se eliminarán — solo perderán esta categoría.",
        "close": "Cerrar",
        "colourInUse": "Ya usado por otra categoría",
        "by": "por"
      },
      "home": {
        "heroTagline": "Todo lo que pasa en tu ciudad.",
        "startCardTitle": "Empezar",
        "startCardDesc": "Descubre todo lo que pasa en tu ciudad.",
        "businessCardTitle": "Negocios",
        "businessCardDesc": "Promociona tu negocio, organización o grupo comunitario.",
        "discoverTitle": "Descubre negocios y eventos",
        "localAlertsTitle": "Centro de Seguridad Comunitaria",
        "classifiedsTitle": "Vender · Perdidos · Encontrados",
        "bankHolidaysTitle": "Días festivos",
        "emergencyTitle": "Números de emergencia",
        "businessSignInTitle": "¿Ya tienes una cuenta de negocio? Inicia sesión",
        "businessCalendarTitle": "Calendario de negocio"
      },
      "frontpageEarnCommission": "Hazte socio comunitario",
      "appBadgeTitle": "Alertas en tu teléfono",
      "appBadgeDesc": "Muestra un indicador en el icono de la app y una notificación cuando llega una nueva Alerta de Seguridad, incluso con la app cerrada.",
      "appBadgeUnsupported": "No compatible en este dispositivo",
      "appBadgeEnabled": "Alertas en el teléfono activadas. Instala Find-2-Day en tu pantalla de inicio para ver el indicador en el icono.",
      "appBadgeFallback": "Tu dispositivo muestra notificaciones pero no un indicador en el icono; aun así verás el aviso de la campana en la app.",
      "safetyOptInTitle": "No te pierdas ninguna Alerta de Seguridad",
      "safetyOptInBody": "Recibe Alertas de Seguridad oficiales directamente en tu teléfono para no perderte información importante de tu comunidad.",
      "safetyOptInCta": "Activar alertas en el teléfono",
      "safetyOptInEnabling": "Activando…",
      "safetyOptInError": "No se pudieron activar las alertas. Inténtalo de nuevo.",
      // Front Page
      "appTitle": "Find-2-day",
      "appTagline": "Encuentra comercios, experiencias, eventos y actividades cerca de ti",
      "getStarted": "Comenzar",
      "listYourBusiness": "Registra tu Negocio",
      "signIn": "Iniciar Sesión",
      "joinBusiness": "Únete como Negocio",
      "categories": {
        "food": "Comida y Restaurantes",
        "music": "Música y Vida Nocturna",
        "family": "Actividades Familiares",
        "offers": "Ofertas Especiales",
        "wellbeing": "Bienestar y más"
      },
      
      // Navigation
      "nav": {
        "discover": "Descubrir",
        "favorites": "Siguiendo",
        "events": "Eventos",
        "offers": "Ofertas",
        "profile": "Perfil",
        "forSale": "Vender / Perdidos / Encontrados",
        "communityHub": "Comunidad"
      },
      
      // Sign In
      "welcomeBack": "¡Bienvenido de Nuevo!",
      "signInSubtitle": "Inicia sesión para continuar descubriendo",
      "emailOrUsername": "Correo electr\u00f3nico",
      "password": "Contraseña",
      "rememberMe": "Recuérdame",
      "forgotPassword": "¿Olvidaste tu Contraseña?",
      "forgotPasswordTitle": "¿Olvidaste tu contraseña?",
      "forgotPasswordDesc": "Introduce tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.",
      "forgotPasswordError": "Algo salió mal. Inténtalo de nuevo.",
      "sendResetLink": "Enviar enlace",
      "sending": "Enviando...",
      "checkYourEmail": "Revisa tu email",
      "resetEmailSent": "Si existe una cuenta con ese email, hemos enviado un enlace para restablecer la contraseña. Revisa tu bandeja de entrada y spam.",
      "backToSignIn": "Volver a Iniciar Sesión",
      "setNewPassword": "Establece una nueva contraseña",
      "setNewPasswordDesc": "Elige una contraseña segura con al menos 6 caracteres.",
      "newPassword": "Nueva contraseña",
      "confirmPassword": "Confirmar contraseña",
      "resetPassword": "Restablecer Contraseña",
      "resetting": "Restableciendo...",
      "passwordReset": "Contraseña Restablecida",
      "passwordResetSuccess": "Tu contraseña ha sido restablecida correctamente. Ya puedes iniciar sesión con tu nueva contraseña.",
      "invalidResetLink": "Enlace no válido",
      "invalidResetLinkDesc": "Este enlace para restablecer la contraseña no es válido o ha caducado.",
      "requestNewLink": "Solicitar un nuevo enlace",
      "passwordMinLength": "La contraseña debe tener al menos 6 caracteres",
      "passwordsMustMatch": "Las contraseñas no coinciden",
      "emailAddress": "Correo electrónico",
      "dontHaveAccount": "¿No tienes una cuenta?",
      "signUp": "Regístrate",
      
      // Business Sign Up
      "businessSignUp": "Registro de Negocio",
      "joinAsBusinessTitle": "Únete como Negocio",
      "freeTrialOffer": "Empieza a publicar tu negocio al instante",
      "startInstantlyTagline": "Empieza a publicar tu negocio al instante",
      "accountInformation": "Información de Cuenta",
      "fullName": "Nombre Completo",
      "email": "Correo Electrónico",
      "businessInformation": "Información del Negocio",
      "businessName": "Nombre del Negocio",
      "description": "Descripción",
      "tellUsAbout": "Cuéntanos sobre tu negocio...",
      "password": "Contraseña",
      "continue": "Continuar",
      "signIn": "Iniciar Sesión",
      
      // Business Sign Up - Placeholders
      "fullNamePlaceholder": "Juan García",
      "passwordPlaceholder": "Crea una contraseña segura",
      "businessNamePlaceholder": "El Nombre de tu Negocio",
      
      // Business Sign Up - Step indicator
      "stepOf": "Paso {{step}} de {{total}}",
      
      // Business Sign Up - Validation errors
      "allFieldsRequired": "Todos los campos son obligatorios",
      "passwordMinLength": "La contraseña debe tener al menos 6 caracteres",
      "businessNameDescRequired": "El nombre y la descripción del negocio son obligatorios",
      "fillAllRequired": "Por favor, rellena todos los campos obligatorios",
      "specifyCategoryOther": "Por favor, especifica tu categoría al seleccionar \"Otro\"",
      "selectPlanRequired": "Por favor, selecciona un plan de suscripción",
      "registrationFailed": "Error en el registro. Por favor, inténtalo de nuevo.",
      "emailAlreadyRegistered": "Este correo ya está registrado. Usa un correo diferente o inicia sesión.",
      
      // Business Sign Up - Step 3: Contact & Location
      "contactLocation": "Contacto y Ubicación",
      "categoryLabel": "Categoría",
      "selectCategory": "Seleccionar categoría",
      "specifyCategory": "Especificar Categoría",
      "specifyCategoryPlaceholder": "ej. Peluquería de Mascotas",
      "cityLabel": "Ciudad",
      "businessAddress": "Dirección del Negocio",
      "addressPlaceholder": "Calle Principal 123, Madrid, 28001, España",
      "addressTip": "Consejo: Busca tu negocio en Google Maps y copia la dirección completa para mejores resultados",
      "phoneNumber": "Teléfono",
      "businessEmailLabel": "Email del Negocio",
      "websiteLabel": "Sitio Web",
      "openingHoursLabel": "Horario de Apertura",
      
      // Business Sign Up - Step 4: Features
      "whatDoesOffer": "¿Qué ofrece tu negocio?",
      "selectAllApply": "Selecciona todo lo que aplique para que los clientes te encuentren",
      "groupSizeLabel": "Capacidad por Tamaño de Grupo",
      "groupSizeHelp": "Selecciona los tamaños de grupo que tu local puede acomodar",
      "eventTypesLabel": "Tipos de Eventos",
      "eventTypesHelp": "Selecciona los tipos de eventos que organizas (si aplica)",
      "specifyEventType": "Especifica el tipo de evento (ej. Shows de comedia, Noches de trivia)",
      "venueTypeLabel": "Tipo de Local",
      "venueTypeHelp": "Selecciona si tu local es interior, exterior o ambos",
      "dietaryOptionsLabel": "Opciones Dietéticas",
      "dietaryOptionsHelp": "Selecciona las opciones dietéticas que ofreces (si aplica)",
      
      // Business Sign Up - Step 5: Additional Features
      "additionalFeaturesTitle": "Características Adicionales",
      "specialFeatures": "Características Especiales",
      "accessibilityFacilities": "Accesibilidad e Instalaciones",
      
      // Business Sign Up - Step 6: Plan Selection
      "chooseYourPlan": "Elige tu Plan",
      "planSubtitle": "Selecciona un plan de suscripción. Tu anuncio se publicará en cuanto se confirme el pago.",
      "b2bExplainerTitle": "¿Facturas a nombre de una empresa?",
      "b2bExplainerBody": "En la siguiente pantalla (Stripe) podrás añadir tu CIF/NIF y la razón social para recibir una factura B2B totalmente conforme, con desglose de IVA. Deja el campo vacío si te suscribes como particular.",
      "perMonth": "mes",
      "plusIVA": "IVA incluido",
      "bestValue": "Mejor Precio",
      "selected": "Seleccionado",
      "freeTrialTitle": "Publica al instante",
      "freeTrialDesc": "Paga una vez — tu anuncio se publica en cuanto se confirma el pago. Todos los precios incluyen IVA del 21%. Cancela cuando quieras.",
      "freeTrialBullet1": "Empieza hoy desde 1 €/mes (facturado anualmente)",
      "freeTrialBullet2": "Publicación inmediata una vez confirmado el pago",
      "freeTrialBullet3": "Cancela cuando quieras desde tu portal de facturación",
      "instantListingTitle": "Empieza a publicar tu negocio al instante",
      "instantListingDesc": "Desde solo 1 €/mes (facturado anualmente, IVA incluido). Sin prueba, sin sorpresas — tu anuncio se publica en cuanto se confirma el pago.",
      "instantBullet1": "Publicación inmediata una vez confirmado el pago",
      "instantBullet2": "Cancela cuando quieras desde tu portal de facturación",
      "instantBullet3": "Todos los precios incluyen el 21% de IVA — sin cargos sorpresa",
      "settingUp": "Configurando...",
      "proceedToPayment": "Continuar al Pago",
      
      // Business Sign Up - Footer
      "alreadyHaveAccount": "¿Ya tienes una cuenta?",
      
      // Opening Hours
      "selectDaysHours": "Selecciona los días y establece el horario de apertura",
      "timeTo": "a",
      "closed": "Cerrado",
      "dayMonday": "Lunes",
      "dayTuesday": "Martes",
      "dayWednesday": "Miércoles",
      "dayThursday": "Jueves",
      "dayFriday": "Viernes",
      "daySaturday": "Sábado",
      "daySunday": "Domingo",
      
      // Plan duration
      "plan6months": "6 meses",
      "plan12months": "Best Setup / Acceso Completo",
      "planBasic": "Básico",
      "perYear": "/año",
      "per6Months": "/6m",
      "per12Months": "/12m",
      "ivaIncluded": "IVA incluido",
      "basicMonthlyHint": "Oferta Fundadora · GRATIS durante 12 meses — sin tarjeta",
      "basicRestrictions": "Solo anuncio + fotos + contacto. Sin eventos, ofertas ni promociones destacadas.",
      "upgradeFromBasicTitle": "Desbloquea eventos, ofertas y funciones",
      "upgradeFromBasicDesc": "Actualmente todas las empresas son gratuitas durante 12 meses con la Oferta Fundadora — eventos, ofertas, promociones destacadas y todas las funciones del anuncio están incluidas sin coste durante el periodo fundador.",
      "planFeatureLocked": "No disponible en el plan Básico",
      "upgradeToUse": "Mejora para usar",
      // Iter 191 — DuckDuckGo / Firefox Focus / Brave install warning
      "browserCompat": {
        "title": "Estás usando {{browser}}",
        "body": "Para instalar Find-2-Day como una app completa — icono correcto, sin la dirección de la web como nombre, pantalla completa al abrir — abre esta página en Chrome, Samsung Internet o Edge.",
        "dismissAria": "Descartar aviso de compatibilidad de navegador"
      },
      "commissionHeadlineNew": "Gana el 50% de todo lo que paguen los negocios que recomendaste",
      "commissionHeadlineSub": "suscripciones, renovaciones, boosts y cualquier futuro producto de pago.",
      "earnUpToPerSignup": "Gana el 50% de todo lo que paguen tus referidos",
      "commissionBasic": "50% de cada pago del plan Básico",
      "commission6mo": "50% de cada pago del plan de 6 meses (legado)",
      "commission12mo": "50% de cada pago del plan de 12 meses",
      "minPayoutHint": "Se necesitan {{amount}} € de saldo mínimo para el pago.",
      "payoutProgress": "Progreso hacia el pago",
      "balanceLabel": "Saldo",
      "eligibleForPayout": "Listo para cobrar",
      "notYetEligible": "Aún no disponible — ¡sigue así!",
      "pendingMaturing": "Pendientes (en maduración)",
      "pendingMaturingDesc": "Comisiones en el periodo de {{days}} días de maduración.",
      "availableForPayout": "Disponibles para pago",
      "availableForPayoutDesc": "Han superado los {{days}} días y están listas para pagarte.",
      "clearanceTooltip": "Las comisiones quedan disponibles {{days}} días después del pago, alineado con la ventana de disputas del procesador de pagos. Cobrarás el importe íntegro — solo tarda {{days}} días en madurar.",
      "maturesInDays": "Madura en {{count}} día",
      "maturesInDays_plural": "Madura en {{count}} días",
      "availableNow": "Disponible",
      "min": "mín",
      "payoutCycleNote": "Los pagos se revisan tras un periodo de maduración de {{days}} días (ventana de reembolso) y se procesan mensualmente por el equipo administrativo.",

      // Comisión por renovación — lenguaje claro
      "earnEachRenewal": "+ 50% cada vez que renueven, mientras sigan contigo",
      "renewalEarnings": "{{count}} renovación · {{total}} €",
      "lifetimeRenewalCommission": "histórico",
      "renewalCommissionTooltip": "Has ganado esto porque el negocio que recomendaste renovó su plan.",
      "initialCommissionTooltip": "Has ganado esto cuando el negocio que recomendaste se registró por primera vez.",
      "renewalLabel": "Renovación",
      "initialLabel": "Inicial",
      "lifetimeRenewalEarnings": "Ganancias por renovación (histórico)",
      "lifetimeRenewalEarningsHint": "Empezarás a ganar en cuanto tus referidos renueven — misma regla del 50%. No tienes que hacer nada.",

      // Cómo ganas — landing
      "howYouEarnTitle": "Cómo ganas — y cómo cobras",
      "howYouEarnPoint1": "Ganas el 50% de cada pago que haga un negocio que recomendaste — suscripción, renovación, boost o cualquier producto de pago futuro.",
      "howYouEarnPoint2": "Misma regla del 50%, para siempre. Mientras sigan pagando a Find-2-Day, tú sigues ganando.",
      "howYouEarnPoint3": "No tienes que hacer nada — calculamos y seguimos cada comisión automáticamente.",
      "howYouEarnPoint4": "Cada nueva ganancia queda pendiente durante {{days}} días, por si el negocio cancela o recibe un reembolso. Si eso ocurre, esa ganancia se elimina. Lo que ya te hayamos pagado, no se revierte nunca.",

      // Cómo funcionan los pagos — panel desplegable
      "howPayoutsWorkTitle": "Cómo funcionan los pagos",
      "howPayoutsWorkOpen": "Ver cómo funcionan los pagos",
      "howPayoutsWorkClose": "Ocultar",
      "howPayoutsRule1": "Ganas el 50% de todo lo que pague un negocio que recomendaste — suscripciones, renovaciones, boosts y cualquier producto de pago futuro.",
      "howPayoutsRule2": "Una sola regla, sin excepciones. Mientras sigan con Find-2-Day, sigues ganando el 50% cada vez que paguen.",
      "howPayoutsRule3": "Las nuevas ganancias quedan pendientes durante {{days}} días mientras esperamos que el pago se confirme con el banco.",
      "howPayoutsRule4": "Si el pago falla, se reembolsa, se cancela o se reclama durante esos {{days}} días, esa ganancia se elimina.",
      "howPayoutsRule5": "Una vez pasados los {{days}} días sin problemas, la ganancia queda disponible para tu próximo pago.",
      "howPayoutsRule6": "Las ganancias que ya te hayamos pagado no se revierten nunca.",

      // Ejemplo de afiliado bajo el modelo unificado del 50%
      "affiliateExampleWithRenewal": "Recomienda un negocio: se une a la Oferta Fundadora — gratis durante 12 meses. Cuando vuelvan los planes de pago, ganarás un 50 % de comisión sobre cada pago que hagan — registro, renovación, boost — automáticamente, mientras sigan suscritos.",

      // Notification preferences
      "notificationPreferences": "Preferencias de notificaciones",
      "emailNotifications": "Notificaciones por email",
      "emailNotificationsOnDesc": "Te enviaremos emails sobre tus favoritos, actualizaciones de afiliados y resúmenes semanales.",
      "emailNotificationsOffDesc": "Desactivado — dejaremos de enviarte actualizaciones y resúmenes. Seguirás recibiendo emails importantes (restablecer contraseña, alertas de seguridad, recibos de facturación) y actualizaciones de servicios contratados.",
      "emailNotificationsSavedOn": "Notificaciones por email activadas.",
      "emailNotificationsSavedOff": "Notificaciones por email desactivadas. Seguirás recibiendo emails importantes y de facturación.",

      // Active / Archived tabs
      "tabActive": "Activos",
      "tabArchived": "Archivados",
      "noActiveItems": "No hay nada activo ahora mismo.",
      "noArchivedItems": "No hay nada archivado.",
      "restore": "Restaurar",
      "restored": "Restaurado",
      "cantRestoreExpired": "Este elemento ya ha caducado. Por favor, duplícalo en su lugar.",
      "restoreFailed": "No se pudo restaurar. Inténtalo de nuevo.",

      
      // Discover
      "sponsored": "Patrocinado",
      "featured": "Destacado",
      "findAmazing": "Encuentra experiencias increíbles cerca de ti",
      "searchPlaceholder": "Buscar negocios...",
      "logout": "Cerrar Sesión",
      "shareApp": "Compartir Find-2-Day",
      "dash": {
        "tagline": "Tu guía local de negocios, eventos y comunidad.",
        "searchPlaceholder": "Busca negocios, eventos, ofertas y más…",
        "discover": "DESCUBRIR", "discoverSub": "Encuentra lo que necesitas en tu zona",
        "search": "Buscar", "searchDesc": "Encuentra de todo",
        "businesses": "Negocios", "businessesDesc": "Servicios locales",
        "events": "Eventos", "eventsDesc": "Qué hacer",
        "offers": "Ofertas", "offersDesc": "Ofertas cerca de ti",
        "myF2d": "MI FIND-2-DAY", "myF2dSub": "Organízate y no pierdas nada",
        "calendar": "Calendario", "calendarDesc": "Planifica tu tiempo",
        "lists": "Listas", "listsDesc": "Mantente organizado",
        "inbox": "Bandeja", "inboxDesc": "Tus novedades",
        "favourites": "Favoritos", "favouritesDesc": "Guarda lo que te gusta",
        "community": "COMUNIDAD", "communitySub": "Una comunidad más segura y fuerte",
        "alerts": "Alertas", "alertsDesc": "Mantente informado",
        "localInfo": "Información local", "localInfoDesc": "Guía de tu zona",
        "business": "NEGOCIO", "businessSub": "Para negocios locales",
        "businessDashboard": "Panel de negocio", "businessDashboardDesc": "Gestiona tu negocio",
        "account": "CUENTA", "accountSub": "Gestiona tu experiencia",
        "settings": "Ajustes", "settingsDesc": "Tus preferencias",
        "shareDesc": "Cuéntaselo a tus amigos",
        "help": "Ayuda y soporte", "helpDesc": "Obtén ayuda"
      },
      "shareAppText": "Descubre negocios, eventos y ofertas locales cerca de ti en Find-2-Day.",
      "linkCopied": "¡Enlace copiado!",
      "all": "Todos",
      "pendingTab": "Pendiente",
      
      // Business Detail extra
      "callNow": "Llamar Ahora",
      "emailButton": "Correo",
      "openingHours": "Horario de Apertura",
      
      // Business Dashboard status messages
      "pendingApprovalTitle": "Pendiente de Aprobación",
      "pendingApprovalDesc": "Tu negocio está siendo revisado por nuestro equipo",
      "applicationRejected": "Solicitud Rechazada",
      "applicationRejectedDesc": "Por favor contacta a soporte para más información",
      "listingSuspended": "Listado Suspendido",
      "listingSuspendedDesc": "Tu listado ha sido suspendido. Contacta a soporte para más detalles.",
      "trialDaysRemaining": "{{days}} día(s) restante(s)",
      "subscribePrompt": "Suscríbete ahora para mantener tu listado activo",
      "paymentPendingTitle": "Pago requerido",
      "paymentPendingDesc": "Completa el pago para hacer visible tu anuncio. Se publicará en cuanto se confirme el pago.",
      "paymentRequiredTitle": "Pago requerido para publicar",
      "paymentRequiredDesc": "Tu negocio está actualmente en la Oferta Fundadora — gratis durante 12 meses, sin tarjeta.",
      "businessVisible": "Tu negocio es visible para los usuarios",
      "subscriptionActive": "Suscripción Activa",
      "subscriptionExpired": "Suscripción Expirada",
      "subscriptionExpiredDesc": "Tu listado está oculto. Suscríbete para reactivarlo.",
      "renewsOn": "Renueva {{date}} ({{days}} días)",
      "listingActive": "Tu listado está activo",
      
      // Photos
      "noPhotosDesc": "Sin fotos aún. Añade hasta 5 fotos de tu negocio.",
      
      // Profile
      "businessAccount": "Cuenta de Negocio",
      "regularAccount": "Cuenta de Usuario",
      "activeFilters": "Filtros Activos",
      "clearAll": "Limpiar Todo",
      "noBusinessesFound": "No se encontraron negocios.",
      "tryDifferentFilters": "Intenta ajustar tus filtros o términos de búsqueda.",
      "businessesWillAppear": "Los negocios aparecerán aquí una vez aprobados por el administrador.",
      "clearFilters": "Limpiar Filtros",
      "registerBusiness": "Registra Tu Negocio",
      "businessFound": "Negocio Encontrado",
      "businessesFound": "Negocios Encontrados",
      
      // Filters
      "filters": "Filtros",
      "groupSize": "Tamaño del Grupo",
      "dietaryOptions": "Opciones Dietéticas",
      "distance": "Distancia",
      "openingHours": "Horario de Apertura",
      "openNow": "Abierto Ahora",
      "openLaterToday": "Abre Más Tarde Hoy",
      "openWeekends": "Abierto Fines de Semana",
      "eventType": "Tipo de Evento",
      "venueType": "Tipo de Local",
      "businessCategories": "Categorías de Negocio",
      "additionalOptions": "Opciones Adicionales",
      "accessibilityFacilities": "Accesibilidad e Instalaciones",
      "resetAll": "Restablecer Todo",
      "applyFilters": "Aplicar Filtros",
      
      // Settings
      "settings": "Configuración",
      "preferences": "Preferencias",
      "language": "Idioma",
      "notifications": "Notificaciones",
      "darkMode": "Modo Oscuro",
      "comingSoon": "Próximamente",
      "account": "Cuenta",
      "privacySecurity": "Privacidad y Seguridad",
      "support": "Soporte",
      "helpCenter": "Centro de Ayuda",
      "aboutApp": "Acerca de Find-2-Day",
      
      // Help Center
      "faqTitle": "Preguntas Frecuentes",
      "faq1q": "¿Cómo registro mi negocio?",
      "faq1a": "Haz clic en 'Únete como Negocio' en la página principal y sigue el proceso de registro de 5 pasos.",
      "faq2q": "¿Cómo funciona la facturación?",
      "faq2a": "Actualmente todas las empresas son gratuitas durante 12 meses con la Oferta Fundadora — sin tarjeta, sin pago durante el periodo fundador. La tarificación futura (si se introduce) se comunicará con suficiente antelación.",
      "faq3q": "¿Cuándo aparecerá mi negocio?",
      "faq3a": "Tu negocio será visible después de la aprobación del administrador, generalmente dentro de 24-48 horas.",
      "contactUs": "Contáctanos",
      
      // About
      "aboutDescription": "Find-2-Day es tu plataforma de descubrimiento local. Encuentra restaurantes, eventos, actividades y ofertas especiales de negocios cerca de ti.",
      "trialPeriod": "Facturación",
      "months": "meses",
      "subscriptionPlans": "Planes de Suscripción",
      "allRightsReserved": "Todos los derechos reservados.",
      
      // Profile
      "changePassword": "Cambiar Contraseña",
      "profilePicture": "Foto de Perfil",
      
      // Events & Offers Pages
      "noEventsYet": "Sin Eventos Aún",
      "eventsWillAppear": "¡Los eventos de negocios locales aparecerán aquí pronto!",
      "browseBusinesses": "Explorar Negocios",
      "noOffersYet": "Sin Ofertas Especiales Aún",
      "offersWillAppear": "¡Vuelve pronto para ver ofertas increíbles de negocios locales!",
      "noFavoritesYet": "Sin Favoritos Aún",
      "startExploring": "¡Comienza a explorar negocios y guarda tus favoritos aquí!",
      "discoverBusinesses": "Descubrir Negocios",
      
      // Common
      "save": "Guardar",
      "saved": "Guardado",
      "removedFromSaved": "Eliminado",
      "followNeedsNotifications": "Permite las notificaciones para seguir negocios.",
      "couldNotSave": "No se pudo guardar — inténtalo de nuevo.",
      "emailNotificationsUpdated": "Notificaciones por email actualizadas",
      "emailSaveFailed": "Error al guardar. Inténtalo de nuevo.",
      "cancel": "Cancelar",
      "editName": "Editar nombre de usuario",
      "displayNameSaved": "Nombre actualizado.",
      "displayNameSaveFailed": "No se pudo actualizar tu nombre. Inténtalo de nuevo.",
      "nameRequired": "El nombre es obligatorio.",
      "nameTooLong": "Mantenlo por debajo de 80 caracteres.",
      "consentText": "Acepto los",
      "consentAnd": "y la",
      "consentRequired": "Debes aceptar los Términos y Condiciones y la Política de Privacidad",
      "termsAndConditions": "Términos y Condiciones",
      "privacyPolicyTitle": "Política de Privacidad",
      "disputePolicyTitle": "Política de Disputas",
      "disputePolicy": "Disputas",
      "date": "Fecha",
      "continue": "Continuar",
      "back": "Atrás",
      "submit": "Enviar",
      "or": "o",
      "loading": "Cargando...",
      "error": "Error",
      "success": "Éxito",
      "goBack": "Volver",
      "goHome": "Ir al Inicio",
      "admin": "Admin",
      
      // Legal footer links
      "terms": "Términos",
      "privacy": "Privacidad",
      "cookies": "Cookies",
      
      // Signup page
      "createAccount": "Crea tu cuenta",
      "confirmPassword": "Confirmar Contraseña",
      "creatingAccount": "Creando Cuenta...",
      
      // Business Dashboard
      "businessDashboard": "Panel de Negocio",
      "businessNotFound": "Perfil de negocio no encontrado",
      "listingUnavailable": "Este anuncio de negocio ya no está disponible.",
      "duplicateViewBtn": "Ver anuncio",
      "claimRetiredTitle": "Reclamar ya no está disponible",
      "claimRetiredBody": "Find-2-Day ahora funciona mediante autorregistro. Si tienes un negocio, simplemente crea tu propio anuncio — no es necesario reclamar un registro existente.",
      "claimRetiredRegister": "Registra tu negocio",
      "backToHome": "Volver al inicio",
      "townCityLabel": "Pueblo / Ciudad", "townCityPlaceholder": "p. ej. Madrid", "countryLabel": "País", "countrySelect": "Selecciona un país", "regionLabel": "Región / Estado / Provincia", "regionPlaceholder": "p. ej. Andalucía", "localAreaLabel": "Zona / Distrito", "postcodeLabel": "Código postal", "regionLabel2": "Región / Estado", "provinceLabel": "Provincia", "provincePlaceholder": "p. ej. Almería", "mapView": "Mapa", "mapNoCoords": "Ninguno de estos negocios tiene ubicación en el mapa todavía. Cambia a la vista de lista para ver todos los resultados.", "directionsBtn": "Cómo llegar", "filterCountry": "País", "filterRegion": "Región / Estado", "filterProvince": "Provincia", "filterTown": "Pueblo / Ciudad", "allCountries": "Todos los países", "allRegions": "Todas las regiones", "allProvinces": "Todas las provincias", "allTowns": "Todos los pueblos", "locationFilterLabel": "Ubicación",
      "invalidBusinessLink": "Lo sentimos, este enlace de negocio parece no ser válido o haber caducado.",
      "brokenLinkTitle": "Este enlace de negocio no funciona",
      "browseBusinesses": "Explorar negocios",
      "returnHome": "Volver al inicio",
      "emailNotVerified": "Correo no verificado",
      "emailNotVerifiedDesc": "Tu listado no será visible públicamente hasta que verifiques tu correo. Revisa tu bandeja de entrada para el enlace de verificación.",
      "subscriptionDetails": "Detalles de Suscripción",
      "plan": "Plan",
      "noPlanSelected": "Sin plan seleccionado",
      "status": "Estado",
      "daysRemaining": "días restantes",
      "fullyActive": "Totalmente activo",
      "approval": "Aprobación",
      "payment": "Pago",
      "confirmed": "Confirmado",
      "pending": "Pendiente",
      "trialEnds": "Fin de Suscripción",
      "subscriptionEnds": "Fin de Suscripción",
      "paymentNotCompleted": "Pago no completado",
      "completePaymentDesc": "Completa tu pago para activar tu listado de negocio.",
      "completePayment": "Completar Pago",
      "processing": "Procesando...",
      "photos": "Fotos",
      "addPhoto": "Añadir Foto",
      "uploading": "Subiendo...",
      "noPhotosYet": "Sin fotos aún. Añade hasta 5 fotos de tu negocio.",
      "businessInfo": "Información del Negocio",
      "editProfile": "Editar Perfil",
      "saveChanges": "Guardar Cambios",
      "cancelEdit": "Cancelar",
      "website": "Sitio Web",
      "openingHoursLabel": "Horario de Apertura",
      "notProvided": "No proporcionado",
      "featuresServices": "Características y Servicios",
      "groupSizeAccommodation": "Capacidad de Grupos",
      "eventTypesLabel": "Tipos de Eventos",
      "venueTypeLabel": "Tipo de Local",
      "dietaryOptionsLabel": "Opciones Dietéticas",
      "additionalFeaturesLabel": "Características Adicionales",
      "accessibilityLabel": "Accesibilidad e Instalaciones",
      "noneSpecified": "No especificado",
      "quickActions": "Acciones Rápidas",
      "viewPublicProfile": "Ver Perfil Público",
      "accountSettings": "Configuración de Cuenta",
      "trialExpired": "Suscripción caducada",
      "trialExpiredDesc": "Tu anuncio ya no es visible. Suscríbete ahora para restaurarlo.",
      "trialEndsOn": "Tu suscripción termina el",
      "trialEndsOnDesc": "Renueva para mantener tu anuncio visible.",
      "subscribeNow": "Suscríbete Ahora",
      "day": "día",
      "days": "días",
      "left": "restante",
      "remaining": "restantes",
      
      // Business Detail
      "about": "Acerca de",
      "share": "Compartir",
      "copyLink": "Copiar Enlace",
      "linkCopied": "¡Copiado!",
      "shareMore": "Más",
      "shareDefaultText": "¡Mira este listado en Find-2-Day!",
      "groupSizesWelcome": "Grupos Bienvenidos",
      "contactInfo": "Información de Contacto",
      
      // Verify Email
      "verifyingEmail": "Verificando tu correo...",
      "verifySuccessDesc": "Ya puedes cerrar esta página o iniciar sesión en tu panel.",
      
      // Data Deletion
      "deleteAccountTitle": "Eliminar Cuenta y Datos",
      "whatWillBeDeleted": "Lo que se eliminará:",
      "deleteItem1": "Tu cuenta de usuario y credenciales de acceso",
      "deleteItem2": "Listado de negocio y fotos (si aplica)",
      "deleteItem3": "Favoritos y preferencias guardadas",
      "deleteItem4": "Todos los datos personales asociados",
      "reasonOptional": "Motivo (opcional)",
      "reasonPlaceholder": "Cuéntanos por qué te vas...",
      "requestDeletion": "Solicitar Eliminación de Cuenta",
      "emailDirectly": "También puedes enviar tu solicitud directamente a:",
      "requestReceived": "Solicitud Recibida",
      "requestReceivedDesc": "Tu solicitud de eliminación de cuenta y datos ha sido enviada. La procesaremos dentro de 30 días y confirmaremos por correo.",
      "contactDirectly": "También puedes contactarnos directamente en",
      
      // Profile
      "memberSince": "Miembro desde",
      
      // Settings links
      "nearbyDeals": "Ofertas & Promociones Cercanas",
      "nearbyDealsDesc": "Recibe ofertas patrocinadas ocasionales de negocios que no sigues",
      "sponsoredDeals": {
        "label": "Mostrar ofertas patrocinadas",
        "helper": "Actívalo para ver ofertas patrocinadas de negocios de toda tu región."
      },
      "yourAlerts": {
        "label": "Tus avisos",
        "helper": "Toca para ver novedades de negocios que sigues, tus municipios y novedades importantes."
      },
      "discoverOnboarding": {
        "stepN": "Paso {{n}} de {{total}}",
        "skip": "Omitir",
        "next": "Siguiente",
        "finish": "Finalizar",
        "done": "Hecho",
        "chooseTowns": "Elige tus municipios",
        "step1": {
          "title": "Descubre lo que pasa cerca de ti",
          "body": "Recibe novedades de negocios locales, eventos y avisos importantes."
        },
        "step2": {
          "title": "Mantente al día",
          "body": "Recibe avisos de negocios que sigues, tus municipios y novedades importantes."
        },
        "step3": {
          "title": "Descubre nuevos lugares",
          "body": "Actívalo para ver ofertas patrocinadas de toda tu región."
        },
        "step4": {
          "title": "¡Todo listo!",
          "body": "¿Quieres avisos aún más relevantes? Elige los municipios que te interesan."
        }
      },
      "enableNotificationsFirst": "Sigue un negocio o activa las notificaciones para gestionar tus preferencias.",
      "legal": "Legal",
      "termsOfService": "Términos de Servicio",
      "privacyPolicy": "Política de Privacidad",
      "cookiePolicy": "Política de Cookies",
      "deleteData": "Eliminar Mis Datos",
      
      // Admin Dashboard
      "adminDashboard": "Panel de Administración",
      "viewSite": "Ver Sitio",
      "totalBusinesses": "Total de Negocios",
      "pendingApproval": "Pendiente de Aprobación",
      "approved": "Aprobados",
      "active": "Activos (Visibles)",
      "suspended": "Suspendidos",
      "rejected": "Rechazados",
      "noBusinessesInCategory": "No se encontraron negocios en esta categoría",
      "loadingBusinesses": "Cargando negocios...",
      "approveBusiness": "Aprobar Negocio",
      "rejectBusiness": "Rechazar Negocio",
      "deleteBusiness": "Eliminar Negocio",
      "suspendBusiness": "Suspender Negocio",
      "approveDesc": "Esto hará que el negocio sea visible para todos los usuarios.",
      "rejectDesc": "Por favor, proporciona un motivo de rechazo.",
      "deleteDesc": "Esto eliminará el negocio temporalmente — se ocultará del feed público pero podrás restaurarlo desde el filtro Papelera.",
      "suspendDesc": "Esto ocultará el negocio de la vista pública. Por favor, proporciona un motivo.",
      "enterRejectReason": "Escribe el motivo del rechazo...",
      "enterSuspendReason": "Escribe el motivo de la suspensión...",
      "yesApprove": "Sí, Aprobar",
      "yesDelete": "Sí, Eliminar",
      "yesSuspend": "Sí, Suspender",
      "approving": "Aprobando...",
      "rejecting": "Rechazando...",
      "deleting": "Eliminando...",
      "suspending": "Suspendiendo...",
      "approve": "Aprobar",
      "reject": "Rechazar",
      "delete": "Eliminar",
      "suspend": "Suspender",
      "view": "Ver",
      
      // Business Signup form extras
      "selectCategory": "Selecciona categoría",
      "selectDays": "Selecciona días y establece el horario",
      "closed": "Cerrado",
      
      // Change Password
      "changePasswordTitle": "Cambiar Contraseña",
      
      // Admin login
      "adminAccess": "Acceso a la administración de la plataforma",
      
      // Business login
      "businessLogin": "Acceso de Negocio",
      "businessLoginDesc": "Inicia sesión para gestionar tu listado de negocio",
      
      // Analytics
      "listingAnalytics": "Analíticas del Listado",
      "views": "Visitas",
      "calls": "Llamadas",
      "directions": "Direcciones",
      
      // Event Management (Admin)
      "eventManagement": "Gestión de Eventos",
      "businessManagement": "Gestión de Negocios",
      "pendingEvents": "Eventos Pendientes",
      "approvedEvents": "Eventos Aprobados",
      "rejectedEvents": "Eventos Rechazados",
      "expiredEvents": "Eventos Expirados",
      "allEvents": "Todos los Eventos",
      "approveEvent": "Aprobar Evento",
      "rejectEvent": "Rechazar Evento",
      "approveEventDesc": "Esto hará que el evento sea visible para todos los usuarios.",
      "rejectEventDesc": "Por favor, proporciona un motivo para rechazar este evento.",
      "enterEventRejectReason": "Escribe el motivo del rechazo del evento...",
      "yesApproveEvent": "Sí, Aprobar Evento",
      "noEventsFound": "No se encontraron eventos en esta categoría",
      "loadingEvents": "Cargando eventos...",
      "eventBy": "por",
      "eventLocation": "Ubicación",
      "eventDates": "Fechas",
      "eventCreated": "Creado",
      
      // Business Dashboard - My Events
      "myEvents": "Mis Eventos",
      "createEvent": "Crear Evento",
      "noEventsCreated": "Aún no has creado ningún evento",
      "eventPending": "Pendiente de Revisión",
      "eventApproved": "Aprobado",
      "eventRejected": "Rechazado",
      "eventExpired": "Expirado",
      "rejectionReasonLabel": "Motivo del Rechazo",
      
      // Event Creation Form
      "newEvent": "Nuevo Evento",
      "eventTitle": "Título del Evento",
      "eventTitlePlaceholder": "Introduce el título del evento",
      "eventDescription": "Descripción",
      "eventDescriptionPlaceholder": "Describe tu evento...",
      "eventCategory": "Categoría",
      "selectEventCategory": "Selecciona una categoría",
      "eventDate": "Fecha del Evento",
      "startTime": "Hora de Inicio",
      "endTime": "Hora de Fin",
      "eventPrice": "Precio (opcional)",
      "eventPricePlaceholder": "ej. 15.00",
      "free": "Gratis",
      "eventPoster": "Cartel del Evento (opcional)",
      "uploadPoster": "Subir Cartel",
      "removePoster": "Eliminar",
      "eventVenue": "Lugar / Ubicación",
      "eventVenuePlaceholder": "Ubicación o nombre del lugar del evento",
      "submitEvent": "Enviar Evento",
      "creatingEvent": "Creando...",
      "eventCreatedSuccess": "¡Evento creado y publicado!",
      "eventCreateFailed": "No se pudo crear el evento. Inténtalo de nuevo.",
      "eventUpdateFailed": "No se pudo actualizar el evento. Inténtalo de nuevo.",
      "eventsBlockedTrialEnded": "Tu prueba ha finalizado. Añade un método de pago para volver a publicar eventos.",
      "eventsBlockedPlan": "Tu plan actual no incluye eventos. Mejora tu plan para publicar eventos.",
      "eventsBlockedMax": "Has alcanzado tu límite de {{max}} eventos activos. Archiva un evento abajo o pásate a Destacado para tener más.",
      "eventsActiveHint": "Activos = solo eventos en curso y próximos. Los eventos pasados/caducados no cuentan para tu límite.",
      "eventTitleRequired": "El título es obligatorio",
      "eventDateRequired": "La fecha es obligatoria",
      "eventStartTimeRequired": "La hora de inicio es obligatoria",
      "eventEndTimeRequired": "La hora de fin es obligatoria",
      "eventEndAfterStart": "La hora de fin debe ser posterior a la de inicio",
      "eventCategoryRequired": "La categoría es obligatoria",
      "customCategoryRequired": "Por favor describe el tipo de evento",
      "customCategoryPlaceholder": "Describe el tipo de evento (ej. Mercadillo, Exposición de Mascotas)",
      "eventVenueRequired": "El lugar es obligatorio",
      "categoryMusic": "Música",
      "categorySports": "Deportes",
      "categoryFood": "Comida y Bebida",
      "categoryArts": "Arte y Cultura",
      "categoryNetworking": "Networking",
      "categoryCommunity": "Comunidad",
      "categoryWorkshop": "Taller",
      "categoryMarket": "Mercado",
      "categoryFestival": "Festival",
      "categoryCharity": "Caridad",
      
      // Event Edit & Delete
      "editEvent": "Editar",
      "archiveEvent": "Archivar",
      "updateEvent": "Actualizar Evento",
      "updatingEvent": "Actualizando...",
      "editEventTitle": "Editar Evento",
      "eventUpdatedSuccess": "¡Evento actualizado y publicado!",
      "eventArchivedSuccess": "Evento archivado correctamente.",
      "archiveEventConfirm": "¿Archivar este evento?",
      "archiveEventDesc": "Esto ocultará el evento de todos los listados. Esta acción no se puede deshacer.",
      "yesArchive": "Sí, Archivar",
      "eventArchived": "Archivado",
      
      // Category translations
      "categoryServices": "Servicios",
      "categoryShopping": "Compras",
      "categoryHealth": "Salud y Belleza",
      "categoryHealthWellness": "Salud y Bienestar",
      "categoryEvents": "Eventos y Entretenimiento",
      "categoryEducation": "Educación",
      "categoryHomeServices": "Servicios del Hogar",
      "categoryTravel": "Viajes y Turismo",
      "categoryTechnology": "Tecnología",
      "categoryVets": "Veterinarios",
      "categoryVolunteerWork": "Voluntariado",
      "categoryJobs": "Empleo",
      "categoryCommunityHelp": "Ayuda Comunitaria",
      "categoryCommunity": "Comunidad",
      "categoryAutomotive": "Automotriz",
      "categorySports": "Deportes y Fitness",
      "categoryArts": "Arte y Cultura",
      "categoryBeautySpa": "Belleza y Spa",
      "categoryProfessional": "Servicios Profesionales",
      "allCategories": "Todas las Categorías",
      "categoryOther": "Otro",
      
      // Reviews
      "reviews": "Reseñas",
      "writeReview": "Escribir una Reseña",
      "yourRating": "Tu Valoración",
      "yourReview": "Tu Reseña",
      "reviewPlaceholder": "Comparte tu experiencia con este negocio (mín. 10 caracteres)...",
      "submitReview": "Enviar Reseña",
      "submittingReview": "Enviando...",
      "reviewSubmitted": "Reseña enviada. Aparecerá después de la aprobación del administrador.",
      "alreadyReviewed": "Ya has dejado una reseña para este negocio",
      "reviewTextMin": "La reseña debe tener al menos 10 caracteres",
      "reviewTextMax": "La reseña debe tener 500 caracteres o menos",
      "ratingRequired": "Por favor, selecciona una valoración",
      "noReviewsYet": "Aún no hay reseñas. ¡Sé el primero!",
      "averageRating": "Valoración Media",
      "reviewCount": "{{count}} reseñas",
      "reviewPending": "Pendiente",
      "reviewApproved": "Aprobada",
      "reviewRejected": "Rechazada",
      "loginToReview": "Inicia sesión para dejar una reseña",
      "reviewModeration": "Moderación de Reseñas",
      "pendingReviews": "Reseñas Pendientes",
      "approvedReviews": "Reseñas Aprobadas",
      "rejectedReviews": "Reseñas Rechazadas",
      "allReviews": "Todas las Reseñas",
      "approveReview": "Aprobar",
      "rejectReview": "Rechazar",
      "noReviewsFound": "No se encontraron reseñas",
      "reviewBy": "por",
      "reviewFor": "para",
      "charsRemaining": "{{count}} caracteres restantes",

      // Reports/Disputes
      "reportTitle": "Reportar Contenido",
      "reportingItem": "Reportando: {{name}}",
      "reportReason": "¿Por qué reportas esto?",
      "reportReasonRequired": "Por favor selecciona un motivo",
      "reportDetails": "Detalles adicionales",
      "reportDetailsPlaceholder": "Describe el problema en detalle (mín. 10 caracteres)...",
      "reportDetailsMin": "Los detalles deben tener al menos 10 caracteres",
      "submitReport": "Enviar Reporte",
      "submitting": "Enviando...",
      "reportSubmitted": "Reporte Enviado",
      "reportSubmittedDesc": "Nuestro equipo revisará tu reporte y tomará las medidas apropiadas. El contenido no se elimina automáticamente.",
      "reportAlreadyExists": "Ya tienes un reporte abierto para este elemento",
      "reportSubmitError": "Error al enviar el reporte. Inténtalo de nuevo.",
      "done": "Listo",
      "reportListing": "Reportar este listado",
      "reportReview": "Reportar reseña",
      "loginToReport": "Inicia sesión para reportar este listado",
      "reportReason_incorrect_information": "Información Incorrecta",
      "reportReason_offensive_content": "Contenido Ofensivo",
      "reportReason_spam": "Spam",
      "reportReason_duplicate": "Duplicado",
      "reportReason_scam_fraud": "Estafa / Fraude",
      "reportReason_other": "Otro",
      "reportManagement": "Reportes",
      "openReports": "Abiertos",
      "resolvedReports": "Resueltos",
      "dismissedReports": "Descartados",
      "allReports": "Todos",
      "allTypes": "Todos los Tipos",
      "reportTypeBusiness": "Negocio",
      "reportTypeEvent": "Evento",
      "reportTypeReview": "Reseña",
      "reportType_business": "Negocio",
      "reportType_event": "Evento",
      "reportType_review": "Reseña",
      "reportStatus_open": "Abierto",
      "reportStatus_resolved": "Resuelto",
      "reportStatus_dismissed": "Descartado",
      "reportedBy": "Reportado por",
      "reason": "Motivo",
      "adminNotes": "Notas del Admin",
      "resolveReport": "Resolver",
      "dismissReport": "Descartar",
      "editNotes": "Notas",
      "saveNotes": "Guardar",
      "reportResolved": "Reporte marcado como resuelto",
      "reportDismissed": "Reporte descartado",
      "notesUpdated": "Notas actualizadas",
      "loadingReports": "Cargando reportes...",
      "noReportsFound": "No se encontraron reportes",

      // Payment Pages
      "paymentVerifying": "Verificando Pago...",
      "paymentVerifyingDesc": "Por favor espera mientras confirmamos tu pago con Stripe.",
      "paymentChecking": "Verificando... ({{current}}/{{total}})",
      "paymentSuccessTitle": "Pago Exitoso!",
      "paymentSuccessDesc": "Tu negocio ha sido registrado exitosamente.",
      "paymentNextSteps": "Próximos pasos:",
      "paymentNextStep1": "Tu suscripción está activa — tu anuncio ya está publicado",
      "paymentNextStep2": "Tu listado está pendiente de aprobación",
      "paymentNextStep3": "Se te notificará una vez aprobado",
      "goToDashboard": "Ir al Panel",
      "paymentExpiredTitle": "Sesión Expirada",
      "paymentErrorTitle": "Problema con la Verificación del Pago",
      "paymentExpiredDesc": "Tu sesión de pago ha expirado. Por favor intenta registrarte de nuevo.",
      "paymentErrorDesc": "No pudimos verificar tu pago en este momento. Tu cuenta ha sido creada — inicia sesión y revisa tu panel.",
      "goHome": "Ir al Inicio",
      "paymentCancelledTitle": "Pago Cancelado",
      "paymentCancelledDesc": "Tu pago no se completó. Tu cuenta ha sido creada pero el pago es necesario para activar tu listado de negocio.",
      "paymentCancelledNote": "Puedes iniciar sesión en tu cuenta y completar el pago más tarde desde tu panel de negocio.",
      "signInToCompletePayment": "Iniciar Sesión para Completar el Pago",

      // Seed Listings
      "seedListing": "Semilla",
      "unclaimed": "Sin Reclamar",
      "seedPlan": "Fuente",
      "allSources": "Todas las Fuentes",
      "seedOnly": "Listados Semilla",
      "paidOnly": "Listados Pagados",

      // Feature labels (DB values)
      "feature": {
        "solo": "Individual",
        "couple": "Pareja (2)",
        "smallGroup": "Grupo Peque\u00f1o (3-5)",
        "mediumGroup": "Grupo Mediano (6-10)",
        "largeGroup": "Grupo Grande (10+)",
        "vegan": "Vegano",
        "vegetarian": "Vegetariano",
        "glutenFree": "Sin Gluten",
        "halal": "Halal",
        "kosher": "Kosher",
        "dairyFree": "Sin L\u00e1cteos",
        "nutFree": "Sin Frutos Secos",
        "eventMusic": "M\u00fasica",
        "eventFoodDrink": "Comida y Bebida",
        "eventWorkshop": "Taller",
        "eventFamilyFriendly": "Familiar",
        "eventArtsCulture": "Arte y Cultura",
        "eventSports": "Deportes",
        "eventNightlife": "Vida Nocturna",
        "eventOther": "Otro",
        "indoor": "Interior",
        "outdoor": "Exterior",
        "petFriendly": "Admite Mascotas",
        "kidFriendly": "Apto para Ni\u00f1os",
        "adultsOnly": "Solo Adultos (18+)",
        "hasSpecialOffers": "Tiene Ofertas Especiales",
        "parkingAvailable": "Aparcamiento Disponible",
        "wheelchairAccess": "Acceso para Sillas de Ruedas",
        "disabledToilets": "Ba\u00f1os para Discapacitados",
        "hasToilets": "Tiene Ba\u00f1os"
      },

      // Section headings
      "featuresAmenities": "Caracter\u00edsticas y Comodidades",
      "noFeaturesYet": "A\u00fan no se han a\u00f1adido caracter\u00edsticas o servicios.",
      "addFeatures": "A\u00f1adir Caracter\u00edsticas y Servicios",
      "featureEditHint": "Haz clic en los elementos para a\u00f1adirlos o eliminarlos. Guarda cuando termines.",
      "visitWebsite": "Visitar Sitio Web",

      // Business Claim Workflow
      "claimBanner": "\u00bfEres el due\u00f1o de este negocio? Recl\u00e1malo en Find-2-Day",
      "claimThisBusiness": "Reclamar Este Negocio",
      "claimFormTitle": "Reclamar Este Negocio",
      "claimFormDesc": "Env\u00eda una solicitud de reclamo y nuestro equipo verificar\u00e1 tu propiedad.",
      "claimMessage": "Mensaje (opcional)",
      "claimMessagePlaceholder": "Cu\u00e9ntanos c\u00f3mo est\u00e1s conectado con este negocio...",
      "submitClaim": "Enviar Reclamo",
      "submittingClaim": "Enviando...",
      "claimSubmitted": "Reclamo Enviado",
      "claimSubmittedDesc": "Nuestro equipo revisar\u00e1 tu reclamo y te responder\u00e1.",
      "claimAlreadyPending": "Ya tienes un reclamo pendiente para este negocio",
      "claimAlreadyClaimed": "Este negocio ya ha sido reclamado",
      "claimError": "Error al enviar el reclamo. Int\u00e9ntalo de nuevo.",

      "favourite": "Seguir",
      "favourited": "Siguiendo",
      "followAlerts": "Recibir Alertas",
      "following": "Alertas Activas",
      "enableNotifications": "Activar Notificaciones",
      "enabling": "Activando...",
      "notificationSettings": "Configuraci\u00f3n de Notificaciones",
      "notificationPromptDesc": "Recibe notificaciones de nuevos eventos y ofertas de los negocios que sigues y de toda Almer\u00eda.",
      "areaAlerts": "Alertas de Zona",
      "areaAlertsDesc": "Recibe notificaciones de todos los nuevos eventos y ofertas en estas zonas.",
      "followingCount": "Alertas activas para {{count}} listados",
      "favouritesCount": "{{count}} favoritos guardados",
      "notificationUnavailable": "Las notificaciones push no están disponibles en este dispositivo o navegador.",
      "notificationUnavailableHint": "Prueba a usar Chrome en Android o Safari en iOS. Asegúrate de que las notificaciones no están bloqueadas para este sitio.",
      "notificationEnableFailed": "No se pudieron activar las notificaciones. Comprueba que tu navegador permite notificaciones para este sitio.",
      "notifIOSTitle": "Añade a la pantalla de inicio para activar notificaciones",
      "notifIOSStep1": "Pulsa el botón Compartir",
      "notifIOSStep1b": "en la parte inferior de Safari",
      "notifIOSStep2": "Desplázate y pulsa \"Añadir a pantalla de inicio\"",
      "notifIOSStep3": "Abre la app desde tu pantalla de inicio y vuelve aquí",
      "notifIOSUseSafari": "Abre este sitio en Safari primero, luego añádelo a tu pantalla de inicio.",
      "notifIOSStandalone": "Las notificaciones requieren iOS 16.4 o superior. Actualiza tu dispositivo si es posible.",
      "notifIOSShort": "Añade esta app a tu pantalla de inicio para activar notificaciones.",
      "notifAndroidChrome": "Abre este sitio en Chrome para activar notificaciones.",
      "notifAndroidChromeHint": "Copia la URL y pégala en Google Chrome.",
      "notifAndroidShort": "Abre en Chrome para activar notificaciones.",
      "notifGeneric": "Las notificaciones push no son compatibles con este navegador.",
      "notifGenericHint": "Prueba Chrome en Android o Safari en iPhone.",
      "notifGenericShort": "Prueba Chrome (Android) o Safari (iPhone) para notificaciones.",
      "claimStatusPending": "Tu reclamo est\u00e1 pendiente de revisi\u00f3n",
      "claimStatusApproved": "\u00a1Tu reclamo ha sido aprobado!",
      "claimStatusRejected": "Tu reclamo no fue aprobado",
      "loginToClaim": "Inicia sesi\u00f3n para reclamar este negocio",
      "claimManagement": "Reclamos",
      "pendingClaims": "Reclamos Pendientes",
      "approvedClaims": "Reclamos Aprobados",
      "rejectedClaims": "Reclamos Rechazados",
      "allClaims": "Todos los Reclamos",
      "noClaims": "No se encontraron reclamos",
      "loadingClaims": "Cargando reclamos...",
      "approveClaim": "Aprobar Reclamo",
      "rejectClaim": "Rechazar Reclamo",
      "approveClaimDesc": "Esto vincular\u00e1 el negocio a la cuenta del reclamante y rechazar\u00e1 otros reclamos pendientes.",
      "rejectClaimDesc": "El listado permanecer\u00e1 sin reclamar.",
      "claimantName": "Reclamante",
      "claimantEmail": "Correo",
      "claimFor": "Negocio",
      "claimMessage_label": "Mensaje",

      // Enhanced claim form
      "claimStepDetails": "Datos",
      "claimStepVerify": "Verificar Email",
      "claimStepProof": "Prueba y Enviar",
      "claimFullName": "Nombre Completo",
      "claimFullNamePlaceholder": "Tu nombre completo",
      "claimRole": "Cargo",
      "claimRoleOwner": "Propietario",
      "claimRoleManager": "Gerente",
      "claimRoleRep": "Representante Autorizado",
      "claimRoleAuthorized_rep": "Rep. Autorizado",
      "claimPhone": "Teléfono",
      "claimMessageLabel": "Notas Adicionales",
      "claimNext": "Continuar",
      "claimBack": "Atrás",
      "claimSkipVerification": "Saltar por Ahora",
      "claimNameRequired": "El nombre es obligatorio",
      "claimPhoneRequired": "El teléfono es obligatorio",
      "claimFillRequired": "Completa tu nombre y teléfono",
      "claimMaxFiles": "Máximo 3 archivos permitidos",
      "claimUploadFailed": "Error al subir archivo. Inténtalo de nuevo.",
      "claimEmailVerifyTitle": "Verifica Tu Acceso a Este Negocio",
      "claimEmailVerifyDesc": "Enviaremos un código de 6 dígitos al email registrado del negocio. Ingresarlo demuestra que tienes acceso.",
      "claimSendCode": "Enviar Código",
      "claimResendCode": "Reenviar",
      "claimSendingCode": "Enviando...",
      "claimCodeSentTo": "Código enviado a",
      "claimCodeSentCheck": "Revisa tu bandeja",
      "claimVerifyBtn": "Verificar",
      "claimVerifySendFailed": "No se pudo enviar el email de verificación",
      "claimVerifyFailed": "Verificación fallida. Revisa el código e intenta de nuevo.",
      "claimEmailVerified": "Email verificado con éxito",
      "claimEmailVerifyRequired": "Verifica tu email antes de enviar la reclamación",
      "claimYourEmail": "Tu Email",
      "claimAutoApproved": "¡Reclamación Aprobada!",
      "claimAutoApprovedDesc": "Tus datos coinciden con nuestros registros. Ya tienes acceso completo para gestionar este negocio.",
      "claimProofTitle": "Documentos de Apoyo",
      "claimProofDesc": "Sube documentos como licencia comercial, factura de servicios, NIF o captura de Google Business. (Máx 3 archivos, 5MB cada uno)",
      "claimProofHint": "Licencia comercial, factura de servicios o captura de Google Business",
      "claimEmailRequired": "Ingresa una dirección de email válida",
      "optional": "opcional",
      "claimUploadFile": "Subir Documento",
      "claimUploading": "Subiendo...",
      "claimSummary": "Resumen de Reclamación",
      "claimEmailStatus": "Verificación de Email",
      "claimVerifiedBadge": "Verificado",
      "claimNotVerified": "No verificado",
      "claimProofDocs": "Documentos de Prueba",
      "claimProofDoc": "Documento",
      "claimSubmitFinal": "Enviar Reclamación",
      "claimEmailVerifiedBadge": "Email Verificado",
      "claimEmailMatchBadge": "Email Coincide",
      "claimPhoneMatchBadge": "Teléfono Coincide",
      "claimAutoApprovedBadge": "Auto-Aprobado",
      "claimantDetails": "Reclamante",
      "listingDetails": "Datos del Listado",

      // Claim visibility & landing page
      "alreadyListedClaim": "Ya apareces? Reclama tu negocio",
      "unclaimedListing": "Sin reclamar",
      "unclaimedContactNotice": "Este negocio no ha sido reclamado. Algunos datos de contacto son limitados.",
      "claimToUnlock": "Reclama este negocio para desbloquear todos los detalles y recibir consultas",
      "verifiedOwner": "Propietario Verificado",
      "claimTopBannerText": "Es tu negocio? Reclámalo ahora y empieza a recibir clientes hoy",
      "claimNowBtn": "Reclamar Ahora",
      "signInToClaim": "Inicia Sesión para Reclamar",
      "claimPageTitle": "Reclama tu Negocio",
      "claimPageSubtitle": "Tu negocio ya aparece en Find-2-Day. Toma el control, actualiza tus datos y conecta con nuevos clientes.",
      "claimFindMyBusiness": "Buscar Mi Negocio",
      "claimHowItWorks": "Cómo Funciona",
      "claimStep1Title": "Encuentra tu Negocio",
      "claimStep1Desc": "Navega nuestro directorio y localiza tu negocio en Find-2-Day.",
      "claimStep2Title": "Envía tu Reclamo",
      "claimStep2Desc": "Haz clic en 'Reclamar Este Negocio' y cuéntanos tu conexión.",
      "claimStep3Title": "Verificación",
      "claimStep3Desc": "Nuestro equipo revisa tu reclamo y obtienes control total de tu listado.",
      "claimWhyClaim": "Por qué Reclamar tu Negocio?",
      "claimBenefit1": "Aparece más alto en los resultados y llega a más clientes locales",
      "claimBenefit2": "Actualiza tus fotos, horarios, menú y datos de contacto cuando quieras",
      "claimBenefit3": "Responde a reseñas y genera confianza en tu comunidad",
      "claimBenefit4": "Publica eventos y ofertas especiales para atraer nuevos visitantes",
      "claimCtaTitle": "Listo para Tomar el Control?",
      "claimCtaDesc": "Únete a cientos de negocios locales que ya están creciendo con Find-2-Day.",
      "claimCreateAccount": "Crear una Cuenta",
      "claimNeedHelp": "No encuentras tu negocio o necesitas ayuda?",
      "claimSearchTitle": "Busca tu Negocio",
      "claimSearchPlaceholder": "Escribe el nombre de tu negocio...",
      "claimSearchBtn": "Buscar",
      "claimSearchNoResults": "No se encontraron negocios sin reclamar con ese nombre. Prueba otra búsqueda o explora todos los listados.",
      "claimCantFind": "¿No encuentras tu negocio?",
      "claimListYourselfDesc": "Publica tu negocio directamente en Find-2-Day y crea tu perfil de empresa.",
      "claimListMyBusinessBtn": "Publicar Mi Negocio",

      // PWA Install Prompt
      "pwaInstallTitle": "Instala Find-2-Day",
      "pwaInstallDesc": "Obtén la experiencia completa con notificaciones",
      "pwaInstallBtn": "Instalar",
      "pwaInstallTapHint": "Toca aquí para ver cómo",
      "pwaInstallDesktopHint": "Abre esta página en tu móvil para instalarla.",
      "pwaHelpIOSTitle": "Instalar en iPhone",
      "pwaHelpIOSIntro": "En el iPhone, las webapps se añaden a la pantalla de inicio desde el menú compartir de Safari. Sigue estos tres pasos:",
      "pwaHelpIOSStep1": "Toca el botón Compartir en la parte inferior de Safari (el cuadrado con una flecha hacia arriba).",
      "pwaHelpIOSStep2": "Desplázate en la hoja de compartir y pulsa \"Añadir a pantalla de inicio\".",
      "pwaHelpIOSStep3": "Toca \"Añadir\" en la esquina superior derecha. Find-2-Day aparecerá en tu pantalla de inicio como una app normal.",
      "pwaHelpIOSNote": "\"Añadir a pantalla de inicio\" solo funciona dentro de Safari. Si usas Chrome u otro navegador en iPhone, abre esta página primero en Safari.",
      "pwaHelpAndroidTitle": "Instalar en Android",
      "pwaHelpAndroidIntro": "Si la ventana de instalación no apareció automáticamente, puedes instalar Find-2-Day desde el menú del navegador:",
      "pwaHelpAndroidStep1": "Toca el botón de menú (tres puntos) en la esquina superior derecha de Chrome.",
      "pwaHelpAndroidStep2": "Elige \"Instalar aplicación\" o \"Añadir a pantalla de inicio\" en el menú.",
      "pwaHelpAndroidStep3": "Confirma para añadir el icono a tu pantalla de inicio.",
      "pwaHelpDesktopTitle": "Instalar Find-2-Day",
      "pwaHelpDesktopIntro": "Instalar Find-2-Day es más rápido desde un móvil, pero también puedes instalarlo en tu ordenador con Chrome o Edge:",
      "pwaHelpDesktopStep1": "Busca el icono de instalación en la barra de direcciones (un monitor con una flecha hacia abajo) o abre el menú del navegador.",
      "pwaHelpDesktopStep2": "Elige \"Instalar Find-2-Day\" — la web se abrirá como su propia ventana de escritorio.",
      "pwaHelpDesktopNote": "Firefox y Safari de escritorio no son compatibles con apps web instalables. Para la experiencia completa, abre esta página en tu móvil.",
      "pwaHelpGotIt": "Entendido",
      "pwaDismissAria": "Cerrar aviso de instalación",
      "pwaIOSInstructions": "Añade Find-2-Day a tu pantalla de inicio para la mejor experiencia:",
      "pwaIOSStep1": "Toca Compartir",
      "pwaIOSStep2": "Añadir a Inicio",
      "pwaAndroidInstructions": "Toca el menú (tres puntos) y selecciona \"Añadir a pantalla de inicio\"",

      // What's New Modal
      "whatsNew_title": "Novedades",
      "whatsNew_item1": "Recibe alertas de tus negocios favoritos — nuevos eventos, ofertas y más",
      "whatsNew_item2": "Funciona en todos los dispositivos — notificaciones push y respaldo por email",
      "whatsNew_cta": "Recibir Alertas de Eventos y Ofertas",

      // Follow Engagement
      "followPromptTitle": "Recibe notificaciones de eventos y ofertas cerca de ti",
      "followPromptDesc": "Toca 'Recibir Alertas' en cualquier listado para estar al día",
      "firstFollowBoost": "Recibirás alertas cuando publiquen nuevos eventos u ofertas",
      "followEmptyTitle": "Recibe notificaciones de eventos y ofertas",
      "followEmptyDesc": "Toca 'Recibir Alertas' en cualquier listado para ser el primero en conocer sus eventos y ofertas",
      "followEmptyCta": "Explorar Listados",

      // Offers & Notification Controls
      "offers": "Ofertas",
      "createOffer": "Nueva Oferta",
      "newOffer": "Nueva Oferta",
      "offerTitle": "Título de la Oferta",
      "offerTitlePlaceholder": "ej. 20% de descuento solo esta semana",
      "offerDescription": "Descripción",
      "offerDescPlaceholder": "¿De qué trata la oferta?",
      "discountPercent": "Descuento %",
      "validUntil": "Válida hasta",
      "offerTitleRequired": "El título es obligatorio",
      "offerDescRequired": "La descripción es obligatoria",
      "offerValidUntilRequired": "La fecha de vencimiento es obligatoria",
      "offerCreatedSuccess": "¡Oferta publicada con éxito!",
      "offerDeletedSuccess": "Oferta eliminada",
      "publishOffer": "Publicar Oferta",
      "creatingOffer": "Publicando...",
      "loadingOffers": "Cargando ofertas...",
      "noOffersCreated": "Sin ofertas todavía — crea una para atraer clientes",
      "delete": "Eliminar",
      "notifyFollowers": "Notificar seguidores",
      "boostPost": "Impulsar esta publicación",
      "comingSoon": "Próximamente",

      // Post Analytics
      "analyticsViews": "vistas",
      "analyticsClicks": "clics",
      "sent": "enviados",

      // Public Offers & Events pages
      "activeOffers": "ofertas activas",
      "upcomingEvents": "eventos próximos",
      "expirestoday": "Expira hoy",
      "daysLeft": "días restantes",

      // Boost
      "boosted": "Destacado",
      "featured": "Destacado",
      "boostAfterCreate": "Impulsar después de crear",
      "boostModalDesc": "Impulsa esta publicación para llegar a más personas con notificaciones en toda el área:",
      "boostPayNow": "Pagar e Impulsar",
      "boostSecurePayment": "Pago seguro con Stripe",
      "redirecting": "Redirigiendo...",
      "boostActivating": "Activando tu impulso...",
      "boostActivatingDesc": "Espera mientras confirmamos tu pago",
      "boostActive": "¡Impulso Activado!",
      "boostActiveDesc": "Tu publicación ahora está destacada y enviada a todos los usuarios del área",
      "boostError": "Algo salió mal",
      "boostErrorDesc": "No pudimos verificar tu pago. Contacta con soporte.",
      "backToDashboard": "Volver al Panel",
      "previewBanner": "Así es como tu anuncio aparece al público",
      "draftPreviewBanner": "Vista previa — sin guardar. Vuelve atrás y pulsa Guardar para aplicar los cambios.",
      "closePreview": "Cerrar vista previa",
      "previewChanges": "Vista previa",
      "previewUnsavedHint": "Abre una vista previa de tus cambios sin guardar en una pestaña nueva",
      "previewMyListing": "Previsualizar mi anuncio",
      "previewYourListing": "Previsualiza tu anuncio",
      "previewYourListingHint": "Ve cómo se verá tu anuncio antes de pagar. Se abre en una nueva pestaña — aún no se guarda nada.",
      "waiverTitle": "Activación inmediata del anuncio y confirmación",
      "waiverLabel": "Al marcar esta casilla, solicito que mi anuncio se active inmediatamente una vez aprobado. Entiendo que, en cuanto mi anuncio sea visible públicamente en Find-2-Day, el servicio digital se considera entregado conforme a la Directiva UE 2011/83/UE, art. 16 m.",
      "waiverRequired": "Por favor confirma la activación inmediata del anuncio para continuar.",
      "waiverLegalNote": "Directiva UE 2011/83/UE, art. 16 m — servicio digital prestado de inmediato a petición del usuario.",
      "contactSupportBeforeChargeback": "¿Necesitas ayuda con tu suscripción más adelante? Escribe a",
      "contactSupportBeforeChargebackTail": "resolvemos reembolsos y problemas de facturación directamente, más rápido que un chargeback.",

      // Boost History
      "boostHistory": "Historial de Impulsos",
      "viewsBefore": "Antes del impulso",
      "viewsDuring": "Durante el impulso",
      "moreViewsDuringBoost": "más vistas durante el impulso",
      "topBoost": "Mejor impulso",
      "boostStatusActive": "Activo",
      "boostStatusEnded": "Finalizado",
      "boostAgain": "Impulsar de Nuevo",
      "boostAgainStrong": "Funcionó bien — impulsar de nuevo",

      "showingOf": "Mostrando {{from}}-{{to}} de {{total}}",
      "manageBilling": "Gestionar Facturación",
      "manageBillingDesc": "Actualiza métodos de pago, consulta facturas y gestiona tu suscripción.",
      "manageSubscription": "Gestionar Suscripción",
      "manageSubscriptionDesc": "Actualiza métodos de pago, consulta facturas, cambia o cancela tu plan.",

      // Reset Filters
      "resetFilters": "Restablecer Filtros",

      // Business Guide
      "businessGuideTitle": "Cómo Funciona — Tu Guía de Negocio",
      "guideSubscriptions": "Suscripciones",
      "guideSub6mo": "Plan de 6 Meses — 49 € / 6 meses (IVA incluido). Incluye eventos, ofertas y promociones destacadas.",
      "guideSub12mo": "Oferta Fundadora — GRATIS durante 12 meses. Incluye perfil completo, fotos, datos de contacto, eventos y ofertas.",
      "guideSubTrial": "Tu anuncio se publica en cuanto se confirma el pago — sin prueba, sin demora.",
      "guideSubInstant": "Tu anuncio se publica en cuanto se confirma el pago — sin prueba, sin demora.",
      "upgradeTitle": "Mejora al Plan de 12 Meses",
      "upgradeDesc": "Actualmente tienes un plan heredado de 6 meses. Ahora todas las empresas son gratuitas durante 12 meses con la Oferta Fundadora — actualiza en un clic para continuar sin pagar durante el periodo fundador.",
      "upgradeNow": "Mejorar Ahora",
      "guideEventsOffers": "Eventos y Ofertas",
      "guideEventLimit": "Puedes tener hasta 5 eventos activos a la vez. Archiva eventos anteriores para crear nuevos.",
      "guideOfferLimit": "Puedes tener hasta 5 ofertas activas a la vez. Elimina ofertas caducadas para añadir nuevas.",
      "guideEventApproval": "Los eventos y ofertas pasan por una revisión rápida del administrador antes de publicarse.",
      "guideBoosts": "Impulsos y Visibilidad",
      "guideBoostDesc": "Impulsa tus eventos u ofertas para aparecer en la parte superior de los listados y llegar a más personas.",
      "guideBoostTiers": "Elige entre diferentes duraciones de impulso para ajustarse a tu presupuesto y campaña.",
      "guideReviews": "Reseñas",
      "guideReviewsDesc": "Los usuarios pueden dejar reseñas en tu listado. Las buenas reseñas ayudan a atraer más clientes.",
      "guideBilling": "Facturación y Pagos",
      "guideBillingPortal": "Usa \"Gestionar Facturación\" para actualizar tu tarjeta, ver facturas o cambiar de plan.",
      "guideBillingUpgrade": "Puedes mejorar de 6 meses a 12 meses en cualquier momento a través del portal de facturación.",

      "offlineSync": {
        "offlineTitle": "Estás sin conexión",
        "offlineDesc": "Los cambios se guardarán y enviarán cuando te reconectes.",
        "queued": "{{count}} envío(s) pendiente(s) guardado(s)",
        "syncing": "Sincronizando envíos guardados...",
        "syncComplete": "{{count}} envío(s) sincronizado(s) correctamente",
        "stillPending": "{{count}} aún pendiente(s)",
        "failedTitle_one": "1 envío no se pudo completar",
        "failedTitle_other": "{{count}} envíos no se pudieron completar",
        "tapToRetry": "Toca para reintentar",
        "dismiss": "Descartar",
        "genericItem": "Elemento",
        "item": {
          "event": "Evento",
          "offer": "Oferta",
          "businessProfile": "Actualización del perfil",
          "review": "Reseña",
          "report": "Denuncia",
          "charity": "Solicitud de organización",
          "contact": "Mensaje de contacto"
        }
      },

      // Onboarding Popup
      "onboardingTitle": "¡Bienvenido a Find-2-Day!",
      "onboardingStep1Title": "Descubre Listados Locales",
      "onboardingStep1Desc": "Explora negocios, organizaciones, eventos y ofertas cerca de ti.",
      "onboardingStep2Title": "Guarda Tus Favoritos",
      "onboardingStep2Desc": "Toca el corazón para guardar listados que te gusten y encontrarlos rápidamente.",
      "onboardingStep3Title": "Recibe Notificaciones de Eventos y Ofertas",
      "onboardingStep3Desc": "Toca 'Recibir Alertas' en cualquier listado para recibir novedades al instante.",
      "onboardingGotIt": "¡Entendido!",
      "onboardingNext": "Siguiente",
      "onboardingSkip": "Saltar",
      "locationPicker": {
        "title": "Recibe alertas de tu zona",
        "intro": "Elige tu pueblo de Almería para recibir eventos, ofertas y alertas de seguridad cercanas.",
        "homeTownLabel": "Tu pueblo o localidad",
        "searchPlaceholder": "Buscar pueblos de Almería…",
        "noTowns": "Ningún pueblo coincide.",
        "nearbyLabel": "Avísame también de pueblos cercanos",
        "nearbyHint": "A {{km}} km de tu pueblo. Ajusta si lo deseas.",
        "radiusAria": "Radio cercano en kilómetros",
        "regionLabel": "Avísame de toda Almería",
        "regionHint": "Recibe eventos provinciales, grandes ofertas y alertas de seguridad.",
        "followedTownsLabel": "Sigue también estos pueblos (opcional)",
        "followedTownsHint": "Elige pueblos adicionales además del tuyo.",
        "followedTownsAddPlaceholder": "Añadir pueblo…",
        "removeTown": "Quitar",
        "skip": "Ahora no",
        "save": "Guardar mi zona",
        "saving": "Guardando…"
      },
      "maxActiveEventsReached": "Máximo 5 eventos activos. Archiva los existentes primero.",
      "maxActiveOffersReached": "Máximo 5 ofertas activas. Elimina las existentes primero.",

      // Charity / Community Listing
      "charity": {
        "formTitle": "Solicitud de Listado Comunitario y Benéfico",
        "formSubtitle": "Solicita un listado gratuito comunitario o benéfico para tu organización",
        "eligibleTitle": "Esta solicitud es para:",
        "eligibleItem1": "Organizaciones benéficas registradas",
        "eligibleItem2": "Organizaciones sin ánimo de lucro",
        "eligibleItem3": "Grupos comunitarios",
        "eligibleItem4": "Organizaciones de voluntariado",
        "notForBusinesses": "Esto no es para negocios comerciales. Si tienes un negocio, regístrate a través de nuestro registro estándar de negocios.",
        "orgDetailsTitle": "Datos de la Organización",
        "organisationName": "Nombre de la Organización",
        "organisationNamePlaceholder": "ej. Banco de Alimentos de Almería",
        "contactName": "Nombre de Contacto",
        "contactNamePlaceholder": "Nombre completo del contacto principal",
        "websiteSocial": "Sitio Web / Enlace de Redes Sociales",
        "aboutOrgTitle": "Sobre tu Organización",
        "orgType": "Tipo de Organización",
        "selectOrgType": "Selecciona el tipo de organización",
        "orgTypeCharity": "Organización Benéfica Registrada",
        "orgTypeNonProfit": "Organización Sin Ánimo de Lucro",
        "orgTypeCommunity": "Grupo Comunitario",
        "orgTypeVolunteer": "Organización de Voluntariado",
        "descriptionPlaceholder": "Describe qué hace tu organización, a quién ayuda y cuál es tu misión...",
        "areaServed": "Zona de Actuación",
        "areaServedPlaceholder": "ej. Almería, Andalucía",
        "registrationNumber": "Número de Registro (si aplica)",
        "registrationNumberPlaceholder": "ej. G12345678",
        "proofTitle": "Prueba de la Organización",
        "proofDesc": "Sube un documento que acredite que tu organización es benéfica, sin ánimo de lucro o comunitaria. Solo será visible para nuestro equipo.",
        "proofUploadLabel": "Haz clic para subir o arrastra y suelta",
        "proofFileTypes": "PDF, JPG, PNG o DOC (máx 10MB)",
        "proofRequired": "Por favor, sube una prueba de tu organización",
        "fileTooLarge": "El archivo es demasiado grande. El tamaño máximo es 10MB.",
        "declarationText": "Confirmo que esta organización no tiene ánimo de lucro y que la información proporcionada en esta solicitud es precisa y veraz. Entiendo que proporcionar información falsa puede resultar en la eliminación de la plataforma.",
        "declarationRequired": "Debes aceptar la declaración para enviar",
        "submitApplication": "Enviar Solicitud",
        "submitError": "Error al enviar la solicitud. Inténtalo de nuevo.",
        "submittedTitle": "Solicitud Enviada",
        "submittedDesc": "Gracias por tu solicitud. Nuestro equipo revisará tu solicitud y documentación. Te notificaremos por email cuando se tome una decisión.",
        "communityListing": "Listado Comunitario y Benéfico",
        "nonProfit": "Sin Ánimo de Lucro",
        "charityBadge": "Comunitario y Benéfico",
        "applyForListing": "Solicitar Listado Comunitario o Benéfico Gratuito",
        "charityManagement": "Comunidad y Beneficencia",
        "pendingApplications": "Pendientes",
        "approvedApplications": "Aprobadas",
        "rejectedApplications": "Rechazadas",
        "needsInfoApplications": "Info Necesaria",
        "allApplications": "Todas",
        "noApplications": "No se encontraron solicitudes",
        "loadingApplications": "Cargando solicitudes...",
        "approveApplication": "Aprobar",
        "rejectApplication": "Rechazar",
        "needsMoreInfo": "Info Necesaria",
        "viewProof": "Ver Prueba",
        "downloadProof": "Descargar Prueba",
        "orgTypeLabel": "Tipo de Organización",
        "areaServedLabel": "Zona de Actuación",
        "regNumberLabel": "Número de Registro",
        "convertToCharity": "Convertir a Benéfica",
        "convertToStandard": "Convertir a Estándar",
        "charityFilter": "Comunitario y Benéfico",
        "filterAll": "Todos los Listados",
        "filterCharity": "Solo Comunitarios",
        "filterStandard": "Solo Negocios"
      },

      // Classifieds / Sell / Lost / Found
      "marketplace": {
        "tabSell": "Vender", "tabWanted": "Se busca", "tabSwap": "Intercambio", "tabFree": "Gratis",
        "hintMarketTitle": "Comprar, vender, intercambiar o regalar",
        "swapTitle": "SWAP-2-DAY", "swapSubtitle": "¿Qué tienes? ¿Por qué lo intercambiarías?",
        "swapLabel": "Intercambio", "orSwap": "O INTERCAMBIO"
      },
      "swap": {
        "moneyNone": "Ninguno", "moneyBtoA": "El ofertante paga al propietario €{n}", "moneyAtoB": "El propietario paga al ofertante €{n}",
        "ownerProvides": "El propietario aporta", "offererProvides": "El ofertante aporta", "money": "Dinero adicional",
        "negotiation": "Negociación de intercambio", "youAre": "Eres el/la", "owner": "propietario del anuncio", "offerer": "persona que hace la oferta",
        "agreed": "INTERCAMBIO ACORDADO", "agreedAt": "Acordado el", "finalTerms": "Términos finales acordados", "currentTerms": "Términos actuales",
        "confirmed": "confirmado ✓", "notConfirmed": "todavía no", "ownerLabel": "Propietario", "offererLabel": "Ofertante",
        "iAgree": "Acepto este intercambio", "youConfirmed": "Has confirmado — esperando a la otra persona",
        "counter": "Contraoferta", "decline": "Rechazar", "withdraw": "Retirar mi oferta", "cancel": "Cancelar esta negociación",
        "history": "Historial de ofertas", "makeCounter": "Hacer una contraoferta", "commaSep": "separados por comas",
        "moneyAmount": "Dinero (€)", "moneyDir": "Sentido del pago", "dirNone": "Sin dinero", "dirBtoA": "El ofertante paga al propietario", "dirAtoB": "El propietario paga al ofertante",
        "notePlaceholder": "Nota opcional…", "sendCounter": "Enviar contraoferta", "backToMarket": "Volver al Mercado",
        "loadFail": "No se pudo cargar este intercambio.", "actionFail": "La acción falló.",
        "ownerGives": "El propietario entrega", "describeYours": "Describe lo que darías a cambio (también puedes añadir dinero).",
        "itemsPlaceholder": "p. ej. Cortacésped, Desbrozadora (separados por comas)", "sendOffer": "Enviar oferta de intercambio", "createFail": "No se pudo enviar la oferta.",
        "errName": "Introduce tu nombre.", "errEmail": "Introduce un correo válido.", "errItems": "Describe al menos un artículo que ofreces."
      },
      "classifieds": {
        "listingType": "Tipo de anuncio", "typeSellOnly": "Solo vender", "typeSwapOnly": "Solo intercambiar", "typeSellOrSwap": "Vender o intercambiar", "typeFree": "Gratis", "typeWanted": "Se busca",
        "swapWantedTitle": "¿Qué te gustaría a cambio?", "swapAddOwn": "Añade el tuyo…", "openToOffers": "Abierto a ofertas",
        "swapPrefsHint": "Estas son solo preferencias — otras personas pueden ofrecer algo diferente.", "sellSwapPriceHint": "Los compradores pueden comprar a este precio O hacer una oferta de intercambio.",
        "errSellSwapPrice": "Introduce un precio de venta para un anuncio de «Vender o intercambiar»",
        "buyFor": "Comprar por", "swapWantedShort": "Intercambio buscado", "swapOpenAny": "abierto a ideas", "otherOffersConsidered": "Se consideran otras ofertas",
        "contactToBuy": "Contactar para comprar", "makeSwapOffer": "Hacer una oferta de intercambio", "statusSwapped": "¡Intercambiado!",
        "swappedNote": "Los anuncios intercambiados se ocultan de la búsqueda. Tu registro se conserva para ti.",
        "editTypePrice": "Cambiar tipo de anuncio / precio (p. ej. vender también este artículo)", "editSwapNote": "Los compradores verán «€… O INTERCAMBIO» y podrán hacer una oferta de intercambio. Tus fotos, descripción e ID del anuncio no cambian.",
        "offersReceived": "Ofertas de intercambio recibidas", "noOffersYet": "Aún no hay ofertas de intercambio.", "errSave": "No se pudieron guardar los cambios.",
        "title": "Vender / Perdidos / Encontrados",
        "sellTab": "Vender",
        "hintCardTitle": "¿Dónde debo publicar esto?",
        "hintCardSell": "Vender un artículo",
        "hintCardSellDesc": "Usa la pestaña Vender abajo",
        "hintCardLostFound": "Perdiste o encontraste algo",
        "hintCardLostFoundDesc": "Usa la pestaña Perdidos y Encontrados",
        "hintCardPetSafety": "Mascota perdida o problema de seguridad",
        "hintCardPetSafetyDesc": "Usa el Centro de Seguridad Comunitaria",
        "searchPlaceholder": "Buscar artículos en venta...",
        "postItem": "Publicar un Artículo",
        "noItems": "No se encontraron artículos",
        "tryDifferent": "Prueba a ajustar tu búsqueda o filtros",
        "beFirst": "¡Sé el primero en publicar algo en venta!",
        "itemFound": "artículo",
        "itemsFound": "artículos",
        "negotiable": "Negociable",
        "sold": "Vendido",
        "description": "Descripción",
        "contactSeller": "Contactar al Vendedor",
        "expiresOn": "Este anuncio caduca el",
        "ownerActions": "Tienes acceso de propietario a este anuncio.",
        "markSold": "Marcar como Vendido",
        "delete": "Eliminar",
        "deleteListing": "Eliminar Anuncio",
        "alreadySold": "Este artículo ha sido marcado como vendido.",
        "confirmSold": "¿Marcar como Vendido?",
        "confirmSoldDesc": "Esto marcará tu anuncio como vendido. Los compradores verán que ya no está disponible.",
        "confirmDelete": "¿Eliminar Anuncio?",
        "confirmDeleteDesc": "Esto eliminará permanentemente tu anuncio. Esta acción no se puede deshacer.",
        "yesSold": "Sí, Vendido",
        "yesDelete": "Sí, Eliminar",
        "marking": "Marcando...",
        "deleting": "Eliminando...",
        "errorMarkSold": "No se pudo marcar como vendido. Comprueba tu enlace de edición.",
        "errorDelete": "No se pudo eliminar el anuncio.",
        "posted": "¡Artículo Publicado!",
        "postedDesc": "Tu anuncio ya está activo. Caducará automáticamente después de 30 días.",
        "saveLink": "Guarda este enlace para gestionar tu anuncio:",
        "viewListing": "Ver Anuncio",
        "browseMore": "Ver Más",
        "photos": "Fotos",
        "itemTitle": "Título",
        "titlePlaceholder": "¿Qué estás vendiendo?",
        "category": "Categoría",
        "price": "Precio",
        "optional": "opcional",
        "priceHint": "Déjalo vacío para \"Negociable\"",
        "descLabel": "Descripción",
        "descPlaceholder": "Describe el artículo, estado, características...",
        "location": "Ubicación",
        "locationPlaceholder": "ej. Almería Centro, Retamar...",
        "contactInfo": "Tus Datos de Contacto",
        "yourName": "Nombre",
        "namePlaceholder": "Tu nombre",
        "yourEmail": "Email",
        "emailPlaceholder": "tu@email.com",
        "yourPhone": "Teléfono",
        "addPhoto": "Añadir",
        "maxPhotos": "Máximo 6 fotos permitidas",
        "errTitle": "El título debe tener al menos 3 caracteres",
        "errDesc": "La descripción debe tener al menos 10 caracteres",
        "errCategory": "Por favor selecciona una categoría",
        "errName": "Por favor introduce tu nombre",
        "errEmail": "Por favor introduce un email válido",
        "errLocation": "Por favor introduce una ubicación",
        "errSubmit": "Error al publicar el anuncio. Inténtalo de nuevo.",
        "posting": "Publicando...",
        "postForFree": "Publicar Gratis",
        "autoExpire": "Los anuncios caducan automáticamente después de 30 días",
        "cat": {
          "Tools": "Herramientas",
          "Furniture": "Muebles",
          "Cars & Vehicles": "Coches y Vehículos",
          "Electronics": "Electrónica",
          "Property": "Inmuebles",
          "Clothing & Fashion": "Ropa y Moda",
          "Home & Garden": "Hogar y Jardín",
          "Sports & Leisure": "Deportes y Ocio",
          "Baby & Kids": "Bebés y Niños",
          "General": "General"
        },
        "statusAvailable": "Disponible",
        "statusPending": "Pendiente de Recogida",
        "statusSold": "Vendido",
        "changeStatus": "Cambiar Estado",
        "soldAutoRemove": "Los artículos vendidos se ocultan y se eliminan automáticamente después de 3 días.",
        "errorStatus": "No se pudo actualizar el estado.",
        "emailSent": "Enlace de gestión enviado a tu email",
        "emailSentDesc": "Revisa tu bandeja de entrada para el enlace de edición, cambio de estado o eliminación.",
        "manageHelp": "Con tu enlace de gestión puedes:",
        "manageEdit": "Editar los detalles del anuncio",
        "managePending": "Establecer estado a Pendiente de Recogida",
        "manageSold": "Marcar como Vendido (oculto después de 3 días)",
        "manageDelete": "Eliminar el anuncio",
        "manageListing": "Gestionar Anuncio",
        "manageYourListing": "Gestionar tu Anuncio",
        "resendLink": "¿Perdiste tu enlace de gestión?",
        "resendTitle": "Obtener tus enlaces de gestión",
        "resendDesc": "Introduce el email que usaste al publicar. Te reenviaremos los enlaces.",
        "resendEmailPlaceholder": "tu@email.com",
        "resendBtn": "Enviar",
        "resendSent": "¡Revisa tu email!",
        "resendSentDesc": "Si tienes anuncios activos, hemos enviado los enlaces de gestión a tu email.",
        "resendErrEmail": "Por favor introduce un email válido",
        "resendError": "Algo salió mal. Inténtalo de nuevo."
      },

      // Nearest Me
      "nearestMe": {
        "button": "Cerca de mí",
        "notSupported": "Tu navegador no soporta geolocalización",
        "denied": "Acceso a ubicación denegado. Puedes buscar por ciudad.",
        "error": "No se pudo obtener tu ubicación. Inténtalo de nuevo.",
        "townPlaceholder": "Escribe una ciudad, p. ej. Lyon, Francia",
        "useTown": "Usar ciudad",
        "townNotFound": "No encontramos esa ciudad. Prueba con una ciudad cercana más grande o añade el país."
      },

      // Lost & Found
      "lostFound": {
        "title": "Objetos Perdidos",
        "searchPlaceholder": "Buscar objetos perdidos y encontrados...",
        "postItem": "Publicar objeto perdido o encontrado",
        "noItems": "No se encontraron objetos",
        "beFirst": "Publica un objeto perdido o encontrado para ayudar a tu comunidad.",
        "lost": "Perdido",
        "found": "Encontrado",
        "lostOn": "Perdido",
        "foundOn": "Encontrado",
        "resolved": "Resuelto",
        "resolvedDesc": "Este objeto ha sido marcado como resuelto.",
        "markResolved": "Marcar como Resuelto",
        "errorResolve": "No se pudo marcar como resuelto.",
        "contactPerson": "Contacto",
        "messageOnWhatsApp": "Enviar mensaje por WhatsApp",
        "whatsappMessage": "Hola, vi tu publicación de Objetos Perdidos en Find-2-Day sobre {{title}}. ¿Sigue disponible / puedo recogerlo?",
        "typeLabel": "¿Este objeto es perdido o encontrado?",
        "iLostSomething": "He Perdido Algo",
        "iFoundSomething": "He Encontrado Algo",
        "itemTitle": "¿Qué se perdió/encontró?",
        "titlePlaceholder": "ej. Perro pequeño marrón, llaves de coche...",
        "category": "Categoría",
        "dateLost": "Fecha de pérdida",
        "dateFound": "Fecha de hallazgo",
        "descPlaceholder": "Describe el objeto, dónde se perdió/encontró, características...",
        "locationLabel": "Zona / Ubicación",
        "locationPlaceholder": "ej. Almería Centro, Playa de Retamar...",
        "posted": "¡Publicado!",
        "postedDesc": "Tu publicación está activa. Caduca después de 30 días.",
        "viewItem": "Ver Objeto",
        "browseMore": "Ver Más",
        "postForFree": "Publicar Gratis",
        "errTitle": "El título debe tener al menos 3 caracteres",
        "errDesc": "La descripción debe tener al menos 10 caracteres",
        "errCategory": "Por favor selecciona una categoría",
        "errName": "Por favor introduce tu nombre",
        "errEmail": "Por favor introduce un email válido",
        "errLocation": "Por favor introduce una ubicación",
        "errSubmit": "Error al publicar. Inténtalo de nuevo.",
        "cat": {
          "Pets": "Mascotas",
          "Keys": "Llaves",
          "Wallets/Bags": "Carteras/Bolsos",
          "Documents/ID": "Documentos/DNI",
          "Phones/Electronics": "Teléfonos/Electrónica",
          "Other": "Otros"
        }
      },

      // Saved Search Alerts
      "savedSearch": {
        "setAlert": "Crear alerta de búsqueda",
        "title": "Alerta de Búsqueda",
        "desc": "Recibe notificaciones cuando se publiquen artículos que coincidan con tu búsqueda.",
        "queryPlaceholder": "ej. sofá, bicicleta, iPhone...",
        "anyCategory": "Cualquier categoría",
        "emailPlaceholder": "Tu email para notificaciones",
        "save": "Guardar Alerta",
        "saving": "Guardando...",
        "saved": "¡Alerta guardada!",
        "savedDesc": "Te enviaremos un email cuando se publiquen artículos que coincidan con tu búsqueda.",
        "errQuery": "El término de búsqueda debe tener al menos 2 caracteres",
        "errEmail": "Por favor introduce un email válido",
        "errSave": "Error al guardar la alerta de búsqueda",
        "unsubscribed": "Dado de baja",
        "unsubscribedDesc": "Ya no recibirás notificaciones para esta búsqueda.",
        "unsubscribeError": "No se pudo dar de baja",
        "unsubscribeErrorDesc": "Este enlace puede haber expirado o ya ha sido utilizado.",
        "backToClassifieds": "Ver En Venta"
      },

      // Emergency numbers
      "emergency": {
        "title": "Números de Emergencia",
        "subtitle": "Región de Almería — pulsa cualquier número para llamar",
        "disclaimerTitle": "En caso de emergencia vital:",
        "disclaimerBody": "Esta página es informativa y puede no reflejar los últimos cambios oficiales de contacto.",
        "callNow": "llama al 112 ahora",
        "tapToCall": "Pulsa para llamar",
        "footerNote": "Números revisados por última vez en febrero de 2026. Verifica siempre la información crítica con fuentes oficiales.",
        "euEmergency": "Emergencias (UE)",
        "euEmergencyDesc": "Número único para policía, ambulancia y bomberos en toda la UE",
        "nationalPolice": "Policía Nacional",
        "nationalPoliceDesc": "Delitos graves, documentación, DNI, pasaporte",
        "guardiaCivil": "Guardia Civil",
        "guardiaCivilDesc": "Zonas rurales, carreteras y fronteras",
        "localPolice": "Policía Local",
        "localPoliceDesc": "Incidentes locales, aparcamiento, asuntos municipales",
        "medical": "Emergencia Sanitaria",
        "medicalDesc": "Ambulancia y servicios urgentes de salud",
        "fire": "Bomberos",
        "fireDesc": "Incendios, rescates, inundaciones",
        "dvSupport": "Violencia de Género",
        "dvSupportDesc": "Línea de ayuda 24/7 — gratuita y confidencial, no aparece en la factura",
        "torrecardenas": "Hospital Torrecárdenas",
        "torrecardenasDesc": "Hospital público principal de Almería",
        "ukConsulate": "Consulado Reino Unido (Málaga)",
        "ukConsulateDesc": "Asistencia a ciudadanos británicos en el sur de España",
        "ieConsulate": "Embajada de Irlanda (Madrid)",
        "ieConsulateDesc": "Asistencia a ciudadanos irlandeses en toda España",
        "cat": {
          "primary": "Emergencia",
          "police": "Policía",
          "medical": "Sanidad",
          "fire": "Bomberos",
          "support": "Apoyo",
          "consulate": "Consulado"
        }
      },

      // Festivos
      "holidays": {
        "title": "Días Festivos",
        "subtitle": "España y Andalucía — 2026",
        "nextHoliday": "Próximo festivo",
        "today": "Hoy",
        "tomorrow": "Mañana",
        "inNDays": "en {{count}} días",
        "footerNote": "Las fechas pueden variar según el municipio. Confirma con tu ayuntamiento para mayor seguridad.",
        "scope": { "national": "Nacional", "andalusia": "Andalucía", "almeria": "Almería" },
        "month": {
          "january": "enero", "february": "febrero", "march": "marzo", "april": "abril",
          "may": "mayo", "june": "junio", "july": "julio", "august": "agosto",
          "september": "septiembre", "october": "octubre", "november": "noviembre", "december": "diciembre"
        },
        "newYear": "Año Nuevo",
        "epiphany": "Epifanía (Reyes Magos)",
        "andalusiaDay": "Día de Andalucía",
        "maundyThursday": "Jueves Santo",
        "goodFriday": "Viernes Santo",
        "labourDay": "Día del Trabajador",
        "sanJuan": "San Juan (Almería)",
        "assumption": "Asunción de la Virgen",
        "virgenDelMar": "Virgen del Mar (Almería)",
        "nationalDay": "Fiesta Nacional de España",
        "allSaints": "Todos los Santos (trasladado)",
        "constitutionDay": "Día de la Constitución",
        "immaculate": "Inmaculada Concepción",
        "christmas": "Navidad"
      },

      // Añadir al calendario
      "calendar": {
        "addToCalendar": "Añadir al Calendario",
        "icsDownload": "Apple / Outlook (.ics)",
        "icsHint": "Archivo universal de calendario",
        "googleCalendar": "Google Calendar",
        "googleHint": "Se abre en una nueva pestaña"
      },

      // Aviso Vecinal de Seguridad (piloto Almería)
      "safety": {
        "title": "Centro de Seguridad Comunitaria",
        "subtitle": "Avisos oficiales de emergencia, informes de la comunidad y ayuda local durante incidentes.",
        "disclaimer": "No es un servicio policial. En caso de emergencia, llama al 112. Cada publicación es revisada por nuestro equipo antes de publicarse. No se aceptan nombres, acusaciones, matrículas ni información privada.",
        "filterTown": "Filtrar por municipio",
        "filterCategory": "Categoría",
        "allTowns": "Todos los municipios",
        "allCategories": "Todas las categorías",
        "category": {
          "incident_factual": "Incidente factual",
          "safety_tip": "Consejo de seguridad",
          "lost_pet": "Mascota perdida",
          "community_alert": "Aviso comunitario (24 h)"
        },
        "categoryHint": {
          "incident_factual": "Una nota breve y factual sobre algo que ya ha ocurrido.",
          "safety_tip": "Ayuda a los vecinos a estar más seguros con un consejo útil.",
          "lost_pet": "Ayuda a reunir a una mascota perdida con su dueño.",
          "community_alert": "Un aviso puntual que desaparecerá automáticamente en 24 horas."
        },
        "categoryNote": {
          "incident_factual": "Sin nombres. Sin acusaciones."
        },
        "categoryEmoji": {
          "safety_tip": "🟧",
          "incident_factual": "🟥",
          "lost_pet": "🟩",
          "community_alert": "🟪"
        },
        "categoryExamples": {
          "safety_tip": [
            "«Cierra el coche por la noche — han revisado varios vehículos últimamente.»",
            "«Cuidado en la carretera principal — gravilla suelta resbaladiza.»"
          ],
          "incident_factual": [
            "«Visto alguien probando manillas de coche cerca de Calle X sobre las 2 a.m.»",
            "«Han cogido un paquete de la entrada de una vivienda esta mañana.»"
          ],
          "lost_pet": [
            "«Perro pequeño marrón perdido cerca de Arboleas — visto por última vez junto al parque.»",
            "«Gato negro no ha vuelto a casa desde ayer por la tarde.»"
          ],
          "community_alert": [
            "«Carretera cortada hoy cerca de Albox por obras.»",
            "«Corte de agua previsto esta tarde en la zona.»"
          ]
        },
        "examplesLabel": "Ejemplos:",
        "empty": "Aún no hay avisos para este filtro.",
        "emptyTitle": "Nada reportado aún en tu zona — eso es una buena señal",
        "emptyBody": "Sé el primero en compartir algo útil para tu comunidad.",
        "submitCta": "Enviar Alerta de Seguridad",
        "myPosts": "Mis envíos",
        "verifiedResident": "Vecino/a verificado/a de {{town}}",
        "expiresIn": "Visible durante {{value}}",
        "publishedOn": "Publicado {{date}}",
        "report": {
          "button": "Denunciar",
          "title": "Denunciar este aviso",
          "placeholder": "¿Por qué es problemático este aviso?",
          "submit": "Enviar denuncia",
          "thanks": "Gracias, lo revisaremos.",
          "alreadyReported": "Ya has denunciado este aviso."
        },
        "submit": {
          "title": "Enviar una Alerta Local de Seguridad",
          "intro": "Comparte una alerta de seguridad breve y factual con los vecinos de tu municipio. Las publicaciones aparecen al instante. El equipo puede editar o eliminar avisos que infrinjan las reglas.",
          "town": "Municipio (solo provincia de Almería)",
          "townPlaceholder": "Selecciona tu municipio",
          "categoryLabel": "¿Qué tipo de publicación es?",
          "titleLabel": "Título",
          "titlePlaceholder": "Resumen corto (máx. 80 caracteres)",
          "bodyLabel": "Detalles",
          "bodyPlaceholder": "Solo texto. Sin enlaces, sin nombres, sin acusaciones, sin matrículas, sin información privada. Máx. 2000 caracteres.",
          "rules": "Al enviar, confirmo:",
          "rulesNoNames": "No he nombrado a ninguna persona.",
          "rulesNoAccusations": "No estoy acusando a nadie de un delito.",
          "rulesNoPlates": "No he incluido ninguna matrícula.",
          "rulesNoPrivate": "No he incluido direcciones, teléfonos ni información privada.",
          "confirmCheckbox": "Confirmo: sin nombres, sin acusaciones, sin matrículas, sin información privada.",
          "submitButton": "Enviar Alerta de Seguridad",
          "needTown": "Selecciona un municipio",
          "needTitle": "Añade un título (al menos 4 caracteres)",
          "needBody": "Añade detalles (al menos 10 caracteres)",
          "needConfirm": "Marca la casilla de confirmación",
          "submitting": "Enviando…",
          "successTitle": "Alerta publicada",
          "successBody": "Tu alerta de seguridad ya está visible para los vecinos de tu municipio.",
          "characters": "{{count}} caracteres",
          "loginRequired": "Inicia sesión para enviar una alerta.",
          "signIn": "Iniciar sesión",
          "rateLimitHit": "Has alcanzado el límite — puedes publicar hasta 3 avisos cada 24 horas.",
          "categoryFooter": "Mantén los mensajes cortos, factuales y útiles — sin nombres, acusaciones ni información privada."
        },
        "mine": {
          "empty": "Aún no has enviado ningún aviso.",
          "status": {
            "pending": "Pendiente de revisión",
            "approved": "Publicado",
            "rejected": "No publicado",
            "expired": "Expirado",
            "takedown": "Retirado",
            "withdrawn": "Retirado por ti"
          },
          "rejectionReason": "Motivo: {{reason}}",
          "back": "Volver",
          "withdraw": "Retirar",
          "withdrawHeading": "Retirar aviso",
          "withdrawNote": "Esto elimina tu aviso de todos los tablones públicos y borra las notificaciones relacionadas para todos. Queda en tu historial como Retirado.",
          "withdrawConfirm": "Retirar aviso",
          "withdrawSuccess": "Aviso retirado — ya no es visible para el público.",
          "withdrawFailed": "No se pudo retirar el aviso.",
          "markFound": "Marcar como encontrado",
          "markFoundSuccess": "Marcado como encontrado — ¡gracias por avisarnos!",
          "markFoundFailed": "No se pudo marcar como encontrado.",
          "closedFound": "Cerrado — mascota encontrada",
          "edit": "Editar",
          "editHeading": "Editar alerta",
          "editTown": "Municipio",
          "editCategory": "Categoría",
          "editTitleField": "Título",
          "editBody": "Detalles",
          "editPendingNote": "Editar una alerta publicada la envía de nuevo a revisión antes de volver a publicarse.",
          "editCancel": "Cancelar",
          "editSave": "Guardar cambios",
          "editSuccessPending": "Guardado — tu alerta vuelve a revisión y se publicará cuando se apruebe.",
          "editSuccessLive": "Tu alerta se ha actualizado.",
          "editFailed": "No se pudieron guardar los cambios. Inténtalo de nuevo.",
          "editLiveNote": "Tu edición permanece publicada de inmediato; los seguidores no reciben aviso salvo que lo elijas abajo.",
          "editNotifyFollowers": "Avisar a los seguidores de esta actualización",
          "editSuccessNotified": "Guardado: se ha avisado a tus seguidores de la actualización."
        },
        "follow": {
          "banner": "Sigue tu municipio para recibir avisos de seguridad locales.",
          "bannerSub": "Elige uno o varios municipios — te avisaremos cuando haya algo nuevo.",
          "bannerCta": "Elegir mis municipios",
          "followYourTown": "Seguir mi municipio",
          "followingYouAre": "Sigues: {{towns}}",
          "followingHelp": "Recibirás una notificación cada vez que se publique un aviso para estos municipios.",
          "onboardTitle": "Elige los municipios de los que quieres recibir avisos",
          "onboardBody": "Elige uno o varios municipios de tu zona. Te avisaremos en cuanto un vecino publique un aviso de seguridad allí.",
          "onboardSkip": "Ahora no",
          "onboardChoose": "Elegir mis municipios",
          "pickerTitle": "Mis avisos de seguridad",
          "pickerSubtitle": "Elige uno o varios municipios. Te avisaremos cuando se publique un aviso allí.",
          "searchPlaceholder": "Buscar municipios…",
          "noResults": "Ningún municipio coincide con tu búsqueda.",
          "followingCount_one": "Siguiendo {{count}} municipio",
          "followingCount_other": "Siguiendo {{count}} municipios",
          "followingCount": "Siguiendo {{count}} municipios",
          "followingBadge": "Siguiendo",
          "doneBtn": "Listo",
          "channelsNote": "Los avisos llegan por push y tu bandeja dentro de la app. El correo queda desactivado salvo que lo actives en ajustes de notificaciones.",
          "followingSummary_one": "Recibirás avisos de: {{towns}}",
          "followingSummary_other": "Recibirás avisos de: {{towns}}",
          "followingSummary": "Recibirás avisos de: {{towns}}",
          "manage": "Gestionar",
          "afterSubmitTitle": "¿Quieres avisos la próxima vez que se publique algo en {{town}}?",
          "afterSubmitBody": "Te avisaremos en la app cuando se publique otro aviso.",
          "followTown": "Seguir {{town}}",
          "pickMore": "Elegir más municipios",
          "nowFollowing": "Recibirás avisos de {{town}}",
          "failed": "No se pudo seguir este municipio. Inténtalo de nuevo.",
          "alreadyFollowing": "Ya sigues {{town}} — te mantendremos al tanto.",
          "settingsTitle": "Municipios para avisos de seguridad",
          "settingsDesc": "Recibe una notificación cuando se publique un aviso en los municipios que sigues.",
          "noneYet": "Aún no sigues ningún municipio.",
          "pickTowns": "Elegir municipios",
          "manageTowns": "Gestionar municipios"
        }
      },

      // Herramientas de la comunidad — tarjeta en Descubrir
      "community": {
        "title": "Herramientas comunitarias",
        "safetySub": "Vecinos verificados, notas factuales",
        "emergencySub": "Policía, sanitario, 112",
        "holidaysSub": "España y Andalucía 2026"
      },

      // Añadido Feb 2026 — claves i18n para auth, bandeja y borrado de cuenta
      "common": {
        "back": "Atrás",
        "loading": "Cargando…",
        "optional": "opcional"
      },
      "auth": {
        "pwStrongPlaceholder": "Crea una contraseña segura",
        "pwReenter": "Vuelve a introducir la contraseña",
        "pwReenterFull": "Vuelve a introducir tu contraseña",
        "pwMin6": "Al menos 6 caracteres",
        "pwConfirm": "Confirmar contraseña"
      },
      "businessAuth": {
        "loginTitle": "Acceso Negocios",
        "loginSubtitle": "Inicia sesión para gestionar tu ficha de negocio",
        "signIn": "Iniciar sesión",
        "signingIn": "Iniciando sesión…",
        "noAccount": "¿No tienes cuenta?",
        "registerHere": "Regístrate aquí",
        "backHome": "Volver al inicio",
        "errInvalidType": "Tipo de cuenta no válido. Usa una cuenta de negocio.",
        "errLoginFailed": "No se pudo iniciar sesión. Inténtalo de nuevo.",
        "registerTitle": "Registra tu Negocio",
        "step1": "Paso 1: Crea tu cuenta",
        "step2": "Paso 2: Datos del negocio",
        "phName": "Nombre de tu negocio",
        "phCategory": "Selecciona una categoría",
        "phAddress": "Dirección"
      },
      "helperAuth": {
        "loginTitle": "Acceso Ayudantes",
        "loginSubtitle": "Inicia sesión para ver tus referidos y comisiones"
      },
      "inbox": {
        "title": "Notificaciones",
        "description": "Tus avisos: negocios que sigues, municipios a los que estás suscrito y ofertas patrocinadas si las has activado.",
        "typeEvent": "Evento",
        "typeOffer": "Oferta",
        "typeSystem": "Sistema",
        "typeSafety": "Seguridad",
        "justNow": "ahora mismo",
        "minsAgo": "hace {{n}} min",
        "hoursAgo": "hace {{n}} h",
        "daysAgo": "hace {{n}} d",
        "unreadCount": "{{n}} sin leer",
        "sponsoredCount": "{{n}} patrocinado",
        "settingsTitle": "Ajustes de notificaciones",
        "markAllRead": "Marcar todo como leído",
        "errLoad": "No se pudieron cargar las notificaciones",
        "errMarkAll": "No se pudieron marcar todas como leídas",
        "emptyEnableTitle": "Activa las notificaciones",
        "emptyEnableBody": "Activa las notificaciones desde una página de negocio o en Ajustes para empezar a recibir avisos aquí.",
        "openSettings": "Abrir Ajustes",
        "emptyTitle": "Aún no tienes avisos",
        "emptyBody": "Sigue negocios o municipios para empezar a recibir novedades.",
        "sponsored": "Patrocinado",
        "sponsoredTitle": "Esta es una oferta promocionada",
        "unread": "Sin leer",
        "fallbackTitle": "Notificación"
      },
      "dataDeletion": {
        "title": "Elimina tu cuenta",
        "subtitle": "Esta acción es permanente y no se puede deshacer.",
        "doneTitle": "Cuenta eliminada",
        "doneBody": "Tu cuenta se ha eliminado permanentemente. Tus reseñas públicas se han anonimizado. Puedes registrarte de nuevo cuando quieras con una cuenta nueva.",
        "doneSubCancelled": "Tu suscripción activa se ha cancelado.",
        "doneUnclaimed": "Tu ficha de negocio ha vuelto a un estado no reclamado para que la comunidad pueda seguir encontrándola.",
        "backHome": "Volver al inicio",
        "signInRequired": "Se requiere iniciar sesión",
        "signInBody": "Debes iniciar sesión para eliminar tu cuenta.",
        "goSignIn": "Ir a iniciar sesión",
        "deletedHeading": "Eliminado permanentemente",
        "deletedItem1": "Tu cuenta, correo y contraseña",
        "deletedItem2": "Favoritos guardados y bandeja de notificaciones",
        "deletedItem3": "Suscripciones a notificaciones y enlaces de dispositivo",
        "deletedItem4": "Cualquier denuncia o solicitud de reclamación pendiente",
        "anonHeading": "Anonimizado (se mantiene público, sin tu nombre)",
        "anonItem1": "Reseñas que has escrito — se muestran como 'Usuario eliminado' en la página del negocio",
        "anonItem2": "Registros de moderación/auditoría ya gestionados",
        "bizHeading": "Si eres propietario de un negocio",
        "bizItem1": "Tu suscripción se cancelará inmediatamente a través de Stripe",
        "bizItem2": "La ficha volverá a un estado no reclamado (para que los vecinos puedan seguir encontrándola)",
        "bizItem3": "Podrás reclamarla más tarde con una cuenta nueva si vuelves",
        "legalHeading": "Se conserva por motivos legales/fiscales",
        "legalItem1": "Facturas de Stripe (guardadas por Stripe — la ley fiscal española exige 6 años)",
        "legalItem2": "Registro anonimizado de eliminación (guardado 2 años para acreditar el cumplimiento)",
        "reasonLabel": "Motivo de la baja",
        "reasonPlaceholder": "Ayúdanos a mejorar — ¿qué te ha llevado a irte?",
        "typeDeleteLead": "Escribe",
        "typeDeleteTrail": "para confirmar",
        "deleting": "Eliminando…",
        "deletePermanent": "Eliminar mi cuenta permanentemente",
        "cancelKeep": "Cancelar, conservar mi cuenta",
        "errDefault": "No se pudo eliminar la cuenta. Inténtalo de nuevo o contacta con soporte."
      },
      // Prueba gratuita de 60 días (plan 12 meses)
      "trial": {
        "signupTitle": "Empieza con una prueba gratuita de 60 días — sin tarjeta",
        "signupBody": "Prueba Find-2-Day gratis durante 60 días. Añade un método de pago cuando quieras antes del día 60 para continuar. Si no lo añades, tus eventos y ofertas se pausarán automáticamente — tu ficha seguirá visible.",
        "signupCta": "Empezar prueba gratuita de 60 días — sin tarjeta",
        "addPaymentMethod": "Añadir método de pago",
        "lockInVisibility": "Asegura tu visibilidad",
        "stayLive": "Sigue activo — añadir método de pago",
        "goLiveAgain": "Volver a publicar",
        "calmTitleV2": "Tu negocio está visible en Find-2-Day ahora mismo",
        "calmBodyV2": "Los vecinos pueden encontrarte en Descubrir, tus eventos aparecen en las búsquedas y tus ofertas llegan a clientes. Te quedan {{days}} días de prueba — añade un método de pago cuando quieras y no notarás ninguna interrupción.",
        "amberTitleV2": "Te quedan {{days}} días — mantén tu visibilidad",
        "urgentTitleV2": "Desaparecerás de Descubrir en {{days}} días",
        "endingTomorrowTitleV2": "Mañana tus eventos dejan de aparecer en las búsquedas",
        "endingTodayTitleV2": "Tu último día visible — los eventos se ocultan mañana",
        "urgentBodyV2": "Hay vecinos buscando hoy y tú apareces. Añade un método de pago ahora y seguirás visible sin ninguna pausa — tus eventos, ofertas y ficha siguen funcionando como hasta ahora.",
        "cardAddedBody": "Tarjeta añadida — se te cobrará automáticamente cuando termine la prueba en {{days}} día(s). No hace falta hacer nada más.",
        "pausedTitleV2": "Tus eventos y ofertas están ocultos ahora mismo",
        "pausedBodyV2": "Hay clientes buscando hoy y no pueden ver tus eventos ni tus ofertas. Añade un método de pago y vuelves a estar visible en segundos — tu ficha, tus fotos y tus reseñas se conservan.",
        "feedbackOpenLink": "Ahora no — dinos por qué",
        "feedbackPrompt": "¿Te importa contarnos por qué? Nos ayuda a mejorar.",
        "feedbackThanks": "Gracias, lo valoramos mucho.",
        "feedbackError": "No se pudo enviar tu opinión ahora mismo.",
        "feedbackSend": "Enviar",
        "feedbackNotePlaceholder": "Lo que quieras añadir (opcional)",
        "reasonNotReady": "Aún no estoy listo",
        "reasonPriceTooHigh": "Precio demasiado alto",
        "reasonPreferMonthly": "Prefiero pagar mensualmente",
        "reasonOther": "Otro motivo",
        "paymentLabel": "Prueba gratuita",
        "pausedLabel": "Pausado"
      },
      "foundingBadge": {
        "label": "Negocio Fundador",
        "tooltip": "Uno de los negocios fundadores en Find-2-Day"
      },
      "foundersPricingNote": "Tu ficha, eventos y ofertas están incluidos sin coste durante el periodo de lanzamiento del Negocio Fundador. Los impulsos son opcionales si quieres más visibilidad en toda la región de Almería.",
      "seededNotice": {
        "body": "Esta ficha se ha creado a partir de información pública sobre negocios para ayudar a los usuarios locales a descubrirlos. El propietario puede reclamar, editar o solicitar la eliminación de la ficha en cualquier momento.",
        "disclaimer": "La información mostrada puede proceder de fuentes públicas.",
        "claimCta": "Reclama esta ficha",
        "removalCta": "Solicitar edición o eliminación"
      },
      "seededListings": {
        "gridNotice": "Algunas fichas se crean a partir de información pública sobre negocios para ayudar a los usuarios locales a descubrirlos. Los propietarios pueden reclamar, editar o solicitar la eliminación en cualquier momento."
      },
      "removalRequest": {
        "title": "Solicitar edición o eliminación",
        "typeRemoval": "Eliminar ficha",
        "typeEdit": "Sugerir cambio",
        "namePh": "Tu nombre",
        "emailPh": "Tu email",
        "roleOwner": "Soy el propietario del negocio",
        "roleEmployee": "Trabajo en este negocio",
        "roleOther": "Otro",
        "reasonPh": "Indícanos por qué (mínimo 10 caracteres)",
        "submit": "Enviar solicitud",
        "submitting": "Enviando…",
        "successTitle": "Gracias — hemos recibido tu solicitud.",
        "successBody": "Nuestro equipo revisará y responderá a tu email en un plazo de 5 días laborables.",
        "error": "No se pudo enviar — inténtalo de nuevo más tarde.",
        "privacyNote": "Usamos tu email solo para responder a esta solicitud. Consulta nuestra Política de Privacidad."
      },
      "frontpage": {
        "foundersHeadline": "Oferta Negocio Fundador — 12 Meses Gratis",
        "foundersSubline": "Publica tu negocio, eventos y ofertas sin pago inicial.",
        "differentiator": "Tus clientes reciben las novedades directamente en su móvil — sin perderse publicaciones en redes sociales.",
        "browseAndFollow": "Haz clic aquí para empezar",
        "browseAndFollowSub": "Descubre eventos, ofertas y negocios locales cerca de ti.",
        "businessOptionsHeading": "Opciones para negocios",
        "alreadyHaveAccount": "¿Ya tienes cuenta? Inicia sesión",
        "businessSignIn": "¿Ya tienes cuenta de negocio? Inicia sesión aquí",
        "claimExistingBusiness": "¿Ya estás listado y quieres reclamar tu negocio? Haz clic aquí",
        "charityApply": "Solicita una ficha gratuita de Comunidad u ONG",
        "discoverTagline": "Descubre eventos, ofertas y novedades locales en Almería",
        "trust": {
          "noUpfront": "Sin pago inicial",
          "publishContent": "Publica eventos y ofertas",
          "reachCustomers": "Llega a clientes locales en toda Almería"
        },
        "foundersCount_one": "{{count}} negocio fundador ya a bordo en Almería",
        "foundersCount_other": "{{count}} negocios fundadores ya a bordo en Almería",
        "claimExistingBusinessTitle": "Reclama o añade un negocio",
        "charityApplyTitle": "Ficha de comunidad / ONG"
      },
      "foundersChecklist": {
        "title": "Tu lista de éxito para la primera semana",
        "titleComplete": "¡Todo listo, buen trabajo!",
        "subtitle": "Pequeños pasos que ayudan a que los clientes locales te encuentren.",
        "subtitleComplete": "Tu negocio está listo para atraer clientes.",
        "completed": "completados",
        "bonus": "Extra",
        "dismiss": "Ocultar lista",
        "celebration": "¡Buen trabajo! Has activado las partes más efectivas de tu ficha. Los clientes de tu zona empezarán a encontrarte.",
        "items": {
          "profile_complete": {
            "label": "Completa el perfil de tu negocio",
            "hint": "Descripción, teléfono y dirección — lo básico que esperan los clientes."
          },
          "photos_3plus": {
            "label": "Sube al menos 3 fotos",
            "hint": "Las fichas con fotos reciben muchas más visitas."
          },
          "first_event": {
            "label": "Publica tu primer evento",
            "hint": "¿Algo esta semana? Publícalo."
          },
          "first_offer": {
            "label": "Publica tu primera oferta",
            "hint": "Un descuento sencillo o un especial atrae a nuevos clientes."
          },
          "first_follower": {
            "label": "Consigue tu primer seguidor",
            "hint": "Comparte el enlace de tu ficha con un cliente satisfecho."
          },
          "opening_hours": {
            "label": "Añade el horario",
            "hint": "Ayuda a los vecinos a saber cuándo pasarse."
          },
          "amenities": {
            "label": "Añade servicios o características",
            "hint": "Tamaño de grupo, opciones dietéticas o accesibilidad."
          },
          "saved_card": {
            "label": "Guarda una tarjeta para promocionar al instante",
            "hint": "Opcional — solo se usa si decides promocionar una publicación."
          }
        }
      },
      "renewal": {
        "title": {
          "30d": "Tu año Fundador termina en 30 días",
          "14d": "Tu año Fundador termina en 2 semanas",
          "7d": "Tu año Fundador termina en 1 semana"
        },
        "body": "Mantén activos tus eventos, ofertas, seguidores y notificaciones directas a tus clientes, sin interrupción.",
        "ctaContinue": "Continuar Acceso Completo",
        "ctaSaveCard": "Guardar método de pago",
        "opening": "Abriendo…",
        "dismiss": "Descartar"
      }
    }
  }
};

// Add imported languages
resources.de = de;
resources.nl = nl;
resources.fr = fr;
resources.ar = ar;

// Home & Local Prices (Stage 1) — merge translation.local.* + dash.local keys
// for all 6 languages without touching any existing translations.
Object.keys(localSection).forEach((lng) => {
  if (!resources[lng]) resources[lng] = { translation: {} };
  if (!resources[lng].translation) resources[lng].translation = {};
  resources[lng].translation.local = localSection[lng];
  resources[lng].translation.dash = resources[lng].translation.dash || {};
  resources[lng].translation.dash.local = LOCAL_DASH[lng].local;
  resources[lng].translation.dash.localDesc = LOCAL_DASH[lng].localDesc;
});

// Multilingual coverage additions — fills ONLY missing keys per language and
// NEVER overwrites an existing (correct) translation. Single-file so the five
// large locale files stay untouched. Keys sourced from en and translated.
function deepFillMissing(target, src) {
  Object.keys(src).forEach((k) => {
    const v = src[k];
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      if (!target[k] || typeof target[k] !== 'object') target[k] = {};
      deepFillMissing(target[k], v);
    } else if (!(k in target)) {
      target[k] = v;
    }
  });
}
['en', 'es', 'de', 'nl', 'fr', 'ar'].forEach((lng) => {
  if (!resources[lng]) resources[lng] = { translation: {} };
  if (!resources[lng].translation) resources[lng].translation = {};
  if (additions[lng]) deepFillMissing(resources[lng].translation, additions[lng]);
  if (additions2[lng]) deepFillMissing(resources[lng].translation, additions2[lng]);
});

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    },
    // Dev-only: log missing translations per language. Enabled only outside
    // production so it doesn't ship in the bundle runtime-wise. The
    // `saveMissing` flag fires `missingKeyHandler` once per missing key.
    saveMissing: process.env.NODE_ENV !== 'production',
    missingKeyHandler: (lngs, ns, key, fallbackValue) => {
      if (process.env.NODE_ENV === 'production') return;
      const lang = Array.isArray(lngs) ? lngs[0] : lngs;
      // Only track non-English languages (EN is the source of truth).
      if (!lang || lang.startsWith('en')) return;
      try {
        window.__i18nMissing = window.__i18nMissing || {};
        const bucket = (window.__i18nMissing[lang] = window.__i18nMissing[lang] || new Set());
        if (!bucket.has(key)) {
          bucket.add(key);
          // Console-grouped warning so the dev can jump to it in sources.
          // eslint-disable-next-line no-console
          console.warn(
            `%c[i18n]%c missing key '%s' for lang '%s' — falling back to: '%s'`,
            'background:#fef3c7;color:#92400e;padding:1px 4px;border-radius:3px;font-weight:bold',
            'color:#a16207',
            key, lang, (fallbackValue || '').slice(0, 80),
          );
        }
      } catch (e) { /* noop */ }
    },
  });

// Dev helper: call `window.__i18nMissingReport()` in the browser console to
// list every missing key seen so far, grouped by language.
if (process.env.NODE_ENV !== 'production') {
  try {
    window.__i18nMissingReport = () => {
      const report = window.__i18nMissing || {};
      const summary = {};
      Object.keys(report).forEach(lang => {
        summary[lang] = Array.from(report[lang] || []).sort();
      });
      // eslint-disable-next-line no-console
      console.table(
        Object.keys(summary).map(lang => ({ lang, missing: summary[lang].length }))
      );
      return summary;
    };
  } catch (e) { /* noop */ }
}

export default i18n;
